import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import io from 'socket.io-client';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';
import { useBranch } from '../contexts/BranchContext';
import { useCurrency } from '../contexts/CurrencyContext';
import BranchSelector from '../components/BranchSelector';
import { getLocalizedText } from '../utils/localizationHelper';
import CustomerLoginSignup from '../components/CustomerLoginSignup';
import Customers from '../components/Customers';
import { addToQueue, saveToStore, syncStoreWithServer, getAllFromStore, removeFromQueue, getQueue, getFromStore } from '../utils/localDb';
import { getHighAccuracyPosition, isGeolocationAvailable } from '../utils/geolocation';

const API_URL = (() => {
    const hostname = window.location.hostname;
    const isLocalIP = hostname === 'localhost' || hostname.endsWith('.localhost') || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.') || /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname);
    return isLocalIP ? `http://${hostname}:5000` : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');
})();

const generateFoodImageURL = (itemName, imageUrl) => {
    if (imageUrl) {
        if (imageUrl.startsWith('/uploads/')) {
            return `${API_URL}${imageUrl}`;
        }
        return imageUrl;
    }

    const normalizedName = itemName.toLowerCase().trim();
    const foodImageMap = {
        'margherita pizza': 'https://images.unsplash.com/photo-1593560708920-61dd98c46a4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
        'chicken biryani': 'https://images.unsplash.com/photo-1589302168068-964a4e1a9eb4?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
        'paneer tikka': 'https://images.unsplash.com/photo-1569957485519-258992c2e8b6?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
        'caesar salad': 'https://images.unsplash.com/photo-1550304963-4a56a14c3df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
        'masala dosa': 'https://images.unsplash.com/photo-1589302168068-964a4e1a9eb4?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
        'chocolate brownie': 'https://images.unsplash.com/photo-1606313564200-e75d5e30476c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
    };
    return foodImageMap[normalizedName] || `https://picsum.photos/seed/${normalizedName.replace(/\s+/g, '')}/400/300.jpg`;
};
const formatCount = (num) => {
    if (!num) return '0';
    if (num >= 1000) {
        return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    }
    return num.toString();
};

const MenuItemCard = ({ item, t, addToCart, updateQuantity, cart, language, categories }) => {
    const { currency, formatAmount } = useCurrency();
    const [showDetails, setShowDetails] = useState(false);
    const [showSelfService, setShowSelfService] = useState(false);
    const price = currency === 'INR' ? (item.price_inr || item.price || 0) : (item.price_usd || item.price || 0);
    const isAvailable = item.is_available === 1 || item.is_available === true;
    const isVeg = !(item.is_veg === 0 || item.is_veg === false || item.is_veg === '0' || item.is_veg === 'false');

    const cartItem = (cart || []).find(c => c.id === item.id);
    const cartQty = cartItem ? cartItem.quantity : 0;

    const hasInfo = Boolean(item.description || item.ingredients || item.nutritional_info || item.vitamins);

    // Helper to format vitamins list nicely
    const formatVitamins = (vitaminStr) => {
        if (!vitaminStr) return null;
        const vitamins = vitaminStr.split(',').map(v => v.trim().replace(/^Vitamin\s+/i, ''));
        return `Vitamins: ${vitamins.join(', ')}`;
    };

    return (
        <div className={`menu-card bg-white rounded-xl shadow-md overflow-hidden ${!isAvailable ? 'opacity-75' : ''} transition-all duration-300 hover:shadow-xl hover:-translate-y-1 h-full flex flex-col border border-gray-100`}>
            {/* Image / Details / Self Service Toggle Area */}
            <div className="relative h-32 bg-gradient-to-br from-gray-50 to-gray-100">
                {!showDetails && !showSelfService ? (
                    <>
                        <img
                            src={generateFoodImageURL(item.name, item.image_url)}
                            alt={getLocalizedText(item, 'name', language)}
                            className={`w-full h-full object-cover food-image transition-transform duration-700 hover:scale-110 ${!isAvailable ? 'grayscale' : ''}`}
                        />
                        {/* Gradient Overlay for better text visibility */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"></div>
                        
                        {/* Veg / Non-Veg Indicator & Discount Badge on Top Left */}
                        <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5">
                            <span 
                                className={`w-4 h-4 rounded-sm border bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md ${
                                    isVeg ? 'border-emerald-600' : 'border-rose-600'
                                }`} 
                                title={isVeg ? (t('veg') || 'Vegetarian') : (t('nonVeg') || 'Non-Vegetarian')}
                            >
                                <span className={`w-2 h-2 rounded-full ${isVeg ? 'bg-emerald-600' : 'bg-rose-600'}`}></span>
                            </span>
                            {item.discount_percentage >= 1 && (
                                <span className="bg-gradient-to-r from-red-500 to-red-600 text-white px-2 py-0.5 rounded-full text-[9px] font-black shadow-lg animate-pulse border border-red-400">
                                    {Math.floor(item.discount_percentage)}% OFF
                                </span>
                            )}
                        </div>

                        {/* Category Tag Top Right */}
                        <div className="absolute top-2 right-2 z-10">
                            <span className="bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-full text-[10px] font-bold text-gray-700 shadow-lg border border-gray-200">{getLocalizedText(categories.find(c => c.name === item.category), 'name', language, 'category')}</span>
                        </div>

                        {!isAvailable && (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-20">
                                <span className="bg-red-600 text-white px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider shadow-xl transform -rotate-12 border-2 border-white">{t('unavailable')}</span>
                            </div>
                        )}

                        {hasInfo && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDetails(true);
                                }}
                                className="absolute bottom-2 right-2 bg-white/95 backdrop-blur-sm text-emerald-600 hover:text-emerald-700 p-2 rounded-full shadow-lg hover:scale-110 transition-all z-10 border border-emerald-200 flex items-center justify-center w-7 h-7"
                                title={t('itemDetails') || "Details & Ingredients"}
                            >
                                <i className="fas fa-info-circle text-xs"></i>
                            </button>
                        )}
                        {!!item.is_self_service && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowSelfService(true);
                                }}
                                className="absolute bottom-2 left-2 bg-white/95 backdrop-blur-sm text-emerald-600 hover:text-emerald-700 p-2 rounded-full shadow-lg hover:scale-110 transition-all z-10 flex items-center justify-center w-7 h-7 border border-emerald-200"
                                title="Self Service: Pick up required"
                            >
                                <i className="fas fa-walking text-xs"></i>
                            </button>
                        )}
                    </>
                ) : showDetails ? (
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-teal-50 to-white p-2 flex flex-col z-40">
                        <div className="flex justify-between items-center mb-1 border-b border-emerald-200/80 pb-1 flex-shrink-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                                <span 
                                    className={`w-3.5 h-3.5 rounded-sm border bg-white flex items-center justify-center flex-shrink-0 ${
                                        isVeg ? 'border-emerald-600' : 'border-rose-600'
                                    }`}
                                >
                                    <span className={`w-1.5 h-1.5 rounded-full ${isVeg ? 'bg-emerald-600' : 'bg-rose-600'}`}></span>
                                </span>
                                <h4 className="font-black text-emerald-950 truncate text-[11px]">
                                    {String(getLocalizedText(item, 'name', language)).replace(/^[^a-zA-Z\s\u0900-\u097F\u0C00-\u0C7F\u0B80-\u0BFF\u0D00-\u0D7F]+/, '').trim()}
                                </h4>
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDetails(false);
                                }}
                                className="text-gray-700 hover:text-gray-900 transition-colors p-1 hover:bg-white/80 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 bg-white/60 shadow-sm"
                            >
                                <i className="fas fa-times text-[10px] font-bold"></i>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto text-[10px] leading-snug space-y-1 pr-0.5 scrollbar-thin">
                            {/* Description */}
                            {item.description && (
                                <div className="bg-white/90 p-1.5 rounded-lg border border-emerald-100 shadow-sm">
                                    <p className="text-gray-800 font-normal leading-relaxed text-[10px]">
                                        {getLocalizedText(item, 'description', language)}
                                    </p>
                                </div>
                            )}
                            {/* Ingredients */}
                            {item.ingredients && (
                                <div className="bg-amber-50/90 p-1.5 rounded-lg border border-amber-200/70 shadow-sm">
                                    <div className="flex items-center gap-1 text-amber-900 font-bold text-[9px] uppercase tracking-wider mb-0.5">
                                        <i className="fas fa-mortar-pestle text-amber-600 text-[8px]"></i>
                                        <span>{t('ingredients') || 'Ingredients'}</span>
                                    </div>
                                    <p className="text-amber-950 text-[10px] leading-relaxed">
                                        {getLocalizedText(item, 'ingredients', language) || item.ingredients}
                                    </p>
                                </div>
                            )}
                            {/* Nutritional Info */}
                            {item.nutritional_info && (
                                <div className="bg-white/90 p-1.5 rounded-lg border border-emerald-100 shadow-sm">
                                    <div className="flex items-center gap-1 text-emerald-900 font-bold text-[9px] uppercase tracking-wider mb-0.5">
                                        <i className="fas fa-fire text-emerald-600 text-[8px]"></i>
                                        <span>{t('nutritionalInfo') || 'Nutrition'}</span>
                                    </div>
                                    <p className="text-gray-800 font-medium text-[10px]">
                                        {getLocalizedText(item, 'nutritional_info', language)}
                                    </p>
                                </div>
                            )}
                            {/* Vitamins */}
                            {item.vitamins && (
                                <div className="bg-emerald-50/90 p-1.5 rounded-lg border border-emerald-200/80 shadow-sm">
                                    <span className="text-emerald-800 font-bold block text-[9px]">{formatVitamins(item.vitamins)}</span>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-teal-50 p-2 flex flex-col z-40">
                        <div className="flex justify-between items-center mb-1.5 border-b-2 border-emerald-300 pb-1">
                            <h4 className="font-black text-emerald-800 flex items-center text-[11px]">
                                <i className="fas fa-walking mr-1 text-[9px]"></i> {t('selfService') || 'Self Service'}
                            </h4>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowSelfService(false);
                                }}
                                className="text-gray-700 hover:text-gray-900 transition-colors p-1 hover:bg-white/80 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 bg-white/60"
                            >
                                <i className="fas fa-times text-xs font-bold"></i>
                            </button>
                        </div>
                        <div className="flex flex-col items-center justify-center text-center flex-1">
                            <div className="bg-white/70 p-2.5 rounded-xl border-2 border-emerald-200">
                                <i className="fas fa-walking text-xl text-emerald-600 mb-1.5"></i>
                                <h3 className="text-[11px] font-black text-emerald-900 mb-0.5">{t('selfServiceItem') || 'Self Service Item'}</h3>
                                <p className="text-emerald-700 font-bold text-[9px]">
                                    {t('pickupRequired') || 'Pickup Required'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="p-2.5 flex flex-col flex-grow">
                <div className="flex items-start justify-between gap-1.5 mb-1.5">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span 
                            className={`w-3.5 h-3.5 rounded-sm border bg-white flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                isVeg ? 'border-emerald-600' : 'border-rose-600'
                            }`}
                            title={isVeg ? 'Veg' : 'Non-Veg'}
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${isVeg ? 'bg-emerald-600' : 'bg-rose-600'}`}></span>
                        </span>
                        <h3 className="text-xs sm:text-sm font-semibold text-gray-900 leading-snug line-clamp-2">
                            {String(getLocalizedText(item, 'name', language)).replace(/^[^a-zA-Z\s\u0900-\u097F\u0C00-\u0C7F\u0B80-\u0BFF\u0D00-\u0D7F]+/, '').trim()}
                        </h3>
                    </div>
                    
                    {item.rating > 0 && (
                        <div className="flex items-center bg-gradient-to-r from-amber-50 to-yellow-50 px-2 py-0.5 rounded-lg border border-amber-200 shrink-0">
                            <i className="fas fa-star text-[9px] text-amber-500 mr-1"></i>
                            <span className="text-[10px] font-black text-amber-700">
                                {Number(item.rating).toFixed(1)}
                                {item.rating_count > 0 && <span className="text-gray-500 font-medium ml-1">({formatCount(item.rating_count)})</span>}
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-100">
                    <div className="flex flex-col">
                        {item.discount_percentage >= 1 ? (
                            <>
                                <span className="text-[10px] text-gray-400 line-through leading-none mb-1">{formatAmount(parseFloat(price))}</span>
                                <div className="text-red-600 font-black text-base leading-none">
                                    {formatAmount(parseFloat(price) * (1 - item.discount_percentage / 100))}
                                </div>
                            </>
                        ) : (
                            <div className="text-emerald-600 font-black text-base leading-none">
                                {formatAmount(parseFloat(price))}
                            </div>
                        )}
                    </div>
                    
                    {/* Interactive Quantity / Add to Cart Button */}
                    {!isAvailable ? (
                        <button
                            disabled
                            className="bg-gray-200 text-gray-500 cursor-not-allowed font-black h-8 px-3 rounded-lg text-[10px]"
                        >
                            <span className="uppercase tracking-wide">N/A</span>
                        </button>
                    ) : cartQty > 0 ? (
                        <div className="flex items-center bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg h-8 shadow-md overflow-hidden transition-all">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    updateQuantity(item.id, -1);
                                }}
                                className="w-7 h-full flex items-center justify-center hover:bg-emerald-700 active:bg-emerald-800 transition-colors text-white"
                                title="Decrease quantity"
                            >
                                <i className="fas fa-minus text-[9px]"></i>
                            </button>
                            <span className="px-2 font-black text-xs min-w-[20px] text-center select-none">
                                {cartQty}
                            </span>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    updateQuantity(item.id, 1);
                                }}
                                className="w-7 h-full flex items-center justify-center hover:bg-emerald-700 active:bg-emerald-800 transition-colors text-white"
                                title="Increase quantity"
                            >
                                <i className="fas fa-plus text-[9px]"></i>
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => addToCart(item.id)}
                            className="font-black h-8 px-3 rounded-lg transition-all flex items-center justify-center text-[10px] shadow-md bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white active:scale-95"
                        >
                            <i className="fas fa-plus mr-1 text-[9px]"></i>
                            <span className="uppercase tracking-wide">{t('add') || 'ADD'}</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

function CustomerPage() {
    // State variables
    // A customer QR contains a company id plus a table/no-table context. Keep
    // this separate from ordinary customer/admin navigation.
    const [searchParams] = useSearchParams();
    const isQrEntry = Boolean(
        (searchParams.get('companyId') || searchParams.get('company_id')) &&
        (searchParams.has('table') || searchParams.has('no_table') || searchParams.has('no_table_mode'))
    );
    const [qrAccess, setQrAccess] = useState({ status: isQrEntry ? 'checking' : 'allowed', message: '' });
    const [menuItems, setMenuItems] = useState([]);
    const [cart, setCart] = useState(() => {
        try {
            const storedCart = localStorage.getItem('dineflow_cart');
            return storedCart ? JSON.parse(storedCart) : [];
        } catch (e) {
            console.error("Failed to parse stored cart", e);
            return [];
        }
    });

    // Save cart to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('dineflow_cart', JSON.stringify(cart));
    }, [cart]);
    const { currency: currentCurrency, formatAmount } = useCurrency();
    const [tableNumber, setTableNumber] = useState(() => {
        const t = searchParams.get('table');
        if (t === 'waiting') return 'waiting';
        if (t === '0' || t === 'delivery' || t === 'offline' || searchParams.get('delivery') === 'true') return 0;
        if (t) return parseInt(t);

        // Fallback: Check localStorage
        try {
            const stored = JSON.parse(localStorage.getItem('scanned_qr_context'));
            if (stored && stored.table) {
                console.log('[CustomerPage] Recovered table from localStorage:', stored.table);
                if (stored.table === 'waiting') return 'waiting';
                if (stored.table === '0' || stored.table === 'delivery' || stored.table === 'offline') return 0;
                return parseInt(stored.table);
            }
        } catch (e) { }
        return null;
    });
    
    // Track if user came from QR code scan (has branch_id in URL or localStorage)
    const [isQRScannedUser] = useState(() => {
        const branchParam = searchParams.get('branch_id');
        if (branchParam) return true;
        
        try {
            const stored = JSON.parse(localStorage.getItem('scanned_qr_context'));
            if (stored && stored.branch_id) return true;
        } catch (e) { }
        
        return false;
    });
    
    const [isTableSelectionModalOpen, setIsTableSelectionModalOpen] = useState(false);
    const [tables, setTables] = useState([]);
    const [tableAvailabilityOrders, setTableAvailabilityOrders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    const [activeVitamin, setActiveVitamin] = useState('all');
    const [vegFilter, setVegFilter] = useState('all'); // 'all', 'veg', 'non-veg'
    const [isCartModalOpen, setIsCartModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showOrderSuccess, setShowOrderSuccess] = useState(false);
    const [waitingNumber, setWaitingNumber] = useState(null);
    const [placedOrderNumber, setPlacedOrderNumber] = useState(null);
    const [customerPhone, setCustomerPhone] = useState(() => localStorage.getItem('customer_phone_36') || '');
    const [hasSelfServiceInOrder, setHasSelfServiceInOrder] = useState(false);
    const [pendingPaymentOrder, setPendingPaymentOrder] = useState(null);
    const [hasClickedPaymentLink, setHasClickedPaymentLink] = useState(false);
    const [utrNumber, setUtrNumber] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const toastTimeoutRef = React.useRef(null);
    const showToast = React.useCallback((message, type = 'success') => {
        if (toastTimeoutRef.current) {
            clearTimeout(toastTimeoutRef.current);
        }
        setToast({ show: true, message, type });
        const duration = type === 'error' ? 10000 : 3000;
        toastTimeoutRef.current = setTimeout(() => {
            setToast({ show: false, message: '', type: 'success' });
            toastTimeoutRef.current = null;
        }, duration);
    }, []);
    const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
    const [customerOrders, setCustomerOrders] = useState([]);
    const [categories, setCategories] = useState([]);
    const { currentUser, logout, token } = useAuth();
    const navigate = useNavigate();
    const { t, language } = useLanguage();
                const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
    const [companyInfo, setCompanyInfo] = useState(() => {
        // Initialise from cache so we don't flash a "Loading experience…"
        // spinner every time a user scans a QR code and lands here. Fresh
        // data is still fetched in the useEffect below and will update this.
        try {
            const cached = localStorage.getItem('redsorm_company_profile_customer');
            if (cached) return JSON.parse(cached);
        } catch (e) { /* ignore */ }
        return null;
    });
    const { selectedBranch, setSelectedBranch } = useBranch();
    const [promoTab, setPromoTab] = useState('special');

    const [customerSession, setCustomerSession] = useState(() => {
        try {
            const saved = localStorage.getItem('dineflow_customer_session');
            if (!saved) return null;
            const parsed = JSON.parse(saved);
            // 24-hr expiry check: if session was saved more than 24h ago, discard it.
            // Also discard old sessions that have no _savedAt (pre-timestamp sessions).
            const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000;
            if (!parsed || !parsed._savedAt || (Date.now() - parsed._savedAt) > SESSION_EXPIRY_MS) {
                localStorage.removeItem('dineflow_customer_session');
                return null;
            }
            return parsed;
        } catch (e) {
            return null;
        }
    });
    const [redeemSettings, setRedeemSettings] = useState(null);
    const [useRedeem, setUseRedeem] = useState(false);
    const [pointsToRedeem, setPointsToRedeem] = useState(0);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const [showCheckoutConfirmModal, setShowCheckoutConfirmModal] = useState(false);

    // Shiprocket Delivery States (Company 37 Only)
    const [isOfflineDelivery, setIsOfflineDelivery] = useState(false);
    const [deliveryName, setDeliveryName] = useState('');
    const [deliveryPhone, setDeliveryPhone] = useState('');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [deliveryCity, setDeliveryCity] = useState('');
    const [deliveryState, setDeliveryState] = useState('');
    const [deliveryPincode, setDeliveryPincode] = useState('');
    const [deliveryLat, setDeliveryLat] = useState(null);
    const [deliveryLng, setDeliveryLng] = useState(null);
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const [outOfRangeModal, setOutOfRangeModal] = useState({ show: false, message: '' });
    const [isMapLoading, setIsMapLoading] = useState(false);
    const [orderTracking, setOrderTracking] = useState({});
    const [simulateTracking, setSimulateTracking] = useState({});

    // Custom Profile, Saved Addresses & Payment states
    const [savedAddresses, setSavedAddresses] = useState([]);
    const [selectedAddressId, setSelectedAddressId] = useState('new');
    const [saveAddressToProfile, setSaveAddressToProfile] = useState(false);
    const [addressLabel, setAddressLabel] = useState('Home');
    const [paymentMethod, setPaymentMethod] = useState('cash');
    
    // Address Edit States
    const [editingAddressId, setEditingAddressId] = useState(null);
    const [editLabel, setEditLabel] = useState('Home');
    const [editName, setEditName] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editAddress, setEditAddress] = useState('');
    const [editCity, setEditCity] = useState('');
    const [editState, setEditState] = useState('');
    const [editPincode, setEditPincode] = useState('');

    const handleStartEditAddress = (addr) => {
        setEditingAddressId(addr.id);
        setEditLabel(addr.address_label || 'Home');
        setEditName(addr.name || '');
        setEditPhone(addr.phone || '');
        setEditAddress(addr.address || '');
        setEditCity(addr.city || '');
        setEditState(addr.state || '');
        setEditPincode(addr.pincode || '');
    };

    const handleSaveEditAddress = async (e) => {
        e.preventDefault();
        if (!customerSession?.id || !editingAddressId) return;
        if (!editAddress.trim() || !editCity.trim() || !editState.trim() || !editPincode.trim()) {
            showToast('Please fill all required fields.', 'error');
            return;
        }
        try {
            const res = await fetch(`${API_URL}/api/customer/addresses/${editingAddressId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customer_id: customerSession.id,
                    address_label: editLabel || 'Home',
                    name: editName || null,
                    phone: editPhone || null,
                    address: editAddress,
                    city: editCity,
                    state: editState,
                    pincode: editPincode
                })
            });
            const json = await res.json();
            if (json.success) {
                showToast('Address updated successfully', 'success');
                setEditingAddressId(null);
                fetchSavedAddresses();
            } else {
                showToast(json.message || 'Failed to update address', 'error');
            }
        } catch (err) {
            console.error('Failed to update address:', err);
            showToast('Failed to update address', 'error');
        }
    };
    
    useEffect(() => {
        setHasClickedPaymentLink(false);
    }, [isCartModalOpen, paymentMethod]);
    
    // Customer signup gate modal states (Company 37 & 38 full login/signup)
    const [isGateModalOpen, setIsGateModalOpen] = useState(false);
    const [gateTab, setGateTab] = useState('signup'); // 'signup' or 'login'
    const [gateUsername, setGateUsername] = useState('');
    const [gatePhone, setGatePhone] = useState('');
    const [gateDob, setGateDob] = useState('');

    // Simple Name+Phone gate for all other companies (non-37, non-38)
    const [isSimpleGateOpen, setIsSimpleGateOpen] = useState(false);
    const [simpleGateName, setSimpleGateName] = useState('');
    const [simpleGatePhone, setSimpleGatePhone] = useState('');
    const [simpleGateError, setSimpleGateError] = useState('');
    const [simpleGateLoading, setSimpleGateLoading] = useState(false);

    const fetchSavedAddresses = async () => {
        if (!customerSession?.id) return;
        try {
            const res = await fetch(`${API_URL}/api/customer/addresses?customer_id=${customerSession.id}`);
            const json = await res.json();
            if (json.success && json.data) {
                setSavedAddresses(json.data);
            }
        } catch (err) {
            console.error('Failed to fetch addresses:', err);
        }
    };

    useEffect(() => {
        fetchSavedAddresses();
    }, [customerSession?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Force full login/signup gate for Company 38 only
    // All other companies (including 37) get the simple Name+Phone gate
    useEffect(() => {
        if (!companyInfo) return;

        // 24-hr runtime expiry check (in case page has been open across midnight)
        const SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000;
        let activeSession = customerSession;
        if (!activeSession || !activeSession._savedAt || (Date.now() - activeSession._savedAt) > SESSION_EXPIRY_MS) {
            if (activeSession) {
                localStorage.removeItem('dineflow_customer_session');
                setCustomerSession(null);
            }
            activeSession = null;
        }

        const cid = parseInt(companyInfo.id);
        if (cid === 38) {
            // Company 38 only: full loyalty login/signup gate
            if (!activeSession) {
                setIsGateModalOpen(true);
            } else {
                setIsGateModalOpen(false);
            }
        } else {
            // All other companies (including 37): simple Name+Phone gate
            // Only show if restaurant has enabled collect_customer_info (default: true)
            const shouldCollect = companyInfo.collect_customer_info !== false;
            if (shouldCollect && !activeSession && !token) {
                setIsSimpleGateOpen(true);
            } else {
                setIsSimpleGateOpen(false);
            }
        }
    }, [companyInfo, customerSession, token]);

    // Set offline delivery if table is 0 (Independent QR Code)
    useEffect(() => {
        if (tableNumber === 0 && companyInfo && parseInt(companyInfo.id) === 37) {
            setIsOfflineDelivery(true);
        }
    }, [tableNumber, companyInfo]);

    const cartTotalInr = useMemo(() => {
        return cart.reduce((sum, item) => {
            const price = parseFloat(parseFloat(item.price) > 0 ? item.price : (item.price_inr || 0));
            const discount = item.discount_percentage || 0;
            const discountedPrice = price * (1 - discount / 100);
            return sum + (discountedPrice * item.quantity);
        }, 0);
    }, [cart]);

    const handleSelectSavedAddress = (addressId) => {
        setSelectedAddressId(addressId);
        if (addressId === 'new') {
            setDeliveryAddress('');
            setDeliveryCity('');
            setDeliveryState('');
            setDeliveryPincode('');
            setDeliveryLat(null);
            setDeliveryLng(null);
            // Don't clear name/phone — keep whatever user typed
            return;
        }
        const addr = savedAddresses.find(a => String(a.id) === String(addressId));
        if (addr) {
            setDeliveryAddress(addr.address);
            setDeliveryCity(addr.city);
            setDeliveryState(addr.state);
            setDeliveryPincode(addr.pincode);
            setDeliveryLat(addr.lat);
            setDeliveryLng(addr.lng);
            // Also fill name & phone from saved address
            if (addr.name) setDeliveryName(addr.name);
            if (addr.phone) setDeliveryPhone(addr.phone);
        }
    };

    // Handler for simple Name+Phone gate (all non-37, non-38 companies)
    const handleSimpleGateSubmit = async (e) => {
        e.preventDefault();
        setSimpleGateError('');

        const nameVal = simpleGateName.trim();
        const phoneVal = simpleGatePhone.trim();

        if (!nameVal) {
            setSimpleGateError('Please enter your name.');
            return;
        }
        if (!phoneVal || phoneVal.length !== 10 || !/^\d{10}$/.test(phoneVal)) {
            setSimpleGateError('Please enter a valid 10-digit mobile number.');
            return;
        }

        setSimpleGateLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/customer/lookup-or-create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    company_id: parseInt(companyInfo.id),
                    name: nameVal,
                    phone: phoneVal
                })
            });
            const json = await res.json();
            if (json.success && json.data) {
                const sessionWithTimestamp = { ...json.data, _savedAt: Date.now() };
                setCustomerSession(sessionWithTimestamp);
                localStorage.setItem('dineflow_customer_session', JSON.stringify(sessionWithTimestamp));
                setIsSimpleGateOpen(false);
                if (json.isReturning) {
                    showToast(`Welcome back, ${json.data.username}! Your past orders are loaded.`, 'success');
                } else {
                    showToast(`Welcome, ${json.data.username}!`, 'success');
                }
            } else {
                setSimpleGateError(json.message || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            console.error('[SimpleGate] Error:', err);
            setSimpleGateError('Network error. Please try again.');
        } finally {
            setSimpleGateLoading(false);
        }
    };

    const handleGateSubmit = async (e) => {
        e.preventDefault();
        
        if (gateTab === 'signup') {
            if (!gateUsername.trim()) {
                showToast('Please enter a username.', 'error');
                return;
            }
            if (!gatePhone.trim() || gatePhone.trim().length !== 10) {
                showToast('Please enter a valid 10-digit phone number.', 'error');
                return;
            }
            
            // Check username uniqueness
            try {
                const checkRes = await fetch(`${API_URL}/api/customer/check-username?username=${encodeURIComponent(gateUsername.trim())}&company_id=${companyInfo.id}`);
                const checkJson = await checkRes.json();
                if (checkJson.success && checkJson.exists) {
                    showToast('Username is already taken. Please choose another.', 'error');
                    return;
                }
            } catch (err) {
                console.error(err);
            }

            // Register
            try {
                const res = await fetch(`${API_URL}/api/customer/signup`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        company_id: parseInt(companyInfo.id),
                        username: gateUsername.trim(),
                        phone: gatePhone.trim(),
                        dob: gateDob || null
                    })
                });
                const json = await res.json();
                if (json.success && json.data) {
                    const sessionWithTimestamp = { ...json.data, _savedAt: Date.now() };
                    setCustomerSession(sessionWithTimestamp);
                    localStorage.setItem('dineflow_customer_session', JSON.stringify(sessionWithTimestamp));
                    showToast('Registration successful! Welcome!', 'success');
                    setIsGateModalOpen(false);
                } else {
                    showToast(json.message || 'Registration failed.', 'error');
                }
            } catch (err) {
                showToast('Registration failed. Try again.', 'error');
            }
        } else {
            // Login
            if (!gateUsername.trim()) {
                showToast('Please enter your Username or Phone number.', 'error');
                return;
            }
            try {
                const res = await fetch(`${API_URL}/api/customer/login`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        company_id: parseInt(companyInfo.id),
                        identifier: gateUsername.trim()
                    })
                });
                const json = await res.json();
                if (json.success && json.data) {
                    const sessionWithTimestamp = { ...json.data, _savedAt: Date.now() };
                    setCustomerSession(sessionWithTimestamp);
                    localStorage.setItem('dineflow_customer_session', JSON.stringify(sessionWithTimestamp));
                    showToast('Logged in successfully!', 'success');
                    setIsGateModalOpen(false);
                } else {
                    showToast(json.message || 'Profile not found. Please sign up.', 'error');
                }
            } catch (err) {
                showToast('Login failed. Try again.', 'error');
            }
        }
    };

    const handleDetectLocation = () => {
        if (!navigator.geolocation) {
            showToast('Geolocation is not supported by your browser', 'error');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                setDeliveryLat(lat);
                setDeliveryLng(lng);
                
                showToast('Location detected! Autofilling details...', 'success');
                
                try {
                    const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
                    const json = await res.json();
                    if (json && json.address) {
                        setDeliveryAddress(json.display_name || '');
                        setDeliveryCity(json.address.city || json.address.town || json.address.village || json.address.county || '');
                        setDeliveryState(json.address.state || '');
                        setDeliveryPincode(json.address.postcode || '');
                    }
                } catch (err) {
                    console.error('Reverse geocoding failed:', err);
                }
            },
            (err) => {
                console.error('Geolocation error:', err);
                showToast('Unable to retrieve location. Please fill manually.', 'warning');
            },
            { enableHighAccuracy: true, timeout: 5000 }
        );
    };

    const loadLeafletResources = () => {
        return new Promise((resolve, reject) => {
            if (window.L) {
                resolve();
                return;
            }
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(link);
            
            const script = document.createElement('script');
            script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Leaflet script failed to load'));
            document.head.appendChild(script);
        });
    };

    const initLeafletMap = async () => {
        setIsMapLoading(true);
        try {
            await loadLeafletResources();
            setIsMapLoading(false);
            
            setTimeout(() => {
                const mapContainer = document.getElementById('leaflet-picker-map');
                if (!mapContainer || mapContainer._leaflet_id) return;
                
                const startLat = deliveryLat || 28.6139;
                const startLng = deliveryLng || 77.2090;
                
                const map = window.L.map('leaflet-picker-map').setView([startLat, startLng], 14);
                window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '© OpenStreetMap contributors'
                }).addTo(map);
                
                const marker = window.L.marker([startLat, startLng], { draggable: true }).addTo(map);
                
                const updateLoc = async (lat, lng) => {
                    setDeliveryLat(lat);
                    setDeliveryLng(lng);
                    try {
                        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
                        const json = await res.json();
                        if (json && json.address) {
                            setDeliveryAddress(json.display_name || '');
                            setDeliveryCity(json.address.city || json.address.town || json.address.village || json.address.county || '');
                            setDeliveryState(json.address.state || '');
                            setDeliveryPincode(json.address.postcode || '');
                        }
                    } catch (e) {
                        console.error(e);
                    }
                };

                marker.on('dragend', () => {
                    const pos = marker.getLatLng();
                    updateLoc(pos.lat, pos.lng);
                });
                
                map.on('click', (e) => {
                    const { lat, lng } = e.latlng;
                    marker.setLatLng([lat, lng]);
                    updateLoc(lat, lng);
                });
            }, 100);
        } catch (err) {
            console.error(err);
            setIsMapLoading(false);
        }
    };

    const fetchOrderTracking = async (orderId, forceSimulate = null) => {
        try {
            const isSimulated = forceSimulate !== null ? forceSimulate : !!simulateTracking[orderId];
            const res = await fetch(`${API_URL}/api/orders/${orderId}/tracking${isSimulated ? '?simulate=true' : ''}`);
            const json = await res.json();
            if (json.success) {
                setOrderTracking(prev => ({ ...prev, [orderId]: json }));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const isBirthdayToday = useMemo(() => {
        if (!customerSession?.dob) return false;
        try {
            const dobDate = new Date(customerSession.dob);
            const today = new Date();
            return dobDate.getMonth() === today.getMonth() && dobDate.getDate() === today.getDate();
        } catch (e) {
            return false;
        }
    }, [customerSession?.dob]);

    // Load Redeem Settings & Refresh Customer Points Balance (Company 38 Only)
    useEffect(() => {
        const loadRedeemData = async () => {
            if (!companyInfo || parseInt(companyInfo.id) !== 38) return;

            // 1. Fetch public settings
            try {
                const res = await fetch(`${API_URL}/api/redeem/settings/public`, {
                    headers: { 'x-company-id': '38' }
                });
                const json = await res.json();
                if (json.success && json.data) {
                    setRedeemSettings(json.data);
                    try {
                        await saveToStore('customer_settings', { key: 'redeem_public', data: json.data });
                    } catch (e) { }
                }
            } catch (err) {
                console.error('Failed to load public redeem settings:', err);
                try {
                    const cached = await getFromStore('customer_settings', 'redeem_public');
                    if (cached && cached.data) setRedeemSettings(cached.data);
                } catch (e) { }
            }

            // 2. Fetch fresh customer profile
            if (customerSession?.id) {
                try {
                    const res = await fetch(`${API_URL}/api/customer/profile/${customerSession.id}?company_id=38`);
                    const json = await res.json();
                    if (json.success && json.data) {
                        setCustomerSession(json.data);
                        localStorage.setItem('dineflow_customer_session', JSON.stringify(json.data));
                        try {
                            await saveToStore('customer_settings', { key: `profile_${customerSession.id}`, data: json.data });
                        } catch (e) { }
                    }
                } catch (err) {
                    console.error('Failed to refresh customer points:', err);
                    try {
                        const cached = await getFromStore('customer_settings', `profile_${customerSession.id}`);
                        if (cached && cached.data) {
                            setCustomerSession(cached.data);
                            localStorage.setItem('dineflow_customer_session', JSON.stringify(cached.data));
                        }
                    } catch (e) { }
                }
            }
        };

        loadRedeemData();
    }, [companyInfo, customerSession?.id]);

    const [showBillModal, setShowBillModal] = useState(false);
    const [billData, setBillData] = useState(null);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [sessionCheckedOut, setSessionCheckedOut] = useState(false);
    const [showTableDropdown, setShowTableDropdown] = useState(false);

    // Ref for menuItems to access in socket callback
    const menuItemsRef = React.useRef(menuItems);
    useEffect(() => { menuItemsRef.current = menuItems; }, [menuItems]);

    const specialScrollRef = React.useRef(null);
    const topRatedScrollRef = React.useRef(null);

    const scroll = (ref, direction) => {
        if (ref.current) {
            const scrollAmount = 300;
            ref.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };


    // Handle Branch ID from URL (QR Scan) or LocalStorage Fallback
    useEffect(() => {
        let bId = null;
        const branchParam = searchParams.get('branch_id');

        if (branchParam) {
            bId = parseInt(branchParam);
        } else {
            // Check localStorage Fallback
            try {
                const stored = JSON.parse(localStorage.getItem('scanned_qr_context'));
                if (stored && stored.branch_id) {
                    bId = parseInt(stored.branch_id);
                    console.log('[CustomerPage] Recovered branch from localStorage:', bId);
                }
            } catch (e) { }
        }

        if (bId !== null && !isNaN(bId) && selectedBranch !== bId) {
            console.log('[CustomerPage] Setting branch:', bId);
            setSelectedBranch(bId);
        }
    }, [searchParams, selectedBranch, setSelectedBranch]);

    // Sync active selectedBranch state to URL parameter
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const currentParam = params.get('branch_id');
        
        if (selectedBranch) {
            if (currentParam !== selectedBranch.toString()) {
                params.set('branch_id', selectedBranch.toString());
                window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);
            }
        } else {
            if (currentParam !== null) {
                params.delete('branch_id');
                window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}${window.location.hash}`);
            }
        }
    }, [selectedBranch]);

    // --- Helper Functions ---
    const getAuthHeaders = useCallback(() => {
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        // Add company ID from URL if present (for testing/no-subdomain mode)
        const params = new URLSearchParams(window.location.search);
        const companyId = params.get('companyId');
        if (companyId) {
            headers['x-company-id'] = companyId;
        } else if (currentUser && currentUser.company_id) {
            headers['x-company-id'] = currentUser.company_id.toString();
        } else if (token) {
            // Fallback: If currentUser not loaded yet, try to parse token
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const payload = JSON.parse(window.atob(base64));
                if (payload.company_id) {
                    headers['x-company-id'] = payload.company_id.toString();
                }
            } catch (e) {/* ignore */ }
        }
        return headers;
    }, [token, currentUser]);

    // 0. Optional Authentication for QR Customers (Removed Redirects)
    useEffect(() => {
        const currentTable = searchParams.get('table') || tableNumber;
        console.log('[CustomerPage] Customer session active. Table:', currentTable || 'None');
    }, [searchParams, tableNumber]);

    // 1. Fetch Company Info
    useEffect(() => {
        const fetchCompanyInfo = async () => {
            try {
                // Use token if available to get company info context
                const headers = getAuthHeaders();

                const url = token
                    ? `${API_URL}/api/company/profile`
                    : `${API_URL}/api/company/public`;

                const res = await fetch(url, { headers });
                const json = await res.json();
                if (json.success && json.data) {
                    setCompanyInfo(json.data);
                    localStorage.setItem('redsorm_company_profile_customer', JSON.stringify(json.data));
                }
            } catch (err) {
                console.error("Failed to fetch company info", err);
                const cached = localStorage.getItem('redsorm_company_profile_customer');
                if (cached) {
                    try {
                        const parsed = JSON.parse(cached);
                        setCompanyInfo(parsed);
                        console.log('[Offline] Loaded companyInfo from localStorage cache:', parsed);
                    } catch (e) {}
                }
            }
        };
        fetchCompanyInfo();
    }, [token, getAuthHeaders]);

    // Apply the shared server-side rule on entry for table, no-table and waiting
    // QR URLs. Order creation repeats this validation as the authoritative check.
    useEffect(() => {
        if (!isQrEntry || !companyInfo?.id) {
            setQrAccess({ status: 'allowed', message: '' });
            return;
        }

        let cancelled = false;
        const companyId = searchParams.get('companyId') || searchParams.get('company_id') || companyInfo.id;
        const branchId = searchParams.get('branch_id');
        const checkLocation = async (latitude = null, longitude = null) => {
            const res = await fetch(`${API_URL}/api/qr/location-check`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-company-id': String(companyId) },
                body: JSON.stringify({ company_id: companyId, branch_id: branchId || null, latitude, longitude })
            });
            const json = await res.json();
            return json.data || { enabled: false, valid: true };
        };

        const validateQrEntry = async () => {
            setQrAccess({ status: 'checking', message: '' });
            try {
                // Disabled restrictions return immediately and never request GPS.
                let result = await checkLocation();
                if (!result.enabled || result.valid) {
                    if (!cancelled) setQrAccess({ status: 'allowed', message: '' });
                    return;
                }

                if (result.reason === 'LOCATION_UNAVAILABLE') {
                    if (!isGeolocationAvailable()) {
                        if (!cancelled) setQrAccess({ status: 'blocked', message: 'Location access is required to use this QR code. Please use a device with GPS enabled.' });
                        return;
                    }
                    try {
                        const position = await getHighAccuracyPosition({
                            cacheKey: `qr:${companyId}:${branchId || 'company'}`
                        });
                        result = await checkLocation(position.latitude, position.longitude);
                    } catch (error) {
                        const message = error && error.code === 'TIMEOUT'
                            ? 'GPS signal is weak. Please step outside or move near a window and try again.'
                            : 'We could not read your location. Please enable GPS/location access and scan again.';
                        if (!cancelled) setQrAccess({ status: 'blocked', message });
                        return;
                    }
                }

                if (!cancelled) {
                    setQrAccess(result.valid
                        ? { status: 'allowed', message: '' }
                        : { status: 'blocked', message: result.message || 'This QR code can only be used within the restaurant location radius.' });
                }
            } catch (error) {
                // A transient API failure never blocks the guest; the order API
                // still validates the location before it accepts an order.
                if (!cancelled) setQrAccess({ status: 'allowed', message: '' });
            }
        };

        validateQrEntry();
        return () => { cancelled = true; };
    }, [isQrEntry, companyInfo?.id, searchParams]);

    // 2. Socket Connection
    useEffect(() => {
        const socketToken = token || localStorage.getItem('token');
        const companyId = companyInfo?.id;
        if (!socketToken && !companyId) return;

        const socket = io(API_URL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 3000,
            reconnectionDelayMax: 15000,
            timeout: 10000,
            auth: { 
                token: socketToken,
                companyId: companyId 
            }
        });

        socket.on('order-status-updated', (order) => {
            const isCompany36 = companyInfo && parseInt(companyInfo.id) === 36;
            const myOrders = JSON.parse(localStorage.getItem('my_orders') || '[]');
            const isMyOrder = myOrders.includes(order.id);

            setCustomerOrders(prev => {
                const targetOrder = prev.find(o => o.id === order.id);

                // Check for Self-Service Ready Notification
                if (order.order_status === 'ready' && (targetOrder?.order_status !== 'ready' || isMyOrder)) {
                    const notifiedReady = JSON.parse(sessionStorage.getItem('notified_ready_orders') || '[]');
                    if (!notifiedReady.includes(order.id)) {
                        const hasSelfServe = targetOrder ? targetOrder.items.some(orderItem => {
                            const mId = orderItem.menu_item_id || orderItem.id;
                            const mItem = menuItemsRef.current.find(m => m.id === mId);
                            return mItem && mItem.is_self_service;
                        }) : false;

                        if (hasSelfServe || isCompany36) {
                            showToast(`${t('order')} #${order.waiting_number || order.id}: Ready for Pickup! 🥡`, 'success');
                            try { new Audio('/notification.mp3').play().catch(e => console.log(e)); } catch (e) { }
                            notifiedReady.push(order.id);
                            sessionStorage.setItem('notified_ready_orders', JSON.stringify(notifiedReady));
                        }
                    }
                }

                // Trigger feedback if just delivered AND not already given
                if (targetOrder && targetOrder.order_status !== 'delivered' && order.order_status === 'delivered') {
                    if (!order.has_feedback) {
                        setFeedbackModal({ show: true, orderId: order.id, items: [] });
                        const audio = new Audio('/notification.mp3');
                        audio.play().catch(e => console.log(e));
                    }
                }

                return prev.map(o => o.id === order.id ? { ...o, order_status: order.order_status, updated_at: order.updated_at } : o);
            });
        });

        socket.on('order-updated', (updatedOrder) => {
            setCustomerOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
        });

        return () => socket.disconnect();
    }, [token, companyInfo?.id, companyInfo, t, showToast]);

    // const playNotificationSound = () => {
    //     const audio = new Audio('/notification.mp3');
    //     audio.play().catch(e => console.log('Audio play failed', e));
    // };

    // Feedback & Cancellation State
    const [feedbackModal, setFeedbackModal] = useState({ show: false, orderId: null, items: [] });
    const [feedbackRating, setFeedbackRating] = useState(5);
    const [feedbackComment, setFeedbackComment] = useState('');

    const [cancellationModal, setCancellationModal] = useState({ show: false, type: null, orderId: null, itemId: null });
    const [cancellationReason, setCancellationReason] = useState('');

    // Payment Modal State (Deprecated in favor of Bill Modal)

    // Track previous statuses to trigger feedback
    // const prevOrderStatuses = React.useRef({});




    // --- Data Loading Functions ---
    const loadTables = useCallback(async () => {
        let currentBranch = selectedBranch;
        if (!currentBranch) {
            try {
                const stored = JSON.parse(localStorage.getItem('scanned_qr_context'));
                if (stored?.branch_id) currentBranch = parseInt(stored.branch_id);
            } catch (e) { }
        }
        try {
            console.log('[loadTables] Selected Branch:', currentBranch);
            const branchQuery = currentBranch ? `?branch_id=${currentBranch}` : '';
            console.log('[loadTables] API URL:', `${API_URL}/api/tables${branchQuery}`);

            const response = await fetch(`${API_URL}/api/tables${branchQuery}`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });
            console.log('[CustomerPage] Tables response status:', response.status);
            if (response.status === 401 || response.status === 403) {
                console.log('[CustomerPage] Tables auth failed, logging out');
                if (token) logout();
                return;
            }
            const data = await response.json();
            console.log('[CustomerPage] Tables data:', data);
            if (data.success) {
                console.log('[CustomerPage] Setting tables, count:', data.data?.length);
                setTables(data.data);
                try {
                    await syncStoreWithServer('tables', data.data);
                } catch (dbErr) {
                    console.error('[CustomerPage] Failed to save tables to IndexedDB:', dbErr);
                }
            } else {
                console.error('[CustomerPage] Tables API Error:', data.message);
            }
        } catch (error) {
            console.error('[CustomerPage] Error loading tables:', error);
            try {
                const cachedTables = await getAllFromStore('tables');
                if (cachedTables && cachedTables.length > 0) {
                    const filteredTables = currentBranch 
                        ? cachedTables.filter(t => t.branch_id === currentBranch)
                        : cachedTables;
                    console.log('[CustomerPage] Loaded tables from IndexedDB:', filteredTables.length);
                    setTables(filteredTables);
                }
            } catch (dbErr) {
                console.error('[CustomerPage] Failed to load tables from IndexedDB:', dbErr);
            }
        }
    }, [getAuthHeaders, logout, selectedBranch, token]);

    const loadCategories = useCallback(async () => {
        let currentBranch = selectedBranch;
        if (!currentBranch) {
            try {
                const stored = JSON.parse(localStorage.getItem('scanned_qr_context'));
                if (stored?.branch_id) currentBranch = parseInt(stored.branch_id);
            } catch (e) { }
        }
        try {
            const branchQuery = currentBranch ? `?branch_id=${currentBranch}` : '';
            const response = await fetch(`${API_URL}/api/categories${branchQuery}`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });
            if (response.status === 401 || response.status === 403) {
                if (token) logout();
                return;
            }
            const data = await response.json();
            if (data.success) {
                setCategories(data.data);
                try {
                    await syncStoreWithServer('categories', data.data);
                } catch (dbErr) {
                    console.error('[CustomerPage] Failed to save categories to IndexedDB:', dbErr);
                }
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            try {
                const cachedCategories = await getAllFromStore('categories');
                if (cachedCategories && cachedCategories.length > 0) {
                    const filteredCategories = currentBranch
                        ? cachedCategories.filter(c => c.branch_id === currentBranch)
                        : cachedCategories;
                    console.log('[CustomerPage] Loaded categories from IndexedDB:', filteredCategories.length);
                    setCategories(filteredCategories);
                } else {
                    setCategories([]);
                }
            } catch (dbErr) {
                console.error('[CustomerPage] Failed to load categories from IndexedDB:', dbErr);
                setCategories([]);
            }
        }
    }, [getAuthHeaders, logout, selectedBranch, token]);

    const loadMenu = useCallback(async () => {
        setIsLoading(true);
        let currentBranch = selectedBranch;
        if (!currentBranch) {
            try {
                const stored = JSON.parse(localStorage.getItem('scanned_qr_context'));
                if (stored?.branch_id) currentBranch = parseInt(stored.branch_id);
            } catch (e) { }
        }
        try {
            const branchQuery = currentBranch ? `?branch_id=${currentBranch}` : '';
            console.log('[loadMenu] Selected Branch:', currentBranch);
            console.log('[loadMenu] API URL:', `${API_URL}/api/menu${branchQuery}`);

            const response = await fetch(`${API_URL}/api/menu${branchQuery}`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401 || response.status === 403) {
                if (token) logout();
                return;
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Menu error response:', errorText);
                throw new Error(`Server responded with ${response.status}: ${errorText}`);
            }

            const data = await response.json();
            console.log('[loadMenu] Response data:', data);
            console.log('[loadMenu] Items received:', data.data?.length || 0);

            if (data.success) {
                // Filter out placeholder items
                const validItems = (data.data || []).filter(item =>
                    !item.name.toLowerCase().includes('placeholder') &&
                    item.category !== 'add-new' &&
                    item.is_available !== false
                );
                console.log('[loadMenu] Valid items after filter:', validItems.length);
                setMenuItems(validItems);
                try {
                    await syncStoreWithServer('menu', validItems);
                } catch (dbErr) {
                    console.error('[CustomerPage] Failed to save menu items to IndexedDB:', dbErr);
                }
            } else {
                console.error('API Error:', data.message);
            }
        } catch (error) {
            console.error('Error loading menu:', error);
            try {
                const cachedMenu = await getAllFromStore('menu');
                if (cachedMenu && cachedMenu.length > 0) {
                    const filteredMenu = currentBranch
                        ? cachedMenu.filter(m => String(m.branch_id) === String(currentBranch))
                        : cachedMenu;
                    console.log('[CustomerPage] Loaded menu items from IndexedDB:', filteredMenu.length);
                    setMenuItems(filteredMenu);
                }
            } catch (dbErr) {
                console.error('[CustomerPage] Failed to load menu from IndexedDB:', dbErr);
            }
        } finally {
            setIsLoading(false);
        }
    }, [getAuthHeaders, logout, selectedBranch, token]);

    const loadTableAvailabilityOrders = useCallback(async () => {
        if (!token) return;

        let currentBranch = selectedBranch;
        if (!currentBranch) {
            try {
                const stored = JSON.parse(localStorage.getItem('scanned_qr_context'));
                if (stored?.branch_id) currentBranch = parseInt(stored.branch_id);
            } catch (e) { }
        }

        try {
            const branchQuery = currentBranch ? `?branch_id=${currentBranch}` : '';
            const response = await fetch(`${API_URL}/api/orders${branchQuery}`, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401 || response.status === 403) {
                if (token) logout();
                return;
            }

            const data = await response.json();
            if (data.success) {
                setTableAvailabilityOrders(data.data || []);
            } else {
                setTableAvailabilityOrders([]);
            }
        } catch (error) {
            console.error('[CustomerPage] Error loading order availability data:', error);
            setTableAvailabilityOrders([]);
        }
    }, [getAuthHeaders, logout, selectedBranch, token]);

    const loadCustomerOrders = useCallback(async () => {
        try {
            // Check 30-min grace period for checked-out sessions
            const checkoutTimeStr = localStorage.getItem('session_checkout_time');
            if (checkoutTimeStr) {
                const checkoutTime = parseInt(checkoutTimeStr);
                const elapsedMin = (Date.now() - checkoutTime) / (1000 * 60);
                if (elapsedMin >= 30) {
                    // 30 minutes elapsed, clean up checked-out session
                    localStorage.removeItem('guest_session_id');
                    localStorage.removeItem('my_orders');
                    localStorage.removeItem('session_checkout_time');
                    setCustomerOrders([]);
                    setCurrentSessionId(null);
                    setBillData(null);
                    return;
                }
            }

            // Get orders from localStorage (for guests) OR from server history (for logged in users)
            const storedOrders = JSON.parse(localStorage.getItem('my_orders') || '[]');
            
            // Construct URL with cache buster
            let url = `${API_URL}/api/customer/orders?t=${Date.now()}`;
            if (token) {
                // Authenticated admin/staff user — server handles by JWT
            } else if (customerSession?.id) {
                // Simple gate session (Name+Phone) — fetch by customer_id to see active session + history
                url += `&customer_id=${customerSession.id}`;
            } else if (storedOrders.length > 0) {
                // Anonymous guest with only localStorage order IDs
                url += `&ids=${storedOrders.join(',')}`;
            } else {
                // Completely empty guest with no session
                setCustomerOrders([]);
                return;
            }

            const response = await fetch(url, {
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                }
            });
            if (response.status === 401 || response.status === 403) {
                if (token) logout();
                return;
            }

            const data = await response.json();
            if (data.success) {
                setCustomerOrders(data.data || []);
                try {
                    await syncStoreWithServer('orders', data.data || []);
                } catch (dbErr) {
                    console.error('[CustomerPage] Failed to save orders to IndexedDB:', dbErr);
                }
                
                // Extract the active session_id from the loaded orders
                if (data.data && data.data.length > 0) {
                    const activeOrder = data.data.find(o => o.session_checkout_at === null);
                    const sid = activeOrder ? activeOrder.session_id : null;
                    setCurrentSessionId(sid);
                    setSessionCheckedOut(!activeOrder);
                } else {
                    setCurrentSessionId(null);
                    setSessionCheckedOut(false);
                }
            }
        } catch (error) {
            console.error('Error loading orders:', error);
            // OFFLINE FALLBACK - load my guest/user orders from local IndexedDB
            try {
                const storedOrders = JSON.parse(localStorage.getItem('my_orders') || '[]');
                if (storedOrders.length > 0) {
                    const cachedOrders = await getAllFromStore('orders');
                    const queuedOrders = await getAllFromStore('orders_queue');
                    const allLocal = [...queuedOrders, ...cachedOrders];
                    // Filter down to the user's orders
                    const myLocalOrders = allLocal.filter(o => storedOrders.includes(o.id) || storedOrders.includes(o.order_id));
                    
                    // Sort them chronologically
                    myLocalOrders.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

                    setCustomerOrders(myLocalOrders);

                    if (myLocalOrders.length > 0) {
                        const activeOrder = myLocalOrders.find(o => !o.session_checkout_at);
                        const sid = activeOrder ? activeOrder.session_id : null;
                        setCurrentSessionId(sid);
                        setSessionCheckedOut(!activeOrder);
                    } else {
                        setCurrentSessionId(null);
                        setSessionCheckedOut(false);
                    }
                } else {
                    setCustomerOrders([]);
                    setCurrentSessionId(null);
                    setSessionCheckedOut(false);
                }
            } catch (dbErr) {
                console.error('[IndexedDB] Load customer orders failed offline:', dbErr);
            }
        }
    }, [token, customerSession, getAuthHeaders, logout]);

    // Offline Synchronization Manager for Customer Page (POS ⇄ Cloud)
    const syncOfflineOrders = useCallback(async () => {
        try {
            const queue = await getQueue();
            if (!queue || queue.length === 0) return;

            console.log(`[Offline Sync] Found ${queue.length} offline orders to sync...`);

            let syncedCount = 0;
            // Loop through queue and place each order using normal /api/orders
            for (const order of queue) {
                const apiOrderData = {
                    table_number: order.is_offline_delivery ? 0 : (order.table_number === 'Delivery' ? 0 : order.table_number),
                    is_waiting: order.is_offline_delivery ? false : order.is_waiting,
                    items: order.items.map(item => ({
                        id: item.menu_item_id || item.id,
                        quantity: item.quantity,
                        price_inr: item.price_inr,
                        price_usd: item.price_usd,
                        name: item.item_name || item.name,
                        discount_percentage: item.discount_percentage || 0
                    })),
                    currency: order.currency,
                    payment_method: order.payment_method,
                    customer_id: order.customer_id,
                    customer_name: order.customer_name,
                    customer_phone: order.customer_phone || null,
                    branch_id: order.branch_id,
                    session_id: order.session_id,
                    notes: order.notes,
                    is_offline_delivery: order.is_offline_delivery ? true : false,
                    delivery_address: order.delivery_address || null,
                    delivery_city: order.delivery_city || null,
                    delivery_state: order.delivery_state || null,
                    delivery_pincode: order.delivery_pincode || null,
                    delivery_lat: order.delivery_lat || null,
                    delivery_lng: order.delivery_lng || null
                };

                const response = await fetch(`${API_URL}/api/orders`, {
                    method: 'POST',
                    headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(apiOrderData)
                });

                const data = await response.json();
                if (data.success) {
                    console.log(`[Offline Sync] Synchronized order ${order.id} successfully!`);
                    await removeFromQueue(order.id);
                    syncedCount++;

                    // Replace the local order ID in my_orders history with the server order ID
                    const myOrders = JSON.parse(localStorage.getItem('my_orders') || '[]');
                    const index = myOrders.indexOf(order.id);
                    if (index !== -1) {
                        myOrders[index] = data.data.id;
                        localStorage.setItem('my_orders', JSON.stringify(myOrders));
                    }
                } else {
                    console.error(`[Offline Sync] Failed to sync order ${order.id}:`, data.message);
                }
            }

            if (syncedCount > 0) {
                // Reload customer orders from server
                loadCustomerOrders();

                // Notify KDS in same browser to refresh from server now that orders are synced
                try {
                    const syncChannel = new BroadcastChannel('redsorm_sync_channel');
                    syncChannel.postMessage({ type: 'ORDERS_SYNCED' });
                    setTimeout(() => { try { syncChannel.close(); } catch (e) {} }, 1000);
                    console.log('[Offline Sync] Broadcasted ORDERS_SYNCED to KDS.');
                } catch (bcErr) {
                    console.warn('[Offline Sync] BroadcastChannel error on sync complete:', bcErr);
                }

                showToast(`${syncedCount} offline order(s) synchronized successfully!`, 'success');
            }
        } catch (error) {
            console.error('[Offline Sync] Error during synchronization:', error);
        }
    }, [getAuthHeaders, loadCustomerOrders, showToast]);

    useEffect(() => {
        const handleOnline = () => {
            console.log('[Connection] Network online. Syncing offline orders...');
            syncOfflineOrders();
        };

        window.addEventListener('online', handleOnline);

        // Check immediately on load if online
        if (navigator.onLine) {
            syncOfflineOrders();
        }

        // Heartbeat check every 30 seconds
        const syncInterval = setInterval(() => {
            if (navigator.onLine) {
                syncOfflineOrders();
            }
        }, 30000);

        return () => {
            window.removeEventListener('online', handleOnline);
            clearInterval(syncInterval);
        };
    }, [syncOfflineOrders]);

    // --- Cart Functions ---
    const addToCart = (itemId) => {
        const item = menuItems.find(i => i.id === itemId);
        if (!item) return;
        setCart(currentCart => {
            const existingItem = currentCart.find(i => i.id === itemId);
            if (existingItem) {
                return currentCart.map(i => i.id === itemId ? { ...i, quantity: i.quantity + 1 } : i);
            } else {
                return [...currentCart, { ...item, quantity: 1 }];
            }
        });
        showToast('Item added to cart!', 'success');
    };

    const removeFromCart = (itemId) => {
        setCart(currentCart => currentCart.filter(i => i.id !== itemId));
    };

    const updateQuantity = (itemId, change) => {
        setCart(currentCart => {
            return currentCart.map(item => {
                if (item.id === itemId) {
                    const newQuantity = Math.max(0, item.quantity + change);
                    return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
                }
                return item;
            }).filter(Boolean);
        });
    };

    const placeOrder = async () => {
        if (cart.length === 0) return;
        
        // Delivery Validation for Company 37
        if (isOfflineDelivery && companyInfo && parseInt(companyInfo.id) === 37) {
            if (!deliveryName.trim()) {
                showToast('Please enter receiver name for delivery.', 'error');
                return;
            }
            if (!deliveryPhone.trim() || deliveryPhone.trim().length !== 10) {
                showToast('Please enter a valid 10-digit mobile number.', 'error');
                return;
            }
            if (!deliveryAddress.trim()) {
                showToast('Please enter your delivery address.', 'error');
                return;
            }
            if (!deliveryCity.trim() || !deliveryState.trim() || !deliveryPincode.trim()) {
                showToast('Please fill City, State, and Pincode for delivery.', 'error');
                return;
            }
        }
        
        const isCompany36 = companyInfo && parseInt(companyInfo.id) === 36;
        if (isCompany36 && customerPhone && customerPhone.trim().length > 0 && customerPhone.trim().length !== 10) {
            showToast('Please enter a valid 10-digit mobile number or leave it blank.', 'error');
            return;
        }

        // UPI manual payment verification validation (UTR check)
        if (paymentMethod === 'online' && companyInfo?.upi_id) {
            if (!hasClickedPaymentLink) {
                showToast('Please click PhonePe, GPay, or Paytm link to pay first.', 'error');
                return;
            }
            if (!utrNumber.trim()) {
                showToast('Please enter the 12-digit UPI Reference ID / UTR ID to confirm payment.', 'error');
                return;
            }
            if (!/^\d{12}$/.test(utrNumber.trim())) {
                showToast('Please enter a valid 12-digit UPI Reference ID (numbers only).', 'error');
                return;
            }
        }

        // Save new address to profile if checked (includes name + phone)
        if (isOfflineDelivery && selectedAddressId === 'new' && saveAddressToProfile && customerSession?.id) {
            try {
                await fetch(`${API_URL}/api/customer/addresses`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        customer_id: customerSession.id,
                        address_label: addressLabel || 'Home',
                        name: deliveryName || null,
                        phone: deliveryPhone || null,
                        address: deliveryAddress,
                        city: deliveryCity,
                        state: deliveryState,
                        pincode: deliveryPincode,
                        lat: deliveryLat,
                        lng: deliveryLng
                    })
                });
                fetchSavedAddresses(); // Refresh list in background
            } catch (e) {
                console.error('Failed to save address:', e);
            }
        }

        const isWaiting = tableNumber === 'waiting';

        let localSessionId = localStorage.getItem('guest_session_id');
        const checkoutTimeStr = localStorage.getItem('session_checkout_time');
        if (checkoutTimeStr || sessionCheckedOut || !localSessionId) {
            localSessionId = 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('guest_session_id', localSessionId);
            localStorage.removeItem('session_checkout_time');
            setSessionCheckedOut(false);
            setCurrentSessionId(null);
        }

        // Calculate totals for DB compatibility
        let total_inr = 0;
        let total_usd = 0;
        cart.forEach(item => {
            const disc = parseFloat(item.discount_percentage || 0);
            const pInr = parseFloat(parseFloat(item.price) > 0 ? item.price : (item.price_inr || 0));
            const pUsd = parseFloat(item.price_usd || 0);
            const qty = parseInt(item.quantity || 1);
            total_inr += (pInr * (1 - disc / 100)) * qty;
            total_usd += (pUsd * (1 - disc / 100)) * qty;
        });

        const activeTableObj = tables.find(t => String(t.table_number) === String(tableNumber));

        const isNoTableParam = searchParams.get('no_table') === '1' || searchParams.get('no_table') === 'true' || !!companyInfo?.no_tables_mode || tableNumber === 0 || tableNumber === '0' || tableNumber === 'none';

        const orderData = {
            table_number: isOfflineDelivery ? 0 : (isWaiting ? 'Waiting' : (isNoTableParam ? 0 : tableNumber)),
            no_table: isNoTableParam,
            is_waiting: isOfflineDelivery ? false : isWaiting,
            items: cart.map(i => ({
                id: i.id,
                menu_item_id: i.id,
                item_name: i.name,
                name: i.name,
                quantity: i.quantity,
                price: currentCurrency === 'INR' ? (parseFloat(i.price) > 0 ? i.price : (i.price_inr || 0)) : (i.price_usd || 0),
                price_inr: parseFloat(i.price) > 0 ? i.price : (i.price_inr || 0),
                price_usd: i.price_usd,
                discount_percentage: i.discount_percentage || 0,
                item_status: 'pending'
            })),
            currency: currentCurrency,
            payment_method: paymentMethod,
            payment_id: paymentMethod === 'online' ? utrNumber.trim() : null,
            customer_id: currentUser?.id || customerSession?.id || null,
            customer_name: isOfflineDelivery ? deliveryName : (currentUser?.full_name || customerSession?.username || (isWaiting ? `Waiting Guest` : (isNoTableParam ? `Guest Order` : `Guest Table ${tableNumber}`))),
            customer_phone: isOfflineDelivery ? deliveryPhone : (
                (companyInfo && parseInt(companyInfo.id) === 36) ? customerPhone :
                (customerSession?.phone || null)
            ),
            branch_id: selectedBranch || activeTableObj?.branch_id || null, // Pass branch context safely
            session_id: currentSessionId || localSessionId,
            total_amount_inr: total_inr.toFixed(2),
            total_amount_usd: total_usd.toFixed(2),
            
            // Offline Delivery Fields
            is_offline_delivery: isOfflineDelivery,
            delivery_address: isOfflineDelivery ? deliveryAddress : null,
            delivery_city: isOfflineDelivery ? deliveryCity : null,
            delivery_state: isOfflineDelivery ? deliveryState : null,
            delivery_pincode: isOfflineDelivery ? deliveryPincode : null,
            delivery_lat: isOfflineDelivery ? deliveryLat : null,
            delivery_lng: isOfflineDelivery ? deliveryLng : null
        };

        // Always attempt the server first — the backend may be reachable on the
        // local network even when navigator.onLine is false (no internet but LAN up).
        // Only fall back to offline queue if the request actually fails.
        const submitOrder = async (lat, lng) => {
            if (lat !== null && lng !== null) {
                orderData.qr_latitude = lat;
                orderData.qr_longitude = lng;
            }

            try {
                const response = await fetch(`${API_URL}/api/orders`, {
                    method: 'POST',
                    headers: {
                        ...getAuthHeaders(),
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(orderData)
                });

            const data = await response.json();
            if (data.success) {
                const selfService = cart.some(item => item.is_self_service);
                setHasSelfServiceInOrder(selfService);

                const isCompany36 = companyInfo && parseInt(companyInfo.id) === 36;
                if (isCompany36) {
                    localStorage.setItem('customer_phone_36', customerPhone);
                    setPendingPaymentOrder(data.data);
                } else {
                    setWaitingNumber(data.data.waiting_number);
                    setPlacedOrderNumber(data.data.order_number);
                    setShowOrderSuccess(true);
                    setCart([]);
                    setIsCartModalOpen(false);
                    setIsOfflineDelivery(false);
                    setDeliveryName('');
                    setDeliveryPhone('');
                    setDeliveryAddress('');
                    setDeliveryCity('');
                    setDeliveryState('');
                    setDeliveryPincode('');
                    setDeliveryLat(null);
                    setDeliveryLng(null);
                    setUtrNumber('');
                    setPaymentMethod('cash');
                }

                // Track order in localStorage for guest history
                const myOrders = JSON.parse(localStorage.getItem('my_orders') || '[]');
                if (!myOrders.includes(data.data.id)) {
                    myOrders.push(data.data.id);
                    localStorage.setItem('my_orders', JSON.stringify(myOrders));
                }

                loadCustomerOrders();
                showToast(isCompany36 ? 'Order placed! Please complete payment.' : 'Order placed successfully!', 'success');
                return;
            }
            // Server responded but with success: false
            if (data.code === 'OUTSIDE_ALLOWED_RADIUS') {
                setIsCartModalOpen(false); // Close cart to show error clearly
                setOutOfRangeModal({ show: true, message: data.message });
            } else {
                showToast(data.message || 'Failed to place order. Please try again.', 'error');
            }
            return;
        } catch (networkErr) {
            // Network error (server unreachable) — fall through to offline queue below
            console.warn('[CustomerPage] Server unreachable, saving order offline:', networkErr.message);
        }

        // ── Offline fallback ──────────────────────────────────────────────────
        // Reached here if: (a) network error, (b) server returned success:false,
        // (c) navigator.onLine was false (fetch would have thrown immediately).
        try {
            const queuedOrder = await addToQueue(orderData);
            setShowOrderSuccess(true);
            setCart([]);
            setIsCartModalOpen(false);
            setIsOfflineDelivery(false);
            setDeliveryName('');
            setDeliveryPhone('');
            setDeliveryAddress('');
            setDeliveryCity('');
            setDeliveryState('');
            setDeliveryPincode('');
            setDeliveryLat(null);
            setDeliveryLng(null);
            setUtrNumber('');
            setPaymentMethod('cash');

            const myOrders = JSON.parse(localStorage.getItem('my_orders') || '[]');
            if (!myOrders.includes(queuedOrder.id)) {
                myOrders.push(queuedOrder.id);
                localStorage.setItem('my_orders', JSON.stringify(myOrders));
            }

            // Broadcast NEW_OFFLINE_ORDER so the KDS on the same device updates instantly
            try {
                const channel = new BroadcastChannel('redsorm_sync_channel');
                channel.postMessage({ type: 'NEW_OFFLINE_ORDER', data: queuedOrder });
                setTimeout(() => { try { channel.close(); } catch (e) {} }, 1000);
                console.log('[CustomerPage] Broadcasted NEW_OFFLINE_ORDER to KDS:', queuedOrder.id);
            } catch (bcErr) {
                console.warn('[CustomerPage] BroadcastChannel error:', bcErr);
            }

            showToast('No connection to server. Order saved locally and will sync automatically.', 'warning');
        } catch (localErr) {
            console.error('[CustomerPage] Failed to save offline order to IndexedDB:', localErr);
            showToast('Could not save order. Please try again.', 'error');
        }
        };

        const locationRestricted = !!(companyInfo && companyInfo.master_qr_location_restriction_enabled);

        if (locationRestricted) {
            if (!isGeolocationAvailable()) {
                showToast('Location access is required to place an order at this restaurant.', 'error');
                return;
            }
            try {
                // Read a precise GPS fix instead of the first cached/Wi-Fi estimate so
                // the radius check is measured from where the guest really is.
                const position = await getHighAccuracyPosition({
                    cacheKey: `qr:${companyInfo?.id}:${searchParams.get('branch_id') || selectedBranch || 'company'}`
                });
                await submitOrder(position.latitude, position.longitude);
            } catch (err) {
                console.error('Geolocation error:', err);
                if (err && err.code === 'TIMEOUT') {
                    showToast('GPS signal is weak. Please step outside or move near a window and try again.', 'error');
                } else if (err && (err.code === 'PERMISSION_DENIED' || err.code === 'UNSUPPORTED')) {
                    showToast('Location access is required to place an order here. Please allow location and try again.', 'error');
                } else {
                    showToast('Could not read your location accurately. Please turn on GPS and try again.', 'error');
                }
                // Do NOT call submitOrder with null — let the user retry with a good fix
            }
        } else {
            submitOrder(null, null);
        }
    };

    const handleMarkPaid = async (orderId) => {
        try {
            const res = await fetch(`${API_URL}/api/customer/orders/${orderId}/mark-paid`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ payment_notes: `UTR: ${utrNumber}` })
            });
            const data = await res.json();
            if (data.success) {
                if (pendingPaymentOrder) {
                    setWaitingNumber(pendingPaymentOrder.waiting_number);
                    setPlacedOrderNumber(pendingPaymentOrder.order_number);
                }
                setShowOrderSuccess(true);
                setPendingPaymentOrder(null);
                setCart([]);
                setIsCartModalOpen(false);
                setUtrNumber('');
                showToast('Payment confirmed! Order sent to kitchen.', 'success');
                loadCustomerOrders();
            } else {
                showToast(data.message || 'Failed to update payment status', 'error');
            }
        } catch (err) {
            console.error('Error marking paid:', err);
            showToast('Server error while marking payment', 'error');
        }
    };

    const handleViewBill = () => {
        const activeOrders = customerOrders.filter(o => o.order_status !== 'cancelled' && o.session_checkout_at === null && o.payment_status !== 'paid');
        if (activeOrders.length === 0) {
            showToast(t('noOrdersToBill') || 'No orders to bill', 'error');
            return;
        }

        const sgst = parseFloat(companyInfo?.sgst || 0);
        const cgst = parseFloat(companyInfo?.cgst || 0);

        let subtotal = 0;

        // Calculate item-wise details
        const billItems = [];
        activeOrders.forEach(order => {
            if (order.items) {
                order.items.forEach(item => {
                    if (item.item_status !== 'cancelled') {
                        const basePrice = parseFloat(item.price_inr || item.price || 0);
                        const qty = parseInt(item.quantity || 1);
                        const itemDiscount = parseFloat(item.discount_percentage || 0);
                        const discountedPrice = basePrice * (1 - itemDiscount / 100);
                        const itemTotal = discountedPrice * qty;

                        billItems.push({
                            name: item.item_name || item.name,
                            quantity: qty,
                            basePrice: basePrice,
                            discountPercentage: itemDiscount,
                            discountedPrice: discountedPrice,
                            total: itemTotal
                        });
                        subtotal += itemTotal;
                    }
                });
            } else {
                subtotal += parseFloat(order.total_amount_inr || 0);
            }
        });

        const sgstAmount = subtotal * (sgst / 100);
        const cgstAmount = subtotal * (cgst / 100);
        let total = subtotal + sgstAmount + cgstAmount;

        let festivalDiscountAmount = 0;
        const festivalDiscountPercent = parseFloat(companyInfo?.festival_discount || 0);
        if (festivalDiscountPercent > 0) {
            festivalDiscountAmount = total * (festivalDiscountPercent / 100);
            total -= festivalDiscountAmount;
        }

        let finalQrCodes = companyInfo?.qr_codes || [];
        if (typeof finalQrCodes === 'string') {
            try {
                finalQrCodes = JSON.parse(finalQrCodes);
            } catch (e) {
                finalQrCodes = [];
            }
        }
        finalQrCodes = (Array.isArray(finalQrCodes) ? finalQrCodes : []).map(qr => {
            if (qr.startsWith('http') || qr.startsWith('data:')) return qr;
            return `${API_URL}${qr.startsWith('/') ? '' : '/'}${qr}`;
        });

        console.log('[Bill] Generating bill for:', activeOrders.map(o => o.id));
        console.log('[Bill] Company Info:', companyInfo);
        
        setBillData({
            items: billItems,
            subtotal,
            sgst,
            cgst,
            sgstAmount,
            cgstAmount,
            festivalName: companyInfo?.festival_name,
            festivalDiscountPercent,
            festivalDiscountAmount,
            total,
            qrCodes: finalQrCodes,
            upiId: companyInfo?.upi_id,
            companyName: companyInfo?.name,
            sessionId: currentSessionId,
            orders: activeOrders
        });
        setShowBillModal(true);
        setIsOrdersModalOpen(false);
    };

    // Checkout session: marks all orders in this session as checked out (30-min grace period begins)
    const handleCheckoutSession = async () => {
        if (!currentSessionId) {
            showToast('No active session to checkout', 'error');
            return;
        }
        setShowCheckoutConfirmModal(true);
    };

    const performCheckout = async () => {
        setShowCheckoutConfirmModal(false);
        try {
            const token = localStorage.getItem('token');

            const bodyPayload = {
                session_id: currentSessionId
            };
            if (parseInt(companyInfo?.id) === 38 && customerSession) {
                bodyPayload.customer_id = customerSession.id;
                bodyPayload.redeemed_points = useRedeem ? pointsToRedeem : 0;
                bodyPayload.apply_birthday_discount = isBirthdayToday;
            }

            const res = await fetch(`${API_URL}/api/customer/checkout-session`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(bodyPayload)
            });
            const data = await res.json();
            if (data.success) {
                setSessionCheckedOut(true);
                setShowBillModal(false);
                showToast('Checked out! Thank you for dining with us 🙏', 'success');
                
                // Save checkout time so we close session but keep order in history for 30 mins
                localStorage.setItem('session_checkout_time', Date.now().toString());

                // If points were updated by backend, refresh customerSession locally
                if (parseInt(companyInfo?.id) === 38 && data.customer) {
                    setCustomerSession(data.customer);
                    localStorage.setItem('dineflow_customer_session', JSON.stringify(data.customer));
                }
            } else {
                showToast(data.message || 'Checkout failed', 'error');
            }
        } catch (err) {
            console.error('[SESSION] Checkout error:', err);
            showToast('Checkout failed. Please try again.', 'error');
        }
    };

    const handleSaveBill = async () => {
        if (!billData) return;

        const getBase64Image = async (imgUrl) => {
            if (imgUrl.startsWith('data:')) {
                return imgUrl;
            }
            try {
                const res = await fetch(imgUrl);
                const blob = await res.blob();
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.onerror = () => resolve(imgUrl);
                    reader.readAsDataURL(blob);
                });
            } catch (e) {
                console.warn('[QR Base64] Failed to convert image to base64:', e);
                return imgUrl;
            }
        };

        // Pre-load and convert QR codes to Base64 for synchronous PDF canvas capture
        const resolvedQrs = [];
        if (billData.qrCodes && billData.qrCodes.length > 0) {
            for (const qr of billData.qrCodes) {
                const b64 = await getBase64Image(qr);
                resolvedQrs.push(b64);
            }
        }

        // Try to load html2pdf first for PDF download
        let html2pdfLoaded = false;
        try {
            if (!window.html2pdf) {
                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
                document.body.appendChild(script);
                await new Promise((resolve, reject) => {
                    script.onload = resolve;
                    script.onerror = reject;
                });
            }
            html2pdfLoaded = true;
        } catch (e) {
            console.warn('[PDF Save] Could not load html2pdf library. Falling back to native print.', e);
        }

        const invoiceId = `INV-${Date.now().toString().slice(-6)}`;
        const dateStr = new Date().toLocaleString();

        // Calculate loyalty adjustments for guest invoice PDF
        let dobDiscount = 0;
        let pointsDiscount = 0;
        let finalTotal = billData.total;

        if (parseInt(companyInfo?.id) === 38 && redeemSettings) {
            const hasBday = customerSession?.dob && isBirthdayToday(customerSession.dob);
            if (hasBday) {
                const value = parseFloat(redeemSettings.dob_discount_value || 0);
                if (value > 0) {
                    if (redeemSettings.dob_discount_type === 'percentage') {
                        dobDiscount = billData.subtotal * (value / 100);
                    } else {
                        dobDiscount = value;
                    }
                }
            }

            const pointVal = parseFloat(redeemSettings?.point_value || 1);
            const currentPts = customerSession?.points || 0;
            const maxPointsAllowed = Math.min(currentPts, Math.floor((billData.total - dobDiscount) / pointVal));
            pointsDiscount = useRedeem ? (maxPointsAllowed * pointVal) : 0;
            finalTotal = Math.max(0, billData.total - dobDiscount - pointsDiscount);
        }


        const invoiceHtml = `
            <div style="font-family: Arial, sans-serif; padding: 40px; color: #333; max-width: 800px; margin: auto; background: #fff;">
                <div style="border-bottom: 2px solid #eee; padding-bottom: 20px;">
                    <table style="width: 100%; border-collapse: collapse;">
                        <tr>
                            <td style="vertical-align: top; text-align: left;">
                                <h1 style="margin: 0; color: #1a1a1a; font-size: 28px;">${billData.companyName || 'Restaurant Invoice'}</h1>
                                <p style="margin: 5px 0; color: #666; font-size: 14px;">Table Number: ${(tableNumber === 0 || tableNumber === '0') ? (parseInt(companyInfo?.id) === 37 ? 'Delivery' : 'No Table') : (tableNumber || 'N/A')}</p>
                                ${parseInt(companyInfo?.id) === 38 && billData.orders ? (() => {
                                    const orderNums = [...new Set(billData.orders.map(o => o.order_number).filter(Boolean))].sort((a, b) => a - b);
                                    if (orderNums.length > 0) {
                                        return `<p style="margin: 5px 0; color: #1a1a1a; font-size: 16px; font-weight: bold;">Order Number: ${orderNums.join(', ')}</p>`;
                                    }
                                    return '';
                                })() : ''}
                            </td>
                            <td style="vertical-align: top; text-align: right;">
                                <h2 style="margin: 0; color: #1a1a1a; font-size: 20px;">INVOICE</h2>
                                <p style="margin: 5px 0; color: #666; font-size: 14px;">#${invoiceId}</p>
                                <p style="margin: 5px 0; color: #666; font-size: 14px;">${dateStr}</p>
                            </td>
                        </tr>
                    </table>
                </div>

                <div style="margin: 30px 0;">
                    <table style="width: 100%; border-collapse: collapse;">
                        <thead>
                            <tr style="background: #f8f9fa; border-bottom: 2px solid #eee;">
                                <th style="text-align: left; padding: 12px; font-size: 14px;">Item Description</th>
                                <th style="text-align: center; padding: 12px; font-size: 14px;">Qty</th>
                                <th style="text-align: right; padding: 12px; font-size: 14px;">Price</th>
                                <th style="text-align: right; padding: 12px; font-size: 14px;">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${billData.items.map(item => `
                                <tr style="border-bottom: 1px solid #eee;">
                                    <td style="padding: 12px; font-size: 14px;">
                                        <div style="font-weight: bold;">${String(item.name).trim()}</div>
                                        ${item.discountPercentage > 0 ? `<div style="font-size: 11px; color: #e53e3e;">${item.discountPercentage}% Discount Applied</div>` : ''}
                                    </td>
                                    <td style="padding: 12px; text-align: center; font-size: 14px;">${item.quantity}</td>
                                    <td style="padding: 12px; text-align: right; font-size: 14px;">₹${(item.discountPercentage > 0 ? item.discountedPrice : item.basePrice).toFixed(2)}</td>
                                    <td style="padding: 12px; text-align: right; font-size: 14px; font-weight: bold;">₹${item.total.toFixed(2)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>

                <table style="width: 100%; margin-top: 30px;">
                    <tr>
                        <td style="width: 55%;"></td>
                        <td style="width: 45%; vertical-align: top;">
                            <table style="width: 100%; border-collapse: collapse;">
                                <tr>
                                    <td style="padding: 5px 0; font-size: 14px; text-align: left;">Subtotal</td>
                                    <td style="padding: 5px 0; font-size: 14px; text-align: right;">₹${billData.subtotal.toFixed(2)}</td>
                                </tr>
                                <tr style="border-top: 1px solid #eee;">
                                    <td style="padding: 5px 0; font-size: 14px; text-align: left; padding-top: 5px;">SGST (${billData.sgst}%)</td>
                                    <td style="padding: 5px 0; font-size: 14px; text-align: right; padding-top: 5px;">₹${billData.sgstAmount.toFixed(2)}</td>
                                </tr>
                                <tr>
                                    <td style="padding: 5px 0; font-size: 14px; text-align: left;">CGST (${billData.cgst}%)</td>
                                    <td style="padding: 5px 0; font-size: 14px; text-align: right;">₹${billData.cgstAmount.toFixed(2)}</td>
                                </tr>
                                ${billData.festivalDiscountPercent > 0 ? `
                                <tr style="color: #e53e3e; font-weight: bold;">
                                    <td style="padding: 5px 0; font-size: 14px; text-align: left;">${billData.festivalName || 'Festival Discount'} (${billData.festivalDiscountPercent}%)</td>
                                    <td style="padding: 5px 0; font-size: 14px; text-align: right;">-₹${billData.festivalDiscountAmount.toFixed(2)}</td>
                                </tr>
                                ` : ''}
                                ${dobDiscount > 0 ? `
                                <tr style="color: #ec4899; font-weight: bold;">
                                    <td style="padding: 5px 0; font-size: 14px; text-align: left;">Birthday Discount</td>
                                    <td style="padding: 5px 0; font-size: 14px; text-align: right;">-₹${dobDiscount.toFixed(2)}</td>
                                </tr>
                                ` : ''}
                                ${pointsDiscount > 0 ? `
                                <tr style="color: #10b981; font-weight: bold;">
                                    <td style="padding: 5px 0; font-size: 14px; text-align: left;">Points Redeemed</td>
                                    <td style="padding: 5px 0; font-size: 14px; text-align: right;">-₹${pointsDiscount.toFixed(2)}</td>
                                </tr>
                                ` : ''}
                                <tr style="border-top: 2px solid #1a1a1a; font-weight: bold; font-size: 20px;">
                                    <td style="padding: 10px 0; text-align: left; padding-top: 10px;">Grand Total</td>
                                    <td style="padding: 10px 0; text-align: right; padding-top: 10px; color: #1a1a1a;">₹${finalTotal.toFixed(2)}</td>
                                </tr>
                            </table>
                        </td>
                    </tr>
                </table>

                <div style="margin-top: 50px; border-top: 2px solid #eee; padding-top: 20px; text-align: center;">
                  <p style="margin: 0; color: #666; font-size: 14px;">Thank you for dining with us!</p>
                </div>
                ${(resolvedQrs && resolvedQrs.length > 0) || billData.upiId ? `
                    <div style="margin-top: 30px; border-top: 1px dashed #eee; padding-top: 20px; text-align: center;">
                        ${billData.upiId ? `
                            <p style="margin: 0 0 10px 0; color: #1a1a1a; font-size: 14px; font-weight: bold;">
                                UPI ID: <span style="font-family: monospace; background: #f3f4f6; padding: 2px 6px; border-radius: 4px;">${billData.upiId}</span>
                            </p>
                        ` : ''}
                        ${resolvedQrs && resolvedQrs.length > 0 ? `
                            <div style="margin-top: 15px;">
                                <p style="font-size: 11px; color: #999; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px;">Scan QR to Pay</p>
                                <div style="display: flex; justify-content: center; gap: 20px; flex-wrap: wrap;">
                                    ${resolvedQrs.map(qr => `
                                        <div style="border: 1px solid #eee; padding: 8px; border-radius: 8px; background: #fff; display: inline-block; margin: 0 10px;">
                                            <img src="${qr}" style="width: 130px; height: 130px; object-fit: contain;" />
                                        </div>
                                    `).join('')}
                                </div>
                            </div>
                        ` : ''}
                    </div>
                ` : ''}
            </div>
        `;

        // Pre-preload the resolved QR codes to browser cache for instant rendering
        if (resolvedQrs && resolvedQrs.length > 0) {
            try {
                await Promise.all(resolvedQrs.map(qr => {
                    return new Promise((resolve) => {
                        const img = new Image();
                        img.onload = () => resolve(img);
                        img.onerror = () => resolve(null);
                        img.src = qr;
                    });
                }));
            } catch (e) {}
        }

        if (html2pdfLoaded) {
            const opt = {
                margin: 0,
                filename: `${billData.companyName || 'Restaurant'}_Bill_Table_${tableNumber || 'NA'}.pdf`,
                image: { type: 'jpeg', quality: 1.0 },
                html2canvas: { scale: 3, useCORS: true, logging: false, scrollY: 0, scrollX: 0 },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
            };

            try {
                showToast('Preparing your invoice...', 'info');
                await window.html2pdf().set(opt).from(invoiceHtml).save();
                showToast('Invoice downloaded successfully!', 'success');
                return;
            } catch (err) {
                console.error('PDF Generation Error:', err);
            }
        }

        // Hidden iframe fallback printing for clean receipt format
        try {
            showToast('Opening print view...', 'info');
            const iframe = document.createElement('iframe');
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = '0';
            document.body.appendChild(iframe);

            const doc = iframe.contentWindow.document;
            doc.write(`
                <html>
                    <head>
                        <title>Print Bill</title>
                        <style>
                            @media print {
                                @page {
                                    size: auto;
                                    margin: 5mm 10mm;
                                }
                                body {
                                    margin: 0;
                                    -webkit-print-color-adjust: exact !important;
                                    print-color-adjust: exact !important;
                                }
                            }
                            body {
                                font-family: Arial, sans-serif;
                                padding: 20px;
                                color: #333;
                                background: #fff;
                                margin: 0;
                            }
                        </style>
                    </head>
                    <body>
                        ${invoiceHtml}
                        <script>
                            window.onload = function() {
                                window.print();
                                setTimeout(function() {
                                    window.frameElement.remove();
                                }, 500);
                            };
                        </script>
                    </body>
                </html>
            `);
            doc.close();
        } catch (printErr) {
            console.error('Print Fallback Error:', printErr);
            showToast('Fallback: Opening standard print', 'warning');
            window.print();
        }
    };



    // --- Cancellation Handlers ---
    const openCancellationModal = (type, orderId, itemId = null) => {
        setCancellationModal({ show: true, type, orderId, itemId });
        setCancellationReason('');
    };

    const handleCancellationSubmit = async () => {
        if (!cancellationReason.trim()) {
            showToast('Please provide a reason for cancellation', 'error');
            return;
        }

        const { type, orderId, itemId } = cancellationModal;
        const url = type === 'order'
            ? `${API_URL}/api/orders/${orderId}/cancel`
            : `${API_URL}/api/orders/${orderId}/items/${itemId}/cancel`;

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    reason: cancellationReason,
                    cancelled_by: 'customer'
                })
            });

            const data = await response.json();
            if (data.success) {
                showToast(type === 'order' ? 'Order cancelled successfully' : 'Item cancelled successfully', 'success');
                setCancellationModal({ show: false, type: null, orderId: null, itemId: null });
                loadCustomerOrders(); // Refresh orders
            } else {
                showToast(data.message || 'Cancellation failed', 'error');
            }
        } catch (error) {
            console.error('Error cancelling:', error);
            showToast('Failed to process cancellation', 'error');
        }
    };

    // --- Feedback Handlers ---
    const handleFeedbackSubmit = async () => {
        try {
            const response = await fetch(`${API_URL}/api/feedback`, {
                method: 'POST',
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    order_id: feedbackModal.orderId,
                    customer_id: currentUser?.id,
                    rating: feedbackRating,
                    comments: feedbackComment
                })
            });

            const data = await response.json();
            if (data.success) {
                setFeedbackModal({ show: false, orderId: null, items: [] });
                setFeedbackRating(5);
                setFeedbackComment('');
                showToast('Thank you for your feedback!', 'success');
                loadCustomerOrders(); // Refresh orders to update has_feedback status
            } else {
                showToast(data.message || 'Failed to submit feedback', 'error');
            }
        } catch (error) {
            console.error('Error submitting feedback:', error);
        }
    };

    // --- Derived State & Timers ---
    // Payment Handlers (Consolidated into handleViewBill)

    // Custom hook for running timers
    const useOrderTimer = (orders) => {
        const [now, setNow] = useState(Date.now());
        useEffect(() => {
            const interval = setInterval(() => setNow(Date.now()), 1000);
            return () => clearInterval(interval);
        }, []);
        return now;
    };

    const currentTime = useOrderTimer(customerOrders);

    const getElapsedTime = (startTime) => {
        if (!startTime) return '0m';
        const start = new Date(startTime).getTime();
        const diff = currentTime - start;
        const mins = Math.floor(diff / 60000);
        const secs = Math.floor((diff % 60000) / 1000);
        if (mins < 1) return `${secs}s`;
        return `${mins}m ${secs}s`;
    };

    const getTotalWaitTime = (start, end) => {
        const s = new Date(start).getTime();
        const e = new Date(end).getTime();
        const diff = e - s;
        const mins = Math.floor(diff / 60000);
        return `${mins} mins`;
    };

    // --- Reorder Logic ---
    const handleReorder = (order) => {
        const newItems = order.items.map(item => ({
            id: item.item_id || item.id, // Handle DB field naming difference if any
            name: item.item_name,
            price_inr: item.price_inr, // Assuming these fields exist on item, might need lookup if not
            price_usd: item.price_usd,
            quantity: item.quantity
        }));

        // Check if we have price info. If not, we might need to fetch menu (already loaded)
        // Let's match with menuItems to get current prices and details
        const cartItems = [];
        newItems.forEach(orderItem => {
            const menuItem = menuItems.find(m => m.id === orderItem.id || m.name === orderItem.name); // Try ID then Name
            if (menuItem) {
                cartItems.push({
                    ...menuItem,
                    quantity: orderItem.quantity
                });
            }
        });

        if (cartItems.length > 0) {
            setCart(prev => [...prev, ...cartItems]);
            setIsCartModalOpen(true);
            setIsOrdersModalOpen(false);
            showToast("Items added to cart!", "success");
        } else {
            showToast("Could not reorder items. They may no longer be available.", "error");
        }
    };

    const uniqueVitamins = useMemo(() => {
        const vitamins = new Set();
        menuItems.forEach(item => {
            if (item.vitamins) {
                item.vitamins.split(',').forEach(v => {
                    const trimmed = v.trim();
                    if (trimmed) vitamins.add(trimmed);
                });
            }
        });
        return Array.from(vitamins).sort();
    }, [menuItems]);

    const filteredMenu = menuItems.filter(item => {
        const matchesCategory = activeCategory === 'all' || item.category === activeCategory;
        const searchLower = (searchTerm || '').toLowerCase();
        const matchesSearch = !searchLower || 
            (item.name && item.name.toLowerCase().includes(searchLower)) || 
            (item.description && item.description.toLowerCase().includes(searchLower)) ||
            (item.ingredients && item.ingredients.toLowerCase().includes(searchLower));

        // Vitamin filter
        let matchesVitamin = true;
        if (activeVitamin !== 'all') {
            matchesVitamin = item.vitamins && item.vitamins.toLowerCase().includes(activeVitamin.toLowerCase());
        }

        // Veg / Non-Veg filter
        let matchesVeg = true;
        if (vegFilter === 'veg') {
            matchesVeg = item.is_veg === 1 || item.is_veg === true || item.is_veg === '1' || item.is_veg === undefined || item.is_veg === null;
        } else if (vegFilter === 'non-veg') {
            matchesVeg = item.is_veg === 0 || item.is_veg === false || item.is_veg === '0';
        }

        return matchesCategory && matchesSearch && matchesVitamin && matchesVeg;
    });

    const specialItems = useMemo(() => {
        return menuItems.filter(item => {
            if (!item.is_special) return false;
            if (!item.special_expiry) return false;
            const expiry = new Date(item.special_expiry);
            return expiry > new Date() && (item.is_available === 1 || item.is_available === true);
        });
    }, [menuItems]);

    const topRatedItems = useMemo(() => {
        return [...menuItems]
            .filter(item => (item.is_available === 1 || item.is_available === true))
            .sort((a, b) => (b.rating || 0) - (a.rating || 0))
            .slice(0, 10);
    }, [menuItems]);

    // Auto-switch away from 'special' if it becomes empty
    useEffect(() => {
        if (!isLoading && specialItems.length === 0 && promoTab === 'special') {
            setPromoTab('top');
        }
    }, [specialItems, promoTab, isLoading]);

    const isManagementRole = useMemo(() => {
        const role = currentUser?.role;
        return role === 'admin' || role === 'manager' || role === 'super_admin' || (typeof role === 'string' && role.includes('manager'));
    }, [currentUser?.role]);

    const tableSelectionOptions = useMemo(() => {
        return tables
            .filter(table => table.is_active !== 0 && table.is_active !== false)
            .map(table => {
                const isManuallyUnavailable = table.is_available === 0 || table.is_available === false;
                const hasActiveOrders = (table.table_number === 0 || table.table_number === '0') ? false : tableAvailabilityOrders.some(order => {
                    if (order.is_waiting || ['cancelled', 'completed'].includes(order.order_status)) {
                        return false;
                    }

                    const matchTable = String(order.table_number) === String(table.table_number);
                    const oBranch = order.branch_id ? String(order.branch_id) : '';
                    const tBranch = table.branch_id ? String(table.branch_id) : '';
                    const matchBranch = !order.branch_id || !table.branch_id || oBranch === tBranch;

                    return matchTable && matchBranch && ['pending', 'preparing', 'ready'].includes(order.order_status);
                });

                return {
                    ...table,
                    isOccupied: hasActiveOrders || isManuallyUnavailable
                };
            });
    }, [tables, tableAvailabilityOrders]);

    const availableTableOptions = useMemo(() => tableSelectionOptions.filter(table => !table.isOccupied), [tableSelectionOptions]);
    const occupiedTableOptions = useMemo(() => tableSelectionOptions.filter(table => table.isOccupied), [tableSelectionOptions]);
    const displayedTableOptions = useMemo(() => {
        if (!isManagementRole) return tableSelectionOptions;
        return [...availableTableOptions, ...occupiedTableOptions];
    }, [availableTableOptions, occupiedTableOptions, isManagementRole, tableSelectionOptions]);

    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    const cartTotal = cart.reduce((sum, item) => {
        const price = currentCurrency === 'INR' ? item.price_inr : item.price_usd;
        const discount = item.discount_percentage || 0;
        const discountedPrice = price * (1 - discount / 100);
        return sum + (discountedPrice * item.quantity);
    }, 0);

    // --- useEffect Hooks ---
    // --- useEffect Hooks ---

    // 1. Load static data on mount
    // 1. Socket connection for live status updates
    useEffect(() => {
        const socketToken = token || localStorage.getItem('token');
        const companyId = companyInfo?.id;
        if (!socketToken && !companyId) return;

        const socket = io(API_URL, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: 10,
            reconnectionDelay: 3000,
            reconnectionDelayMax: 15000,
            timeout: 10000,
            auth: { 
                token: socketToken,
                companyId: companyId 
            }
        });

        socket.on('order-status-updated', (updatedOrder) => {
            setCustomerOrders(prev => {
                const isTableAssigned = prev.some(o => o.id === updatedOrder.id && o.is_waiting && !updatedOrder.is_waiting);
                if (isTableAssigned && updatedOrder.table_number && updatedOrder.table_number !== 'Waiting') {
                    setTableNumber(parseInt(updatedOrder.table_number));
                }
                return prev.map(o => {
                    if (o.id === updatedOrder.id) {
                        const wasWaiting = o.is_waiting && !updatedOrder.is_waiting;
                        return { ...o, ...updatedOrder, was_assigned_from_waiting: o.was_assigned_from_waiting || wasWaiting };
                    }
                    return o;
                });
            });
            loadTableAvailabilityOrders();
        });

        socket.on('waiting-list-updated', () => {
            loadCustomerOrders();
            loadTableAvailabilityOrders();
        });

        socket.on('new-order', (order) => {
            if (currentUser && order.customer_id === currentUser.id) {
                loadCustomerOrders();
            }
            loadTableAvailabilityOrders();
        });

        return () => {
            socket.disconnect();
        };
    }, [token, companyInfo?.id, currentUser, loadCustomerOrders, t, loadTableAvailabilityOrders]);

    // 10-second polling fallback loop (specifically for guests who cannot use WebSocket auth, or general fallback)
    useEffect(() => {
        const intervalId = setInterval(() => {
            const hasGuestContext = searchParams.get('table') && searchParams.get('companyId');
            if (token || hasGuestContext) {
                loadCustomerOrders();
            }
        }, 10000); // 10 seconds

        return () => clearInterval(intervalId);
    }, [token, loadCustomerOrders, searchParams]);

    // Poll Shiprocket tracking details (Company 37 Only)
    useEffect(() => {
        if (isOrdersModalOpen && customerOrders.length > 0 && companyInfo && parseInt(companyInfo.id) === 37) {
            const deliveryOrders = customerOrders.filter(o => o.is_offline_delivery);
            deliveryOrders.forEach(o => {
                fetchOrderTracking(o.id);
            });
            
            const interval = setInterval(() => {
                deliveryOrders.forEach(o => {
                    if (o.order_status !== 'completed' && o.order_status !== 'cancelled') {
                        fetchOrderTracking(o.id);
                    }
                });
            }, 20000);
            
            return () => clearInterval(interval);
        }
    }, [isOrdersModalOpen, customerOrders, companyInfo, simulateTracking]); // eslint-disable-line react-hooks/exhaustive-deps

    // 1. Load data explicitly when token/user is ready OR guest context is available
    useEffect(() => {
        const hasGuestContext = searchParams.get('table') && searchParams.get('companyId');
        if (token || hasGuestContext) {
            console.log(`[CustomerPage] ${token ? 'Token' : 'Guest Context'} available, loading data...`);
            loadTables();
            loadMenu();
            loadCategories();
            loadTableAvailabilityOrders();
            if (token) loadCustomerOrders();

            // Retry after 1 second if menu is still empty (handles new signup edge case)
            const retryTimer = setTimeout(() => {
                if (menuItems.length === 0) {
                    console.log('[CustomerPage] Retrying data load (menu still empty)...');
                    loadTables();
                    loadMenu();
                    loadCategories();
                }
            }, 1000);

            return () => clearTimeout(retryTimer);
        }
    }, [token, loadTables, loadMenu, loadCategories, loadCustomerOrders, loadTableAvailabilityOrders, menuItems.length, searchParams]);

    // 2. Handle URL Table Param (Run only when URL params change)
    useEffect(() => {
        const tableFromUrl = searchParams.get('table');
        if (tableFromUrl) {
            const tNum = parseInt(tableFromUrl);
            if (!isNaN(tNum)) {
                setTableNumber(tNum);
                setIsTableSelectionModalOpen(false);
            }
        } else {
            // If no table in URL, and no table selected yet, open modal
            // Skip table selection for admin users
            if (!tableNumber) {
                setIsTableSelectionModalOpen(true);
            }
        }
    }, [searchParams, tableNumber, currentUser]);

    // Click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.language-dropdown-container') && !event.target.closest('.table-dropdown-container')) {
                setShowLanguageDropdown(false);
                setShowTableDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 3. Reload data when branch changes (important for QR code scans)
    useEffect(() => {
        const hasGuestContext = searchParams.get('table') && searchParams.get('companyId');
        if (token || hasGuestContext) {
            console.log('[CustomerPage] Context changed - Reloading data for branch:', selectedBranch || 'All');
            loadTables();
            loadMenu();
            loadCategories();
            loadTableAvailabilityOrders();
        }
    }, [selectedBranch, token, loadTables, loadMenu, loadCategories, loadTableAvailabilityOrders, searchParams]);

    // 4. Load Orders (Run when token changes)


    const scannedTableObj = tables.find(t => String(t.table_number) === String(tableNumber));
    const isTableInactive = tables.length > 0 && tableNumber && tableNumber !== 'waiting' && scannedTableObj && (scannedTableObj.is_active === 0 || scannedTableObj.is_active === false);

    if (!companyInfo) {
        return (
            <div className="min-h-screen bg-emerald-50 flex items-center justify-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-emerald-700 font-medium animate-pulse">Loading experience...</p>
                </div>
            </div>
        );
    }

    if (qrAccess.status === 'checking') {
        return (
            <div className="min-h-screen bg-emerald-50 flex items-center justify-center p-6 text-center">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-emerald-700 font-medium">Checking QR location...</p>
                </div>
            </div>
        );
    }

    if (qrAccess.status === 'blocked') {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-orange-50 flex items-center justify-center p-6 text-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-red-100 flex flex-col items-center gap-4">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                        <i className="fas fa-location-crosshairs text-red-600 text-3xl"></i>
                    </div>
                    <h1 className="text-2xl font-black text-red-700">QR Location Restricted</h1>
                    <p className="text-gray-600 font-medium leading-relaxed">{qrAccess.message}</p>
                    <button onClick={() => window.location.reload()} className="px-5 py-2.5 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 transition">Try Again</button>
                </div>
            </div>
        );
    }

    if (isTableInactive) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-orange-50 flex flex-col items-center justify-center p-6 text-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full border border-red-100 flex flex-col items-center gap-4">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center animate-bounce">
                        <i className="fas fa-chair text-red-600 text-3xl"></i>
                    </div>
                    <h1 className="text-2xl font-black text-red-700">Table Not Available</h1>
                    <p className="text-gray-600 font-medium leading-relaxed">
                        This table has been deactivated or is currently not available for ordering. 
                        Please contact the restaurant staff or choose another table.
                    </p>
                    {currentUser?.role === 'admin' && (
                        <p className="text-xs text-red-400">
                            (Admin Note: You can activate Table #{tableNumber} in the Admin Panel  Tables perspective)
                        </p>
                    )}
                </div>
            </div>
        );
    }

    if (companyInfo && parseInt(companyInfo.id) === 38 && currentUser?.role === 'admin') {
        return (
            <Customers
                companyInfo={companyInfo}
                currentUser={currentUser}
                API_URL={API_URL}
                getAuthHeaders={getAuthHeaders}
            />
        );
    }

    if (companyInfo && parseInt(companyInfo.id) === 38 && !customerSession) {
        return (
            <CustomerLoginSignup
                companyId={companyInfo.id}
                companyName={companyInfo.name || 'DineFlow'}
                onLoginSuccess={(customer) => {
                    setCustomerSession(customer);
                    localStorage.setItem('dineflow_customer_session', JSON.stringify(customer));
                    showToast('Logged in successfully!', 'success');
                }}
                API_URL={API_URL}
            />
        );
    }

    return (
        <div className="digital-emerald-theme min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 fade-in pb-20" style={{ borderBottom: 'none', boxShadow: 'none' }}>
            {/* Top Greeting Banner for all companies with customer session */}
            {customerSession && parseInt(companyInfo?.id) !== 38 && (
                <div className="bg-gradient-to-r from-teal-700 to-emerald-800 text-white py-2 px-4 shadow-md flex justify-between items-center text-xs sm:text-sm">
                    <div className="flex items-center gap-2">
                        <i className="fas fa-user-circle text-base text-emerald-300"></i>
                        <span className="font-semibold">Hello, {customerSession.username}!</span>
                        {customerSession.phone && (
                            <span className="text-emerald-300 font-mono text-[10px]">📞 {customerSession.phone}</span>
                        )}
                    </div>
                </div>
            )}
            {/* Top Greeting Banner for Company 38 Custom Customer session (with points) */}
            {parseInt(companyInfo?.id) === 38 && customerSession && (
                <div className="bg-gradient-to-r from-teal-700 to-emerald-800 text-white py-2.5 px-4 shadow-md flex justify-between items-center text-xs sm:text-sm">
                    <div className="flex items-center gap-2">
                        <button 
                            onClick={() => setIsProfileModalOpen(true)}
                            className="flex items-center gap-1.5 hover:underline focus:outline-none font-bold text-emerald-300"
                        >
                            <i className="fas fa-user-circle text-base"></i>
                            <span>Hello, {customerSession.username}! (View Profile)</span>
                        </button>
                    </div>
                    <div className="flex items-center gap-3">
                        <span className="bg-emerald-600/50 px-2.5 py-1 rounded-full font-bold border border-emerald-400/30 flex items-center gap-1">
                            <i className="fas fa-award text-amber-300"></i>
                            <span>{customerSession.points} Points</span>
                        </span>
                    </div>
                </div>
            )}

            <div className="top-cart">
                <div className={`text-white p-3 sm:p-4 transition-all duration-500 ${!companyInfo?.banner_url ? 'bg-gradient-to-r from-teal-600 via-cyan-600 to-teal-600' : ''}`}
                    style={companyInfo?.banner_url ? {
                        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${companyInfo.banner_url})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                    } : {}}
                >
                    <div className="container mx-auto px-4">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center min-w-0"> {/* min-w-0 allows shrinking */}
                                {companyInfo?.logo_url ? (
                                    <img src={companyInfo.logo_url} alt="Logo" className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover mr-2 sm:mr-3 border-2 border-white flex-shrink-0" />
                                ) : (
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0">
                                        <i className="fas fa-utensils text-emerald-600 text-sm sm:text-base"></i>
                                    </div>
                                )}
                                <div className="truncate">
                                    <h1 className="text-base sm:text-2xl font-bold truncate">{companyInfo ? companyInfo.name : 'Restaurant'}</h1>
                                    <p className="text-emerald-100 text-xs flex items-center truncate">
                                        {(tableNumber === 0 || tableNumber === '0') ? (
                                             <>
                                                 <i className="fas fa-chair mr-1"></i>
                                                 <span className="font-semibold text-sm sm:text-base">
                                                     {parseInt(companyInfo?.id) === 37 ? 'Delivery' : 'No Table'}
                                                 </span>
                                             </>
                                        ) : currentUser?.role === 'admin' && tableNumber === 0 ? (
                                            <>
                                                <i className="fas fa-user-shield mr-1"></i>
                                                <span className="font-semibold text-sm sm:text-base">Admin View</span>
                                            </>
                                        ) : tableNumber === 'waiting' ? (
                                            <>
                                                <i className="fas fa-clock mr-1"></i>
                                                <span className="font-semibold text-sm sm:text-base">{t('waiting') || 'Waiting List'}</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-chair mr-1"></i> {t('table')} #<span className="font-semibold ml-0.5 text-sm sm:text-base">{tableNumber}</span>
                                            </>
                                        )}
                                    </p>
                                </div>
                            </div>

                            {/* Central Welcome Message */}
                            <div className="hidden lg:flex flex-1 justify-center px-4">
                                <span className="text-base font-black text-emerald-900 bg-white/95 px-5 py-2 rounded-full border border-white/20 shadow-lg whitespace-nowrap">
                                    Welcome to {companyInfo ? companyInfo.name : 'our Restaurant'}
                                </span>
                            </div>

                            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-2">
                                {/* Language Dropdown */}
                                <div className="relative language-dropdown-container">
                                    <button
                                        onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
                                        className="bg-white/95 backdrop-blur-sm rounded-xl h-10 sm:h-11 px-3 sm:px-4 flex items-center gap-2 text-xs sm:text-sm transition-all hover:bg-white border-2 border-white/20 shadow-lg hover:shadow-xl"
                                    >
                                        <i className="fas fa-globe text-lg text-emerald-600"></i>
                                        <span className="hidden sm:inline font-black text-gray-800">{language.toUpperCase()}</span>
                                        <i className={`fas fa-chevron-down text-xs text-gray-600 transition-transform duration-300 ${showLanguageDropdown ? 'rotate-180' : ''}`}></i>
                                    </button>
                                    {showLanguageDropdown && (
                                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-2xl border-2 border-gray-100 py-2 z-[100] animate-fade-in">
                                            <div className="px-4 py-2 border-b-2 border-gray-100 mb-1 flex justify-between items-center">
                                                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{t('selectLanguage') || 'Select Language'}</p>
                                                <span className="text-[8px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-black uppercase tracking-wider">Coming Soon</span>
                                            </div>
                                            {SUPPORTED_LANGUAGES.map((lang) => (
                                                <button
                                                    key={lang.code}
                                                    onClick={() => {
                                                        if (language === lang.code) {
                                                            setShowLanguageDropdown(false);
                                                        } else {
                                                            // Show a toast or just do nothing for coming soon
                                                            showToast('This language is coming soon!', 'info');
                                                        }
                                                    }}
                                                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${language === lang.code ? 'text-emerald-600 font-black bg-emerald-50 cursor-pointer' : 'text-gray-400 font-bold opacity-70 cursor-not-allowed hover:bg-gray-50'}`}
                                                >
                                                    <span className="truncate">{lang.label}</span>
                                                    {language === lang.code && <i className="fas fa-check-circle text-emerald-600 text-sm"></i>}
                                                    {language !== lang.code && <i className="fas fa-clock text-amber-500 text-[10px]"></i>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Currency Display (Read-only) */}
                                <div className="hidden xs:block">
                                    <div className="bg-white/95 backdrop-blur-sm text-gray-800 border-2 border-white/20 rounded-xl h-10 sm:h-11 px-3 sm:px-4 text-xs sm:text-sm font-black shadow-lg flex items-center gap-2">
                                        <i className="fas fa-coins text-emerald-600"></i>
                                        <span>{currentCurrency === 'INR' ? '₹ INR' : '$ USD'}</span>
                                    </div>
                                </div>

                                {/* My Orders Button */}
                                <button 
                                    onClick={() => { setIsOrdersModalOpen(true); loadCustomerOrders(); }} 
                                    className="bg-white/95 backdrop-blur-sm rounded-xl w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center hover:bg-white transition-all border-2 border-white/20 shadow-lg hover:shadow-xl active:scale-95" 
                                    title="My Orders"
                                >
                                    <i className="fas fa-clipboard-list text-lg text-emerald-600"></i>
                                </button>

                                {/* Cart Button */}
                                <button 
                                    onClick={() => setIsCartModalOpen(true)} 
                                    className="bg-white/95 backdrop-blur-sm rounded-xl w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center hover:bg-white transition-all relative border-2 border-white/20 shadow-lg hover:shadow-xl active:scale-95" 
                                    title="Cart"
                                >
                                    <i className="fas fa-shopping-cart text-lg text-emerald-600"></i>
                                    {cartCount > 0 && (
                                        <span className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center border-2 border-white shadow-lg animate-pulse">
                                            {cartCount}
                                        </span>
                                    )}
                                </button>

                                {/* Logout Button (Company 38 Only) */}
                                {parseInt(companyInfo?.id) === 38 && (
                                    <button 
                                        onClick={() => {
                                            localStorage.removeItem('dineflow_customer_session');
                                            setCustomerSession(null);
                                            setUseRedeem(false);
                                            setPointsToRedeem(0);
                                            showToast('Logged out from loyalty profile', 'info');
                                        }} 
                                        className="bg-white/95 backdrop-blur-sm rounded-xl w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center hover:bg-white transition-all border-2 border-white/20 shadow-lg hover:shadow-xl active:scale-95" 
                                        title="Logout"
                                    >
                                        <i className="fas fa-sign-out-alt text-lg text-red-600"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-4">
                {/* Only show branch selector for admins */}
                {/* Only show BranchSelector for admin users who are NOT QR scanned users */}
                {currentUser?.role === 'admin' && !isQRScannedUser && (
                    <div className="mb-4">
                        <BranchSelector API_URL={API_URL} />
                    </div>
                )}

                {/* Festival Offer Banner */}
                {companyInfo?.festival_name && companyInfo?.festival_discount > 0 && (
                    <div className="bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 text-white p-4 rounded-2xl shadow-xl mb-5 border-2 border-yellow-300 animate-pulse transition-all">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2.5 sm:gap-4">
                                <div className="bg-white/20 backdrop-blur-sm p-2 sm:p-3 rounded-xl flex-shrink-0">
                                    <i className="fas fa-gift text-xl sm:text-2xl"></i>
                                </div>
                                <div className="min-w-0">
                                    <h3 className="font-black text-base sm:text-2xl leading-tight truncate">{companyInfo.festival_name}</h3>
                                    <p className="text-[10px] sm:text-sm font-bold text-white/90 uppercase tracking-wide">Get {companyInfo.festival_discount}% OFF ON FINAL BILL!</p>
                                </div>
                            </div>
                            <div className="bg-white text-orange-600 px-3 py-1.5 rounded-xl font-black shadow-lg flex-shrink-0 flex flex-col items-center justify-center leading-tight">
                                <span className="text-sm sm:text-xl">{companyInfo.festival_discount}% OFF</span>
                                <span className="text-[7px] sm:text-[9px] uppercase opacity-80 whitespace-nowrap">On Final Bill</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- Promo Tabs Toggle --- */}
                {menuItems.length > 0 && (
                    <div className="flex flex-col items-center mb-5">
                        <div className="inline-flex p-1.5 bg-white rounded-2xl border-2 border-gray-200 shadow-xl mb-5">
                            {specialItems.length > 0 && (
                                <button
                                    onClick={() => setPromoTab('special')}
                                    className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all duration-300 flex items-center gap-2 ${promoTab === 'special'
                                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg scale-105'
                                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                                        }`}
                                >
                                    <i className={`fas fa-fire ${promoTab === 'special' ? 'animate-pulse' : ''}`}></i>
                                    <span>{t('todaySpecialItems')}</span>
                                </button>
                            )}
                            <button
                                onClick={() => setPromoTab('top')}
                                className={`px-4 sm:px-6 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-black transition-all duration-300 flex items-center gap-2 ${promoTab === 'top'
                                    ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg scale-105'
                                    : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                                    }`}
                            >
                                <i className="fas fa-award"></i>
                                <span>{t('topRatedItems')}</span>
                            </button>
                        </div>

                        {/* --- Conditional Promo Content --- */}
                        <div className="w-full">
                            {/* Logic: If Special tab is active AND there are specials, show them.
                                Otherwise, show Top Rated items (either because tab is 'top' OR because 'special' is empty) */}
                            {((promoTab === 'special' && specialItems.length > 0)) ? (
                                <div className="fade-in-up">
                                    <div className="relative group">
                                        {/* Scroll Buttons */}
                                        <button
                                            onClick={() => scroll(specialScrollRef, 'left')}
                                            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/50 backdrop-blur-md text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-amber-500 hidden sm:flex border border-white/20 -ml-5"
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                        <button
                                            onClick={() => scroll(specialScrollRef, 'right')}
                                            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/50 backdrop-blur-md text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-amber-500 hidden sm:flex border border-white/20 -mr-5"
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>

                                        <div ref={specialScrollRef} className="flex gap-3 overflow-x-auto pb-5 scrollbar-hide snap-x snap-mandatory px-4">
                                            {specialItems.map(item => (
                                                <div key={item.id} className="min-w-[180px] sm:min-w-[240px] snap-start transform transition-all duration-500 hover:scale-[1.02]">
                                                    <div className="relative h-36 sm:h-40 rounded-2xl overflow-hidden shadow-2xl border-2 border-white/20 group">
                                                        <img
                                                            src={generateFoodImageURL(item.name, item.image_url)}
                                                            alt={item.name}
                                                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                                        />
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

                                                        {/* Badges Row */}
                                                        <div className="absolute top-2.5 left-2.5 right-2.5 flex items-center justify-between gap-2 z-10">
                                                            <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[9px] sm:text-[10px] font-black px-2.5 py-1 rounded-lg uppercase tracking-wider shadow-xl flex items-center border border-amber-300">
                                                                <i className="fas fa-star mr-1.5 text-[8px] sm:text-[9px]"></i> Special
                                                            </span>
                                                            {item.discount_percentage >= 1 && (
                                                                <span className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] sm:text-[10px] font-black px-2.5 py-1 rounded-lg shadow-xl animate-pulse border border-red-300">
                                                                    {Math.floor(item.discount_percentage)}% OFF
                                                                </span>
                                                            )}
                                                        </div>
                                                        {item.rating > 0 && (
                                                            <div className="absolute top-10 left-2.5">
                                                                <span className="bg-white/95 backdrop-blur-sm text-gray-900 text-[10px] font-black px-2 py-1 rounded-lg shadow-xl flex items-center border border-gray-200">
                                                                    <i className="fas fa-star text-amber-500 mr-1.5"></i> {Number(item.rating).toFixed(1)}
                                                                    {item.rating_count > 0 && <span className="text-gray-500 font-medium ml-1">({formatCount(item.rating_count)})</span>}
                                                                </span>
                                                            </div>
                                                        )}

                                                        <div className="absolute bottom-2.5 left-2.5 right-2.5 text-white">
                                                            <h3 className="text-sm sm:text-base font-bold line-clamp-2 mb-1.5 drop-shadow-lg">{String(getLocalizedText(item, 'name', language)).replace(/^[^a-zA-Z\s\u0900-\u097F\u0C00-\u0C7F\u0B80-\u0BFF\u0D00-\u0D7F]+/, '').trim()}</h3>
                                                            <div className="flex flex-wrap items-end justify-between gap-2">
                                                                 <div className="flex-1 min-w-0">
                                                                     {item.discount_percentage >= 1 ? (
                                                                         <div className="flex flex-col">
                                                                             <span className="text-[11px] text-gray-300 line-through leading-none drop-shadow-md">
                                                                                 {formatAmount(parseFloat(parseFloat(item.price) > 0 ? item.price : (item.price_inr || item.price_usd || 0)))}
                                                                             </span>
                                                                             <span className="text-red-400 font-black text-lg sm:text-xl leading-none mt-1 drop-shadow-lg">
                                                                                 {formatAmount(parseFloat(parseFloat(item.price) > 0 ? item.price : (item.price_inr || item.price_usd || 0)) * (1 - item.discount_percentage / 100))}
                                                                             </span>
                                                                         </div>
                                                                     ) : (
                                                                         <span className="text-amber-400 font-black text-lg sm:text-xl leading-none drop-shadow-lg">
                                                                             {formatAmount(parseFloat(parseFloat(item.price) > 0 ? item.price : (item.price_inr || item.price_usd || 0)))}
                                                                         </span>
                                                                     )}
                                                                 </div>
                                                                 {(() => {
                                                                      const cartItem = cart.find(c => c.id === item.id);
                                                                      const cartQty = cartItem ? cartItem.quantity : 0;
                                                                      return cartQty > 0 ? (
                                                                          <div className="flex items-center bg-white text-gray-900 rounded-xl h-8 shadow-2xl overflow-hidden font-black text-xs">
                                                                              <button
                                                                                  onClick={(e) => {
                                                                                      e.stopPropagation();
                                                                                      updateQuantity(item.id, -1);
                                                                                  }}
                                                                                  className="w-7 h-full flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 text-gray-900"
                                                                              >
                                                                                  <i className="fas fa-minus text-[9px]"></i>
                                                                              </button>
                                                                              <span className="px-2 text-center min-w-[18px]">{cartQty}</span>
                                                                              <button
                                                                                  onClick={(e) => {
                                                                                      e.stopPropagation();
                                                                                      updateQuantity(item.id, 1);
                                                                                  }}
                                                                                  className="w-7 h-full flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 text-gray-900"
                                                                              >
                                                                                  <i className="fas fa-plus text-[9px]"></i>
                                                                              </button>
                                                                          </div>
                                                                      ) : (
                                                                          <button
                                                                              onClick={() => addToCart(item.id)}
                                                                              className="bg-white text-gray-900 px-4 py-2 rounded-xl text-[11px] font-black shadow-2xl hover:bg-amber-400 hover:text-white transition-all transform active:scale-95 flex-shrink-0"
                                                                          >
                                                                              <i className="fas fa-plus mr-1"></i>
                                                                              {t('add')}
                                                                          </button>
                                                                      );
                                                                  })()}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            {/* Spacer for scroll transparency */}
                                            <div className="min-w-[40px] h-full"></div>
                                        </div>

                                        {/* Horizontal Hint Scroll */}
                                        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-1.5">
                                            {
                                                specialItems.slice(0, 5).map((_, i) => (
                                                    <div key={i} className={`h-1 rounded-full transition-all duration-300 ${i === 0 ? 'w-4 bg-amber-500' : 'w-1 bg-gray-300'}`}></div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                </div>
                            ) : (promoTab === 'top' || (promoTab === 'special' && specialItems.length === 0)) && topRatedItems.length > 0 ? (
                                <div className="fade-in-up">
                                    <div className="relative group">
                                        {/* Scroll Buttons */}
                                        <button
                                            onClick={() => scroll(topRatedScrollRef, 'left')}
                                            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/50 backdrop-blur-md text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-emerald-600 hidden sm:flex border border-white/20 -ml-5"
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                        <button
                                            onClick={() => scroll(topRatedScrollRef, 'right')}
                                            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-black/50 backdrop-blur-md text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-emerald-600 hidden sm:flex border border-white/20 -mr-5"
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>

                                        <div ref={topRatedScrollRef} className="flex gap-3 overflow-x-auto pt-3 pb-5 scrollbar-hide snap-x snap-mandatory px-4">
                                            {topRatedItems.map((item, index) => (
                                                <div key={item.id} className="min-w-[130px] sm:min-w-[160px] snap-start relative group bg-white rounded-2xl p-2.5 border-2 border-gray-100 shadow-xl hover:shadow-2xl transition-all flex flex-col transform hover:-translate-y-1">

                                                    <div className="h-24 sm:h-32 rounded-xl overflow-hidden mb-2 border-2 border-gray-100 relative shadow-md">
                                                        <img
                                                            src={generateFoodImageURL(item.name, item.image_url)}
                                                            alt={item.name}
                                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                        />
                                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                                        
                                                        {/* Veg/Non-Veg badge on Top Left */}
                                                        <div className="absolute top-1.5 left-1.5 z-10">
                                                            <span className={`w-3.5 h-3.5 rounded-sm border bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-sm ${
                                                                (item.is_veg === 0 || item.is_veg === false || item.is_veg === '0' || item.is_veg === 'false')
                                                                    ? 'border-rose-600'
                                                                    : 'border-emerald-600'
                                                            }`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full ${
                                                                    (item.is_veg === 0 || item.is_veg === false || item.is_veg === '0' || item.is_veg === 'false')
                                                                        ? 'bg-rose-600'
                                                                        : 'bg-emerald-600'
                                                                }`}></span>
                                                            </span>
                                                        </div>

                                                        {item.discount_percentage >= 1 && (
                                                            <div className="absolute top-1.5 right-1.5">
                                                                <span className="bg-gradient-to-r from-red-500 to-red-600 text-white text-[9px] font-black px-2 py-0.5 rounded-lg shadow-lg animate-pulse border border-red-300">
                                                                    {item.discount_percentage}%
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center justify-between gap-1.5 mb-1.5">
                                                        <h3 className="text-xs sm:text-sm font-semibold text-gray-800 line-clamp-2 flex-1 leading-snug">{String(getLocalizedText(item, 'name', language)).replace(/^[^a-zA-Z\s\u0900-\u097F\u0C00-\u0C7F\u0B80-\u0BFF\u0D00-\u0D7F]+/, '').trim()}</h3>
                                                        <span className={`text-xs sm:text-sm font-black flex-shrink-0 ${item.discount_percentage >= 1 ? 'text-red-600' : 'text-emerald-600'}`}>
                                                            {formatAmount(parseFloat(parseFloat(item.price) > 0 ? item.price : (parseFloat(item.price_inr) > 0 ? item.price_inr : (parseFloat(item.price_usd) > 0 ? item.price_usd : 0))) * (1 - item.discount_percentage / 100))}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-auto pt-2 gap-1 border-t border-gray-100 overflow-hidden">
                                                        {item.rating > 0 && (
                                                        <div className="flex items-center text-[9px] text-white font-black bg-gradient-to-r from-amber-500 to-orange-500 px-1.5 py-0.5 rounded-md shadow-sm min-w-0 shrink">
                                                            <i className="fas fa-star mr-0.5 text-[8px]"></i> {Number(item.rating).toFixed(1)}
                                                        </div>
                                                        )}
                                                        {(() => {
                                                             const cartItem = cart.find(c => c.id === item.id);
                                                             const cartQty = cartItem ? cartItem.quantity : 0;
                                                             return cartQty > 0 ? (
                                                                 <div className="flex items-center bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-md h-6 shadow-md overflow-hidden font-black text-[10px] flex-shrink-0 ml-auto">
                                                                     <button
                                                                         onClick={(e) => {
                                                                             e.stopPropagation();
                                                                             updateQuantity(item.id, -1);
                                                                         }}
                                                                         className="w-5 h-full flex items-center justify-center hover:bg-emerald-700 active:bg-emerald-800 text-white"
                                                                     >
                                                                         <i className="fas fa-minus text-[7px]"></i>
                                                                     </button>
                                                                     <span className="px-1 text-center min-w-[14px] text-[10px]">{cartQty}</span>
                                                                     <button
                                                                         onClick={(e) => {
                                                                             e.stopPropagation();
                                                                             updateQuantity(item.id, 1);
                                                                         }}
                                                                         className="w-5 h-full flex items-center justify-center hover:bg-emerald-700 active:bg-emerald-800 text-white"
                                                                     >
                                                                         <i className="fas fa-plus text-[7px]"></i>
                                                                     </button>
                                                                 </div>
                                                             ) : (
                                                                 <button
                                                                     onClick={() => addToCart(item.id)}
                                                                     className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:from-emerald-700 hover:to-teal-700 px-2 py-1 rounded-md transition-all shadow-md active:scale-95 flex items-center gap-0.5 flex-shrink-0 ml-auto"
                                                                 >
                                                                     <i className="fas fa-plus text-[8px]"></i>
                                                                     <span className="text-[9px] font-black uppercase">Add</span>
                                                                 </button>
                                                             );
                                                         })()}
                                                    </div>
                                                </div>
                                            ))}
                                            {/* Spacer for scroll transparency */}
                                            <div className="min-w-[40px] h-full"></div>
                                        </div>
                                    </div>
                                </div>
                            ) : promoTab === 'top' ? (
                                <div className="text-center py-10 fade-in text-red-600 font-normal">
                                    <i className="fas fa-award text-3xl mb-3 block text-red-200"></i>
                                    {t('noTopRatedItems')}
                                </div>
                            ) : null}
                        </div>
                    </div>
                )}

                {/* Search and Filter Section */}
                <div className="mb-3">
                    <div className="relative mb-3">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <i className="fas fa-search text-gray-400"></i>
                        </div>
                        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={t('search')} className="w-full pl-10 pr-4 py-2 sm:py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white shadow-sm text-sm sm:text-base input-focus" />
                    </div>
                    <div className="mb-3 w-full relative table-dropdown-container">
                        <button
                            onClick={() => setShowTableDropdown(!showTableDropdown)}
                            className="w-full px-3.5 py-2 rounded-xl border border-gray-200 focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 bg-white text-gray-700 font-bold shadow-sm transition-all flex items-center justify-between group hover:border-emerald-300 active:scale-[0.99]"
                        >
                            <div className="flex items-center gap-2.5">
                                <div className="w-6 h-6 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                                    <i className="fas fa-chair text-[10px]"></i>
                                </div>
                                <span className="truncate text-xs">
                                    {tableNumber ? (
                                        tableNumber === 'waiting' ? (t('waiting') || 'Waiting') : (
                                            (() => {
                                                const selectedTable = tables.find(t => t.table_number === tableNumber);
                                                if (selectedTable) {
                                                    return selectedTable.table_name 
                                                        ? `${t('table')} ${selectedTable.table_number}(${selectedTable.table_name})` 
                                                        : `${t('table')} ${selectedTable.table_number}`;
                                                }
                                                return `${t('table')} ${tableNumber}`;
                                            })()
                                        )
                                    ) : t('chooseTable') || 'Choose your table...'}
                                </span>
                            </div>
                            <i className={`fas fa-chevron-down text-gray-400 text-[10px] transition-transform duration-300 ${showTableDropdown ? 'rotate-180' : ''}`}></i>
                        </button>

                        {showTableDropdown && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-white opacity-100 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.2)] border border-gray-200 py-1.5 z-[100] animate-fade-in text-gray-800 max-h-64 overflow-y-auto" style={{ backgroundColor: '#ffffff' }}>
                                <div className="px-3 py-1.5 border-b border-gray-50 mb-1 font-bold text-[9px] text-gray-400 uppercase tracking-widest">
                                    {t('selectYourTable') || 'Select Your Table'}
                                </div>
                                {displayedTableOptions.map((table) => {
                                    const isDisabled = isManagementRole && table.isOccupied;
                                    return (
                                        <button
                                            key={table.id}
                                            onClick={() => {
                                                if (isDisabled) return;
                                                setTableNumber(table.table_number);
                                                setShowTableDropdown(false);
                                            }}
                                            disabled={isDisabled}
                                            className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center gap-3 ${isDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70' : (tableNumber === table.table_number ? 'text-emerald-600 font-black bg-emerald-50 hover:bg-emerald-50' : 'text-gray-700 font-bold hover:bg-emerald-50')}`}
                                        >
                                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${tableNumber === table.table_number ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-200' : isDisabled ? 'bg-gray-200 text-gray-400' : 'bg-gray-100 text-gray-400'}`}>
                                                <i className="fas fa-chair text-[9px]"></i>
                                            </div>
                                            <span className="text-[11px] flex-1">
                                                {table.table_name ? `${t('table')} ${table.table_number}(${table.table_name})` : `${t('table')} ${table.table_number}`}
                                                {currentUser?.role === 'admin' && !selectedBranch && table.branch_name && (
                                                    <span className="text-[9px] text-gray-400 ml-2">({table.branch_name})</span>
                                                )}
                                            </span>
                                            {isDisabled ? (
                                                <span className="ml-auto text-[8px] font-black uppercase tracking-wider text-red-500">Occupied</span>
                                            ) : tableNumber === table.table_number ? (
                                                <i className="fas fa-check-circle ml-auto text-emerald-600 text-[9px]"></i>
                                            ) : null}
                                        </button>
                                    );
                                })}
                                {displayedTableOptions.length === 0 && (
                                    <div className="px-4 py-6 text-center text-red-600 font-normal text-[10px]">
                                        {t('noTablesAvailable') || 'No tables available'}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Dietary / Veg Filter */}
                    <div className="mb-2.5 flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
                        <button
                            onClick={() => setVegFilter('all')}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 flex-shrink-0 shadow-sm border ${
                                vegFilter === 'all'
                                    ? 'bg-gray-900 text-white border-gray-900 shadow-gray-300 ring-2 ring-gray-400/30'
                                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <span>{t('all') || 'All'}</span>
                        </button>
                        <button
                            onClick={() => setVegFilter(vegFilter === 'veg' ? 'all' : 'veg')}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 flex-shrink-0 shadow-sm border ${
                                vegFilter === 'veg'
                                    ? 'bg-emerald-600 text-white border-emerald-600 shadow-emerald-200 ring-2 ring-emerald-400'
                                    : 'bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50'
                            }`}
                        >
                            <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center p-0.5 ${vegFilter === 'veg' ? 'border-white bg-white' : 'border-emerald-600 bg-white'}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                            </span>
                            <span>{t('vegOnly') || 'Veg'}</span>
                        </button>
                        <button
                            onClick={() => setVegFilter(vegFilter === 'non-veg' ? 'all' : 'non-veg')}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 flex-shrink-0 shadow-sm border ${
                                vegFilter === 'non-veg'
                                    ? 'bg-rose-600 text-white border-rose-600 shadow-rose-200 ring-2 ring-rose-400'
                                    : 'bg-white text-rose-700 border-rose-200 hover:bg-rose-50'
                            }`}
                        >
                            <span className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center p-0.5 ${vegFilter === 'non-veg' ? 'border-white bg-white' : 'border-rose-600 bg-white'}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-rose-600"></span>
                            </span>
                            <span>{t('nonVegOnly') || 'Non-Veg'}</span>
                        </button>
                    </div>

                    {/* Categories */}
                    <div className="mb-2">
                        <div className="flex gap-2 overflow-x-auto pb-2 px-1 -mx-1 scrollbar-hide">
                            <button
                                onClick={() => setActiveCategory('all')}
                                className={`category-pill px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-medium whitespace-nowrap flex items-center text-xs sm:text-sm flex-shrink-0 ${activeCategory === 'all' ? 'active text-white' : 'bg-gray-200 text-gray-700'}`}
                            >
                                <i className="fas fa-border-all mr-1 sm:mr-2"></i><span>{t('allCategories')}</span>
                            </button>
                            {categories.map(cat => (
                                <button
                                    key={cat.id || cat.name}
                                    onClick={() => setActiveCategory(cat.name)}
                                    className={`category-pill px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-medium whitespace-nowrap flex items-center text-xs sm:text-sm flex-shrink-0 ${activeCategory === cat.name ? 'active text-white' : 'bg-gray-200 text-gray-700'}`}
                                >
                                    <i className="fas fa-utensils mr-1 sm:mr-2"></i><span>{getLocalizedText(cat, 'name', language)}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Vitamin Filters */}
                    <div className="mb-3">
                        <div className="flex gap-2 overflow-x-auto pb-2 px-1 -mx-1 scrollbar-hide">
                            <button
                                onClick={() => setActiveVitamin('all')}
                                className={`px-3 py-1 rounded-full text-xs font-medium border ${activeVitamin === 'all' ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-white text-gray-600 border-gray-200'}`}
                            >
                                All Vitamins
                            </button>
                            {uniqueVitamins.map(vit => (
                                <button
                                    key={vit}
                                    onClick={() => setActiveVitamin(vit)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium border ${activeVitamin === vit ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-white text-gray-600 border-gray-200'}`}
                                >
                                    {vit}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Menu Items */}
                {isLoading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mb-6">
                        {Array(10).fill().map((_, i) => (
                            <div key={i} className="bg-white rounded-2xl shadow-md overflow-hidden">
                                <div className="h-32 shimmer"></div>
                                <div className="p-3"><div className="h-3 bg-gray-200 rounded w-3/4 mb-2 shimmer"></div><div className="h-2 bg-gray-200 rounded w-full mb-1 shimmer"></div><div className="h-6 bg-gray-200 rounded w-full shimmer"></div></div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mb-6">
                        {filteredMenu.length === 0 ? (
                            <div className="col-span-full bg-white rounded-2xl p-8 text-center shadow-lg border-2 border-gray-100">
                                <i className="fas fa-search text-red-200 text-4xl mb-4"></i>
                                <h3 className="text-lg sm:text-xl font-normal text-red-600 mb-2">{t('noItemsFound')}</h3>
                                <p className="text-red-400 text-xs sm:text-sm font-normal">Try adjusting your search or filter criteria</p>
                            </div>
                        ) : (
                            filteredMenu.map(item => (
                                <MenuItemCard
                                    key={item.id}
                                    item={item}
                                    currentCurrency={currentCurrency}
                                    t={t}
                                    addToCart={addToCart}
                                    updateQuantity={updateQuantity}
                                    cart={cart}
                                    language={language}
                                    categories={categories}
                                />
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* Floating Cart Button (Mobile) */}
            <button 
                onClick={() => setIsCartModalOpen(true)} 
                className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-full shadow-2xl flex items-center justify-center z-40 transition-all active:scale-95 sm:hidden"
            >
                <i className="fas fa-shopping-cart text-xl"></i>
                {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-black rounded-full w-5 h-5 flex items-center justify-center border-2 border-white shadow-lg animate-pulse">
                        {cartCount}
                    </span>
                )}
            </button>

            {/* Cart Modal */}
            {
                isCartModalOpen && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center animate-fade-in">
                        <div className="bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl shadow-2xl max-h-[90vh] flex flex-col transform transition-all">
                            {/* Header */}
                            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-5 sm:p-6 flex justify-between items-center z-10 sm:rounded-t-3xl rounded-t-3xl shadow-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                                        <i className="fas fa-shopping-bag text-2xl"></i>
                                    </div>
                                    <div>
                                        <h2 className="text-xl sm:text-2xl font-black">{t('yourCart')}</h2>
                                        <p className="text-xs text-white/80 font-medium">{cart.length} {cart.length === 1 ? 'item' : 'items'}</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setIsCartModalOpen(false)} 
                                    className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all active:scale-95"
                                >
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>

                            {/* Cart Items */}
                            <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 bg-gray-50">
                                {cart.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-3">
                                            <i className="fas fa-shopping-cart text-3xl text-red-200"></i>
                                        </div>
                                        <h3 className="text-lg font-normal text-red-600 mb-1">{t('cartEmpty')}</h3>
                                        <p className="text-red-400 text-xs font-normal">Add some delicious items to get started!</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Items List */}
                                        {cart.map(item => {
                                            const price = currentCurrency === 'INR' ? item.price_inr : item.price_usd;
                                            const symbol = currentCurrency === 'INR' ? '₹' : '$';
                                            const discount = item.discount_percentage || 0;
                                            const discountedPrice = price * (1 - discount / 100);

                                            return (
                                                <div key={item.id} className="bg-white rounded-xl p-3 border border-gray-200 hover:border-emerald-300 transition-all shadow-sm hover:shadow-md">
                                                    <div className="flex gap-3">
                                                        {/* Item Image */}
                                                        <div className="relative flex-shrink-0">
                                                            <img 
                                                                src={generateFoodImageURL(item.name, item.image_url)} 
                                                                alt={getLocalizedText(item, 'name', language)} 
                                                                className="w-16 h-16 rounded-lg object-cover shadow-sm border border-gray-100" 
                                                            />
                                                            {discount > 0 && (
                                                                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full shadow-md">
                                                                    {discount}%
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Item Details */}
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex justify-between items-start mb-1">
                                                                <div className="flex-1 min-w-0 pr-2">
                                                                    <h4 className="font-black text-gray-900 text-sm truncate">{String(getLocalizedText(item, 'name', language)).replace(/^[^a-zA-Z\s\u0900-\u097F\u0C00-\u0C7F\u0B80-\u0BFF\u0D00-\u0D7F]+/, '').trim()}</h4>
                                                                    {!!item.is_self_service && (
                                                                        <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase mt-0.5">
                                                                            <i className="fas fa-walking"></i> Self
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <button 
                                                                    onClick={() => removeFromCart(item.id)} 
                                                                    className="w-7 h-7 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center justify-center transition-all active:scale-95 flex-shrink-0"
                                                                >
                                                                    <i className="fas fa-trash-alt text-xs"></i>
                                                                </button>
                                                            </div>

                                                            {/* Price */}
                                                            <div className="flex items-center gap-1.5 mb-2">
                                                                {discount > 0 ? (
                                                                    <>
                                                                        <span className="text-[10px] text-gray-400 line-through font-medium">{symbol}{parseFloat(price).toFixed(0)}</span>
                                                                        <span className="text-sm font-black text-red-600">{symbol}{discountedPrice.toFixed(0)}</span>
                                                                    </>
                                                                ) : (
                                                                    <span className="text-sm font-black text-emerald-600">{symbol}{parseFloat(price).toFixed(0)}</span>
                                                                )}
                                                                <span className="text-[9px] text-gray-500">each</span>
                                                            </div>

                                                            {/* Quantity Controls & Total */}
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-0.5 border border-gray-200">
                                                                    <button 
                                                                        onClick={() => updateQuantity(item.id, -1)} 
                                                                        className="w-7 h-7 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center transition-all active:scale-95 border border-gray-200"
                                                                    >
                                                                        <i className="fas fa-minus text-[10px] text-gray-700"></i>
                                                                    </button>
                                                                    <span className="font-black text-gray-900 w-6 text-center text-sm">{item.quantity}</span>
                                                                    <button 
                                                                        onClick={() => updateQuantity(item.id, 1)} 
                                                                        className="w-7 h-7 rounded-md bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white flex items-center justify-center transition-all active:scale-95 shadow-sm"
                                                                    >
                                                                        <i className="fas fa-plus text-[10px]"></i>
                                                                    </button>
                                                                </div>
                                                                <div className="text-right">
                                                                    <span className="font-black text-base text-emerald-600">{symbol}{(discountedPrice * item.quantity).toFixed(0)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* Scrollable Checkout Form */}
                                        <div className="mt-6 border-t border-gray-200 pt-4 space-y-4">
                                            {/* Payment Method - Compact */}
                                            <div className="mb-3">
                                                <label className="block text-xs font-black text-gray-700 mb-2 flex items-center">
                                                    <i className="fas fa-wallet mr-1.5 text-emerald-600 text-sm"></i>
                                                    {t('paymentMethod')}
                                                </label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <label className="relative cursor-pointer group">
                                                        <input 
                                                            type="radio" 
                                                            name="payment-method" 
                                                            value="cash" 
                                                            className="peer sr-only" 
                                                            checked={paymentMethod === 'cash'} 
                                                            onChange={() => setPaymentMethod('cash')} 
                                                        />
                                                        <div className="border-2 border-gray-200 peer-checked:border-emerald-600 peer-checked:bg-emerald-50 rounded-lg p-2 transition-all hover:border-emerald-400">
                                                            <div className="text-center">
                                                                <div className="w-8 h-8 mx-auto mb-1 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg flex items-center justify-center">
                                                                    <i className="fas fa-money-bill-wave text-base text-emerald-600"></i>
                                                                </div>
                                                                <p className="text-xs font-black text-gray-900">Cash</p>
                                                                <p className="text-[9px] text-gray-500 font-medium">Collect on Delivery</p>
                                                            </div>
                                                        </div>
                                                        <div className="absolute top-1 right-1 w-4 h-4 bg-emerald-600 rounded-full items-center justify-center hidden peer-checked:flex">
                                                            <i className="fas fa-check text-white text-[8px]"></i>
                                                        </div>
                                                    </label>
                                                    <label className="relative cursor-pointer group">
                                                        <input 
                                                            type="radio" 
                                                            name="payment-method" 
                                                            value="online" 
                                                            className="peer sr-only" 
                                                            checked={paymentMethod === 'online'} 
                                                            onChange={() => setPaymentMethod('online')} 
                                                        />
                                                        <div className="border-2 border-gray-200 peer-checked:border-emerald-600 peer-checked:bg-emerald-50 rounded-lg p-2 transition-all hover:border-emerald-400">
                                                            <div className="text-center">
                                                                <div className="w-8 h-8 mx-auto mb-1 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center">
                                                                    <i className="fas fa-mobile-alt text-base text-blue-600"></i>
                                                                </div>
                                                                <p className="text-xs font-black text-gray-900">Online</p>
                                                                <p className="text-[9px] text-gray-500 font-medium">Digital UPI</p>
                                                            </div>
                                                        </div>
                                                        <div className="absolute top-1 right-1 w-4 h-4 bg-emerald-600 rounded-full items-center justify-center hidden peer-checked:flex">
                                                            <i className="fas fa-check text-white text-[8px]"></i>
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>

                                            {/* Online Payment Details (UPI UTR Entry) */}
                                            {paymentMethod === 'online' && companyInfo?.upi_id && (
                                                <div className="mb-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3.5 space-y-3 animate-fade-in text-xs">
                                                    <div className="flex items-center gap-2 text-indigo-950 font-black">
                                                        <i className="fas fa-qrcode text-indigo-600 text-sm"></i> Pay via UPI App
                                                    </div>
                                                    <p className="text-[11px] text-gray-600 font-medium leading-relaxed">
                                                        Please make payment of <span className="font-bold text-indigo-900">{formatAmount(cartTotalInr)}</span> to the merchant UPI ID: 
                                                        <span className="font-mono bg-white px-2 py-0.5 rounded border border-indigo-100 font-black ml-1 select-all">{companyInfo.upi_id}</span>
                                                    </p>
                                                    
                                                    <div className="flex gap-2">
                                                        <a 
                                                            href={`upi://pay?pa=${companyInfo.upi_id}&pn=${encodeURIComponent(companyInfo.name)}&am=${parseFloat(cartTotalInr || 0).toFixed(2)}&cu=INR`} 
                                                            onClick={() => setHasClickedPaymentLink(true)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex-1 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800 py-1.5 rounded-lg text-center font-black transition flex items-center justify-center gap-1 shadow-sm text-[10px]"
                                                        >
                                                            <i className="fas fa-mobile-alt text-purple-600"></i> PhonePe
                                                        </a>
                                                        <a 
                                                            href={`upi://pay?pa=${companyInfo.upi_id}&pn=${encodeURIComponent(companyInfo.name)}&am=${parseFloat(cartTotalInr || 0).toFixed(2)}&cu=INR`} 
                                                            onClick={() => setHasClickedPaymentLink(true)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex-1 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800 py-1.5 rounded-lg text-center font-black transition flex items-center justify-center gap-1 shadow-sm text-[10px]"
                                                        >
                                                            <i className="fab fa-google text-blue-500"></i> GPay
                                                        </a>
                                                        <a 
                                                            href={`upi://pay?pa=${companyInfo.upi_id}&pn=${encodeURIComponent(companyInfo.name)}&am=${parseFloat(cartTotalInr || 0).toFixed(2)}&cu=INR`} 
                                                            onClick={() => setHasClickedPaymentLink(true)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex-1 bg-white hover:bg-gray-50 border border-gray-200 text-gray-800 py-1.5 rounded-lg text-center font-black transition flex items-center justify-center gap-1 shadow-sm text-[10px]"
                                                        >
                                                            <i className="fas fa-wallet text-sky-500"></i> Paytm
                                                        </a>
                                                    </div>

                                                    {hasClickedPaymentLink && (
                                                        <div className="border-t border-indigo-100 pt-2.5 space-y-1 animate-fade-in">
                                                            <label className="block text-[10px] font-black text-indigo-950 uppercase tracking-wide">
                                                                UPI Ref No / UTR ID (12 Digits) *
                                                            </label>
                                                            <input 
                                                                type="text" 
                                                                maxLength="12"
                                                                value={utrNumber}
                                                                onChange={(e) => setUtrNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                                                                placeholder="Enter 12-digit UTR number"
                                                                className="w-full px-3 py-2 border border-indigo-200 rounded-lg text-xs font-mono focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                                            />
                                                            <p className="text-[9px] text-gray-400 font-medium">
                                                                Order will be processed only after UTR verification.
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Offline Delivery Option for Company 37 */}
                                            {companyInfo && parseInt(companyInfo.id) === 37 && (
                                                <div className="mb-4 bg-white p-3 rounded-xl border border-gray-200 shadow-sm space-y-3">
                                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={isOfflineDelivery}
                                                            disabled={tableNumber === 0}
                                                            onChange={(e) => {
                                                                setIsOfflineDelivery(e.target.checked);
                                                                if (e.target.checked) {
                                                                    setDeliveryName(currentUser?.full_name || customerSession?.username || '');
                                                                    setDeliveryPhone(customerSession?.phone || '');
                                                                }
                                                            }}
                                                            className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500 disabled:opacity-50"
                                                        />
                                                        <span className="text-sm font-bold text-gray-800 flex items-center gap-1">
                                                            <i className="fas fa-truck text-emerald-600"></i> Order as Offline Delivery
                                                        </span>
                                                    </label>
                                                    
                                                    {isOfflineDelivery && (
                                                        <div className="pt-2 border-t border-gray-100 space-y-3 animate-fade-in">
                                                            {/* ── Saved Address Cards (Swiggy/Zomato style) ── */}
                                                            {savedAddresses.length > 0 && (
                                                                <div>
                                                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-2">Saved Addresses</p>
                                                                    <div className="space-y-2">
                                                                        {savedAddresses.map(addr => (
                                                                            <button
                                                                                key={addr.id}
                                                                                type="button"
                                                                                onClick={() => handleSelectSavedAddress(String(addr.id))}
                                                                                className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                                                                                    selectedAddressId === String(addr.id)
                                                                                        ? 'border-emerald-500 bg-emerald-50'
                                                                                        : 'border-gray-200 bg-white hover:border-emerald-300'
                                                                                }`}
                                                                            >
                                                                                <div className="flex items-start gap-2">
                                                                                    <div className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                                                                                        selectedAddressId === String(addr.id) ? 'border-emerald-500' : 'border-gray-300'
                                                                                    }`}>
                                                                                        {selectedAddressId === String(addr.id) && <div className="w-2 h-2 rounded-full bg-emerald-500"/>}
                                                                                    </div>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <div className="flex items-center gap-1.5 mb-0.5">
                                                                                            <span className="text-[10px] font-black uppercase text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">{addr.address_label || 'Home'}</span>
                                                                                            {addr.name && <span className="text-xs font-bold text-gray-800">{addr.name}</span>}
                                                                                            {addr.phone && <span className="text-xs text-gray-500 font-mono">{addr.phone}</span>}
                                                                                        </div>
                                                                                        <p className="text-xs text-gray-600 leading-snug truncate">{addr.address}</p>
                                                                                        <p className="text-[10px] text-gray-400">{addr.city}, {addr.state} - {addr.pincode}</p>
                                                                                    </div>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={async (e) => {
                                                                                            e.stopPropagation();
                                                                                            if (!customerSession?.id) return;
                                                                                            try {
                                                                                                await fetch(`${API_URL}/api/customer/addresses/${addr.id}?customer_id=${customerSession.id}`, { method: 'DELETE' });
                                                                                                fetchSavedAddresses();
                                                                                                if (selectedAddressId === String(addr.id)) {
                                                                                                    setSelectedAddressId('new');
                                                                                                    setDeliveryAddress(''); setDeliveryCity(''); setDeliveryState(''); setDeliveryPincode('');
                                                                                                }
                                                                                            } catch(err) { console.error(err); }
                                                                                        }}
                                                                                        className="text-gray-300 hover:text-red-400 transition-colors flex-shrink-0 p-1"
                                                                                    >
                                                                                        <i className="fas fa-trash-alt text-xs"/>
                                                                                    </button>
                                                                                </div>
                                                                            </button>
                                                                        ))}

                                                                        {/* Add New Address card */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleSelectSavedAddress('new')}
                                                                            className={`w-full text-left p-3 rounded-xl border-2 border-dashed transition-all ${
                                                                                selectedAddressId === 'new'
                                                                                    ? 'border-emerald-500 bg-emerald-50'
                                                                                    : 'border-gray-200 hover:border-emerald-300'
                                                                            }`}
                                                                        >
                                                                            <div className="flex items-center gap-2 text-emerald-600">
                                                                                <i className="fas fa-plus-circle text-sm"/>
                                                                                <span className="text-xs font-bold">Add New Address</span>
                                                                            </div>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* ── New Address Form (shown when 'Add New' is selected or no saved addresses) ── */}
                                                            {selectedAddressId === 'new' && (
                                                                <div className="space-y-3 animate-fade-in">
                                                                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider">
                                                                        {savedAddresses.length === 0 ? 'Delivery Details' : 'New Address'}
                                                                    </p>

                                                                    {/* Name */}
                                                                    <div>
                                                                        <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Receiver Name *</label>
                                                                        <input
                                                                            type="text"
                                                                            value={deliveryName}
                                                                            onChange={(e) => setDeliveryName(e.target.value)}
                                                                            placeholder="Full name"
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                                                        />
                                                                    </div>

                                                                    {/* Phone */}
                                                                    <div>
                                                                        <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Mobile Number *</label>
                                                                        <input
                                                                            type="tel"
                                                                            value={deliveryPhone}
                                                                            onChange={(e) => setDeliveryPhone(e.target.value.replace(/\D/g,'').slice(0,10))}
                                                                            placeholder="10-digit mobile number"
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                                                        />
                                                                    </div>

                                                                    {/* Location buttons */}
                                                                    <div className="flex gap-2">
                                                                        <button type="button" onClick={handleDetectLocation}
                                                                            className="flex-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 py-2 px-3 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 border border-emerald-200">
                                                                            <i className="fas fa-location-arrow"/> Detect Location
                                                                        </button>
                                                                        <button type="button" onClick={() => { setIsMapModalOpen(true); initLeafletMap(); }}
                                                                            className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 px-3 rounded-lg text-xs font-bold transition flex items-center justify-center gap-1.5 border border-blue-200">
                                                                            <i className="fas fa-map-marked-alt"/> Adjust on Map
                                                                        </button>
                                                                    </div>

                                                                    {/* Address textarea */}
                                                                    <div>
                                                                        <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">Delivery Address *</label>
                                                                        <textarea
                                                                            value={deliveryAddress}
                                                                            onChange={(e) => setDeliveryAddress(e.target.value)}
                                                                            placeholder="Flat/House no, building, street address"
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm h-16 resize-none focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                                                        />
                                                                    </div>

                                                                    {/* City / State / Pincode */}
                                                                    <div className="grid grid-cols-3 gap-2">
                                                                        <div>
                                                                            <label className="block text-[9px] font-black text-gray-500 uppercase mb-1">City *</label>
                                                                            <input type="text" value={deliveryCity} onChange={(e) => setDeliveryCity(e.target.value)}
                                                                                placeholder="City" className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"/>
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-[9px] font-black text-gray-500 uppercase mb-1">State *</label>
                                                                            <input type="text" value={deliveryState} onChange={(e) => setDeliveryState(e.target.value)}
                                                                                placeholder="State" className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"/>
                                                                        </div>
                                                                        <div>
                                                                            <label className="block text-[9px] font-black text-gray-500 uppercase mb-1">Pincode *</label>
                                                                            <input type="text" value={deliveryPincode} onChange={(e) => setDeliveryPincode(e.target.value.replace(/\D/g,'').slice(0,6))}
                                                                                placeholder="Pincode" className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-emerald-500 focus:outline-none"/>
                                                                        </div>
                                                                    </div>

                                                                    {/* Save to profile */}
                                                                    {customerSession?.id && (
                                                                        <div className="space-y-2 pt-2 border-t border-gray-100">
                                                                            <label className="flex items-center gap-2 cursor-pointer select-none">
                                                                                <input type="checkbox" checked={saveAddressToProfile}
                                                                                    onChange={(e) => setSaveAddressToProfile(e.target.checked)}
                                                                                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"/>
                                                                                <span className="text-xs font-bold text-gray-700">Save address to profile</span>
                                                                            </label>
                                                                            {saveAddressToProfile && (
                                                                                <div>
                                                                                    <label className="block text-[9px] font-black text-gray-500 uppercase mb-1">Label (e.g. Home, Office)</label>
                                                                                    <input type="text" value={addressLabel} onChange={(e) => setAddressLabel(e.target.value)}
                                                                                        placeholder="Home, Office, etc."
                                                                                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-emerald-500 focus:outline-none"/>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Mobile Number for Company 36 */}
                                            {companyInfo && parseInt(companyInfo.id) === 36 && (
                                                <div className="mb-4 bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                                                    <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1">
                                                        Mobile Number (For Ready Notification)
                                                    </label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <i className="fas fa-phone text-gray-400 text-xs"></i>
                                                        </div>
                                                        <input 
                                                            type="tel"
                                                            value={customerPhone}
                                                            onChange={(e) => setCustomerPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                                            placeholder="Enter 10-digit mobile number"
                                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono text-sm text-gray-900"
                                                            required
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* Footer - Payment & Checkout */}
                            {cart.length > 0 && (
                                <div className="border-t-2 border-gray-200 p-4 bg-white sticky bottom-0 z-10 shadow-lg">
                                    {/* Total */}
                                    <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-3 mb-3 border-2 border-emerald-200">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm font-black text-gray-800">{t('total')}:</span>
                                            <span className="text-2xl font-black text-emerald-600">{currentCurrency === 'INR' ? '₹' : '$'}{cartTotal.toFixed(0)}</span>
                                        </div>
                                    </div>

                                    {/* Place Order Button */}
                                    {tableNumber === 'waiting' && parseInt(companyInfo?.id) !== 36 && customerOrders.some(o => o.is_waiting && o.order_status !== 'completed' && o.order_status !== 'cancelled') ? (
                                        <div className="w-full bg-orange-100 text-orange-800 p-4 rounded-xl text-center text-sm font-bold border border-orange-200">
                                            <i className="fas fa-exclamation-triangle mr-2"></i>
                                            You already have an active order in the waiting queue. Please wait until a table is assigned to order more items.
                                        </div>
                                    ) : (
                                        <button 
                                            onClick={placeOrder} 
                                            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black py-3.5 px-6 rounded-xl transition-all duration-300 flex items-center justify-center relative group shadow-lg hover:shadow-xl active:scale-98"
                                        >
                                            <div className="flex items-center gap-2">
                                                <i className="fas fa-check-circle text-lg"></i>
                                                <span className="text-base">{t('placeOrder')}</span>
                                                {cart.some(item => item.is_self_service) && (
                                                    <i className="fas fa-info-circle text-white/80 text-xs"></i>
                                                )}
                                            </div>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )
            }

            {/* Orders Modal */}
            {
                isOrdersModalOpen && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center animate-fade-in">
                        <div className="bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl shadow-2xl max-h-[90vh] flex flex-col transform transition-all">
                            {/* Header */}
                            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-5 sm:p-6 flex justify-between items-center z-10 sm:rounded-t-3xl rounded-t-3xl shadow-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                                        <i className="fas fa-receipt text-2xl"></i>
                                    </div>
                                    <div>
                                        <h2 className="text-xl sm:text-2xl font-black">{t('yourOrders')}</h2>
                                        <p className="text-xs text-white/80 font-medium">{customerOrders.length} {customerOrders.length === 1 ? 'order' : 'orders'}</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setIsOrdersModalOpen(false)} 
                                    className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all active:scale-95"
                                >
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>

                            {/* Orders List */}
                            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
                                {customerOrders.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16 text-center">
                                        <div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mb-4">
                                            <i className="fas fa-receipt text-4xl text-red-200"></i>
                                        </div>
                                        <h3 className="text-xl font-normal text-red-600 mb-2">{t('noOrders')}</h3>
                                        <p className="text-red-400 text-sm font-normal">Your order history will appear here</p>
                                    </div>
                                ) : (() => {
                                    const sortedOrders = [...customerOrders].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                                    const sessionCurrency = sortedOrders[0]?.currency || 'INR';
                                    const sessionSymbol = sessionCurrency === 'INR' ? '₹' : '$';
                                    const activeSessionOrders = sortedOrders.filter(o => o.order_status !== 'cancelled' && o.session_checkout_at === null && o.payment_status !== 'paid');
                                    const sessionTotal = activeSessionOrders.reduce((sum, o) => sum + parseFloat(o[`total_amount_${sessionCurrency.toLowerCase()}`] || 0), 0);
                                    return (
                                        <>
                                            {activeSessionOrders.length > 1 && !companyInfo?.no_tables_mode && (
                                                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-2 border-purple-200 rounded-xl p-3 mb-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                                                                <i className="fas fa-layer-group text-white text-xs"></i>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs font-black text-purple-900">Table {tableNumber} — Your Session</p>
                                                                <p className="text-[10px] text-purple-700 font-medium">{activeSessionOrders.length} rounds ordered</p>
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="text-[9px] text-purple-500 font-bold uppercase">Combined Total</p>
                                                            <p className="text-base font-black text-purple-700">{sessionSymbol}{sessionTotal.toFixed(0)}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {sortedOrders.map((order, roundIndex) => {
                                    const statusMap = {
                                        pending: 1,
                                        preparing: 2,
                                        ready: 3,
                                        delivered: 4
                                    };
                                    const currentStep = statusMap[order.order_status] || 0;
                                    const createdTime = new Date(order.created_at).toLocaleString();
                                    const symbol = order.currency === 'INR' ? '₹' : '$';
                                    const amount = order[`total_amount_${order.currency.toLowerCase()}`];
                                    
                                    return (
                                        <div key={order.id} className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-100 rounded-xl p-3 shadow-md hover:shadow-lg transition-all">
                                            {sortedOrders.length > 1 && !companyInfo?.no_tables_mode && (
                                                <div className="flex items-center gap-2 mb-2">
                                                    <span className="bg-purple-600 text-white text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide">Round {roundIndex + 1}</span>
                                                    <span className="text-[9px] text-gray-400 font-medium">{new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                            )}
                                            {/* Order Header */}
                                            <div className="flex justify-between items-center mb-2">
                                                <div>
                                                    <h4 className="font-black text-gray-900 text-base">
                                                        {companyInfo?.no_tables_mode
                                                            ? `Order #${order.order_number || order.id}`
                                                            : (companyInfo && parseInt(companyInfo.id) === 36 ? `Order #${order.waiting_number || order.id}` : 'Order Summary')
                                                        }
                                                    </h4>
                                                    <p className="text-[10px] text-gray-500 font-medium mt-0.5">{createdTime}</p>
                                                </div>
                                                {!!order.table_number && String(order.table_number) !== '0' && String(order.table_number) !== 'Waiting' && (
                                                    <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-2.5 py-1 border border-emerald-200 rounded-lg shadow-sm">
                                                        Your Table : {order.table_number}
                                                    </span>
                                                )}
                                                <span className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide shadow-sm ${
                                                    order.order_status === 'cancelled' ? 'bg-red-600 text-white' :
                                                    (order.is_waiting && order.order_status !== 'ready' && order.order_status !== 'completed' && order.order_status !== 'delivered') ? 'bg-orange-600 text-white' :
                                                    order.order_status === 'pending' ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-yellow-900' :
                                                    order.order_status === 'preparing' ? 'bg-gradient-to-r from-emerald-400 to-teal-400 text-emerald-900' :
                                                    order.order_status === 'ready' ? 'bg-gradient-to-r from-purple-400 to-pink-400 text-purple-900' :
                                                    'bg-gradient-to-r from-green-400 to-emerald-400 text-green-900'
                                                }`}>
                                                    {order.order_status === 'cancelled' ? 'CANCELLED' : (order.is_waiting && order.order_status !== 'ready' && order.order_status !== 'completed' && order.order_status !== 'delivered') ? `${t('waiting') || 'Waiting'} #${order.waiting_number}` : order.order_status}
                                                </span>
                                            </div>
                                            {!!order.is_waiting && order.order_status !== 'cancelled' && parseInt(companyInfo?.id) !== 36 && (
                                                <div className="mb-3 p-3 bg-orange-50 border border-orange-100 rounded-xl flex items-center gap-3">
                                                    <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center text-white shrink-0">
                                                        <i className="fas fa-hourglass-half"></i>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-black text-orange-900 uppercase tracking-tight">You are in queue</p>
                                                        <p className="text-[10px] text-orange-700 font-medium">Wait for manager to assign your table. Position: <span className="font-black">#{order.waiting_number}</span></p>
                                                    </div>
                                                </div>
                                            )}
                                            {(!order.is_waiting && !!order.was_assigned_from_waiting && !!order.table_number && String(order.table_number) !== '0' && String(order.table_number) !== 'Waiting') && (
                                                <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3 shadow-sm animate-fade-in">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white shrink-0 shadow-md">
                                                        <i className="fas fa-chair"></i>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-black text-emerald-900 uppercase tracking-tight">🎉 Table Assigned: {order.table_number}</p>
                                                        <p className="text-[10px] text-emerald-700 font-medium">Please be seated and enjoy your dining experience!</p>
                                                    </div>
                                                </div>
                                            )}
                                            {/* Order Items */}
                                            <div className="mb-2 space-y-1.5">
                                                {order.items.map((item, index) => (
                                                    <div key={index} className="flex justify-between items-center bg-white p-2 rounded-lg border border-gray-200 shadow-sm">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-black text-white bg-gradient-to-br from-purple-600 to-indigo-600 w-6 h-6 flex items-center justify-center rounded-lg shadow-sm text-xs">
                                                                {item.quantity}
                                                            </span>
                                                            <span className={`text-xs font-bold ${item.item_status === 'cancelled' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                                                                {String(getLocalizedText(menuItems.find(mi => mi.id === item.menu_item_id) || { name: item.item_name }, 'name', language)).replace(/^[^a-zA-Z\s\u0900-\u097F\u0C00-\u0C7F\u0B80-\u0BFF\u0D00-\u0D7F]+/, '').trim()}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1.5">
                                                            {item.item_status === 'cancelled' ? (
                                                                <span className="text-red-600 text-[9px] font-black border border-red-200 bg-red-50 px-1.5 py-0.5 rounded">CANCELLED</span>
                                                            ) : (
                                                                ['pending', 'preparing'].includes(order.order_status) && (
                                                                    <button
                                                                        onClick={() => openCancellationModal('item', order.id, item.id)}
                                                                        className="w-6 h-6 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center justify-center transition-all active:scale-95"
                                                                        title="Cancel Item"
                                                                    >
                                                                        <i className="fas fa-times text-xs"></i>
                                                                    </button>
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Total Amount */}
                                            <div className="flex justify-between items-center bg-gradient-to-r from-purple-50 to-indigo-50 p-2 rounded-lg border border-purple-200 mb-2">
                                                <span className="text-xs font-black text-gray-800">Total Amount</span>
                                                <span className="text-base font-black text-purple-600">{symbol}{parseFloat(amount).toFixed(0)}</span>
                                            </div>

                                            {/* Cancel Order Button */}
                                            {['pending', 'preparing'].includes(order.order_status) && (
                                                <button
                                                    onClick={() => openCancellationModal('order', order.id)}
                                                    className="w-full mb-2 py-2 rounded-lg text-xs font-black text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 transition-all flex items-center justify-center gap-1.5 active:scale-98"
                                                >
                                                    <i className="fas fa-ban text-xs"></i> Cancel Full Order
                                                </button>
                                            )}

                                            {/* Order Timeline */}
                                            {order.order_status === 'cancelled' ? (
                                                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center my-2">
                                                    <div className="flex items-center justify-center gap-2 text-red-600 mb-1">
                                                        <i className="fas fa-times-circle text-sm"></i>
                                                        <span className="text-xs font-black uppercase tracking-wider">Order Cancelled</span>
                                                    </div>
                                                    <p className="text-[10px] text-red-700 font-medium">This order was cancelled on {new Date(order.updated_at || order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.</p>
                                                </div>
                                            ) : companyInfo && (parseInt(companyInfo.id) === 36 || companyInfo.no_tables_mode) ? (
                                                <div className="relative pt-2 pb-2 px-3 bg-gray-50 rounded-xl p-3 my-2 border border-gray-100">
                                                    <div className="absolute top-6 left-12 right-12 h-1 bg-gray-200 rounded-full"></div>
                                                    <div
                                                        className="absolute top-6 left-12 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-700 ease-out"
                                                        style={{ width: ['ready', 'completed', 'delivered'].includes(order.order_status) ? 'calc(100% - 6rem)' : '0%' }}
                                                    ></div>

                                                    <div className="relative flex justify-between px-4">
                                                        {/* Step 1: Preparing */}
                                                        <div className="flex flex-col items-center">
                                                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs z-10 transition-all duration-300 shadow-md ${!['ready', 'completed', 'delivered'].includes(order.order_status) ? 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white scale-105 animate-pulse' : 'bg-emerald-600 text-white'}`}>
                                                                <i className={!['ready', 'completed', 'delivered'].includes(order.order_status) ? 'fas fa-fire-alt' : 'fas fa-check'}></i>
                                                            </div>
                                                            <span className="text-[10px] font-black mt-1 text-gray-800">Preparing</span>
                                                            <span className="text-[8px] text-gray-500 font-mono">
                                                                {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>

                                                        {/* Step 2: Ready */}
                                                        <div className="flex flex-col items-center">
                                                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs z-10 transition-all duration-300 shadow-md ${['ready', 'completed', 'delivered'].includes(order.order_status) ? 'bg-gradient-to-br from-purple-500 to-pink-600 text-white scale-105' : 'bg-gray-200 text-gray-400'}`}>
                                                                <i className="fas fa-bell"></i>
                                                            </div>
                                                            <span className="text-[10px] font-black mt-1 text-gray-800">Ready for Pickup</span>
                                                            {['ready', 'completed', 'delivered'].includes(order.order_status) && (
                                                                <span className="text-[8px] text-emerald-600 font-black">
                                                                    {getElapsedTime(order.updated_at)} ago
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="relative pt-1.5 pb-2 px-1 bg-gray-50 rounded-lg p-2.5">
                                                    <div className="absolute top-5 left-6 right-6 h-0.5 bg-gray-200 rounded-full"></div>
                                                    <div
                                                        className="absolute top-5 left-6 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-1000 ease-out"
                                                        style={{ width: `calc(${(currentStep / 4) * 100}% - 1.5rem)` }}
                                                    ></div>

                                                    <div className="relative flex justify-between">
                                                        {/* Step 1: Ordered */}
                                                        <div className="flex flex-col items-center">
                                                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] z-10 transition-all duration-300 shadow-md ${currentStep >= 1 ? 'bg-gradient-to-br from-purple-600 to-indigo-600 text-white scale-105' : 'bg-gray-200 text-gray-400'}`}>
                                                                <i className="fas fa-clipboard-check"></i>
                                                            </div>
                                                            <span className="text-[9px] font-black mt-1 text-gray-700">Ordered</span>
                                                            <span className="text-[8px] text-gray-500 font-mono">
                                                                {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </span>
                                                        </div>

                                                        {/* Step 2: Preparing */}
                                                        <div className="flex flex-col items-center">
                                                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] z-10 transition-all duration-300 shadow-md ${currentStep >= 2 ? 'bg-gradient-to-br from-emerald-600 to-teal-600 text-white scale-105' : 'bg-gray-200 text-gray-400'}`}>
                                                                <i className="fas fa-fire-alt"></i>
                                                            </div>
                                                            <span className="text-[9px] font-black mt-1 text-gray-700">Cooking</span>
                                                            {currentStep === 2 && (
                                                                <span className="text-[8px] text-emerald-600 font-black animate-pulse">
                                                                    {getElapsedTime(order.created_at)}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Step 3: Ready */}
                                                        <div className="flex flex-col items-center">
                                                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] z-10 transition-all duration-300 shadow-md ${currentStep >= 3 ? 'bg-gradient-to-br from-pink-600 to-rose-600 text-white scale-105' : 'bg-gray-200 text-gray-400'}`}>
                                                                <i className="fas fa-bell"></i>
                                                            </div>
                                                            <span className="text-[9px] font-black mt-1 text-gray-700">Ready</span>
                                                            {currentStep === 3 && (
                                                                <span className="text-[8px] text-pink-600 font-black">
                                                                    {getElapsedTime(order.updated_at)} ago
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Step 4: Delivered */}
                                                        <div className="flex flex-col items-center">
                                                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] z-10 transition-all duration-300 shadow-md ${currentStep >= 4 ? 'bg-gradient-to-br from-green-600 to-emerald-600 text-white scale-105' : 'bg-gray-200 text-gray-400'}`}>
                                                                <i className="fas fa-check-double"></i>
                                                            </div>
                                                            <span className="text-[9px] font-black mt-1 text-gray-700">Enjoy</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Footer Actions */}
                                            <div className="mt-2 space-y-2">
                                                {order.order_status === 'delivered' && (
                                                    <div className="text-center bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-2">
                                                        <span className="text-[10px] text-gray-600 uppercase font-black tracking-wide">Total Wait Time</span>
                                                        <p className="text-sm font-black text-gray-900 mt-0.5">
                                                            {getTotalWaitTime(order.created_at, order.updated_at)}
                                                        </p>
                                                    </div>
                                                )}

                                                {/* Payment Section */}
                                                {/* Removed individual Pay Bill button to consolidate into main View Bill & Pay button */}

                                                {order.payment_status === 'paid' && (
                                                    <div className="mt-2 bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
                                                        <div className="flex items-center justify-center gap-2 text-emerald-600">
                                                            <i className="fas fa-check-circle"></i>
                                                            <span className="text-xs font-black uppercase tracking-wider">Payment Completed</span>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Shiprocket Delivery Tracking Info */}
                                                {!!order.is_offline_delivery && companyInfo && parseInt(companyInfo.id) === 37 && (
                                                    <div className="mt-2 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl p-3 space-y-2 text-xs">
                                                        <div className="flex justify-between items-center">
                                                            <span className="font-bold text-indigo-950 flex items-center gap-1.5">
                                                                <i className="fas fa-shipping-fast text-indigo-600 animate-bounce"></i> Delivery Status
                                                            </span>
                                                            <span className="bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wide">
                                                                {orderTracking[order.id]?.status || 'Order Placed'}
                                                            </span>
                                                        </div>
                                                        
                                                        <div className="flex justify-between items-center pt-1 border-t border-indigo-100/30">
                                                            <label className="flex items-center gap-1.5 cursor-pointer text-indigo-700 font-bold select-none text-[10px]">
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={!!simulateTracking[order.id]} 
                                                                    onChange={(e) => {
                                                                        const val = e.target.checked;
                                                                        setSimulateTracking(prev => ({ ...prev, [order.id]: val }));
                                                                        fetchOrderTracking(order.id, val);
                                                                    }}
                                                                    className="w-3.5 h-3.5 text-indigo-600 border-indigo-300 rounded focus:ring-indigo-500"
                                                                />
                                                                <span>Simulate / Demo Tracking</span>
                                                            </label>
                                                        </div>
                                                        
                                                        {orderTracking[order.id] && (
                                                            <div className="space-y-1.5 text-gray-700 border-t border-indigo-100/50 pt-2 mt-2">
                                                                {orderTracking[order.id].edd && (
                                                                    <p className="text-[10px] font-medium">
                                                                        <span className="font-black text-gray-500">ETA:</span> {orderTracking[order.id].edd}
                                                                    </p>
                                                                )}
                                                                {orderTracking[order.id].agent_name && (
                                                                    <p className="text-[10px] font-medium">
                                                                        <span className="font-black text-gray-500">Rider:</span> {orderTracking[order.id].agent_name} 
                                                                        {orderTracking[order.id].agent_phone && (
                                                                            <span className="font-mono text-gray-400 ml-1">({orderTracking[order.id].agent_phone})</span>
                                                                        )}
                                                                    </p>
                                                                )}
                                                                
                                                                {orderTracking[order.id].activities && orderTracking[order.id].activities.length > 0 && (
                                                                    <div className="mt-2.5 pt-2 border-t border-indigo-100/50 space-y-2">
                                                                        <p className="text-[9px] font-black text-indigo-800 uppercase tracking-wider mb-1.5">Tracking Updates</p>
                                                                        <div className="relative pl-3 border-l border-indigo-200 space-y-2 text-[10px] text-gray-600 font-medium">
                                                                            {orderTracking[order.id].activities.map((act, index) => (
                                                                                <div key={index} className="relative">
                                                                                    <div className={`absolute -left-[16.5px] top-1 w-2.5 h-2.5 rounded-full border-2 border-white ${index === 0 ? 'bg-indigo-600 ring-2 ring-indigo-100' : 'bg-gray-300'}`}></div>
                                                                                    <p className={`font-bold ${index === 0 ? 'text-indigo-950 font-black' : 'text-gray-700'}`}>{act.activity}</p>
                                                                                    {act.location && <p className="text-[9px] text-gray-400 font-semibold">{act.location}</p>}
                                                                                    <p className="text-[8px] text-gray-400 font-mono mt-0.5">
                                                                                        {new Date(act.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                                                                    </p>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                                
                                                                {(orderTracking[order.id]?.track_url || order.shiprocket_order_id) && (
                                                                    <a 
                                                                        href={orderTracking[order.id]?.track_url || `https://shiprocket.co/tracking/${order.shiprocket_order_id}`} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer" 
                                                                        className="mt-2 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-3 rounded-lg text-center block transition-all text-[10px] shadow-sm hover:shadow active:scale-95"
                                                                    >
                                                                        <i className="fas fa-map-marked-alt mr-1"></i> Track Live on Shiprocket
                                                                    </a>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}



                                                {/* Feedback and Reorder Buttons */}
                                                <div className="grid grid-cols-2 gap-2">
                                                    {order.order_status === 'delivered' && !order.has_feedback && (
                                                        <button 
                                                            onClick={() => setFeedbackModal({ show: true, orderId: order.id })} 
                                                            className="col-span-1 bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-yellow-900 py-2 rounded-lg text-xs font-black transition-all shadow-md active:scale-95 flex items-center justify-center gap-1.5"
                                                        >
                                                            <i className="fas fa-star text-xs"></i> Rate
                                                        </button>
                                                    )}

                                                    {order.order_status === 'delivered' && (
                                                        <button 
                                                            onClick={() => handleReorder(order)} 
                                                            className={`bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-2 rounded-lg text-xs font-black transition-all shadow-md active:scale-95 flex items-center justify-center gap-1.5 ${order.has_feedback ? 'col-span-2' : 'col-span-1'}`}
                                                        >
                                                            <i className="fas fa-redo text-xs"></i> Reorder
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                        </>
                                    );
                                })()}

                                {companyInfo && parseInt(companyInfo.id) !== 37 && customerOrders.some(o => o.order_status !== 'cancelled' && o.session_checkout_at === null) && (
                                    <div className="pt-4 border-t border-gray-100 mt-4 sticky bottom-0 bg-white pb-2">
                                        <button
                                            onClick={handleViewBill}
                                            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-3 rounded-xl font-bold uppercase tracking-widest shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2"
                                        >
                                            <i className="fas fa-receipt"></i> Pay &amp; Checkout
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }



            {/* Bill Modal */}
            {showBillModal && billData && (() => {
                // Compute Birthday Discount
                let dobDiscount = 0;
                if (isBirthdayToday && parseInt(companyInfo?.id) === 38 && redeemSettings) {
                    const value = parseFloat(redeemSettings.dob_discount_value || 0);
                    if (value > 0) {
                        if (redeemSettings.dob_discount_type === 'percentage') {
                            dobDiscount = billData.subtotal * (value / 100);
                        } else {
                            dobDiscount = value;
                        }
                    }
                }

                // Compute Redeem points
                const pointVal = parseFloat(redeemSettings?.point_value || 1);
                const currentPts = customerSession?.points || 0;
                const maxPointsAllowed = Math.min(currentPts, Math.floor((billData.total - dobDiscount) / pointVal));
                const pointsDiscount = useRedeem ? (maxPointsAllowed * pointVal) : 0;
                const netTotal = Math.max(0, billData.total - dobDiscount - pointsDiscount);

                // Save pointsToRedeem state safely without infinite loops
                if (useRedeem && pointsToRedeem !== maxPointsAllowed) {
                    setTimeout(() => setPointsToRedeem(maxPointsAllowed), 0);
                }

                return (
                    <div className="fixed inset-0 modal-backdrop z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setShowBillModal(false)}>
                        <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl modal-content animate-fade-in-up max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                            <div id="bill-content" className="bg-white">
                                <div className="flex justify-between items-center mb-6 border-b pb-4">
                                    <div>
                                        <h2 className="text-2xl font-black text-gray-800">{t('bill') || 'Bill'}</h2>
                                        <p className="text-gray-500 font-medium text-sm">{(tableNumber === 0 || tableNumber === '0') ? (parseInt(companyInfo?.id) === 37 ? 'Delivery' : 'No Table') : `Table ${tableNumber}`}</p>
                                        {parseInt(companyInfo?.id) === 38 && billData.orders && (() => {
                                            const orderNums = [...new Set(billData.orders.map(o => o.order_number).filter(Boolean))].sort((a, b) => a - b);
                                            if (orderNums.length > 0) {
                                                return <p className="text-gray-900 font-bold text-sm mt-1">Order Number: {orderNums.join(', ')}</p>;
                                            }
                                            return null;
                                        })()}
                                    </div>
                                    <button onClick={() => setShowBillModal(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full w-10 h-10 flex items-center justify-center transition-colors">
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>


                                {/* Itemized List */}
                                {billData.items && billData.items.length > 0 && (
                                    <div className="mb-6">
                                        <h3 className="font-bold text-gray-800 mb-3 text-sm uppercase tracking-wide">Order Items</h3>
                                        <div className="space-y-2">
                                            {billData.items.map((item, idx) => (
                                                <div key={idx} className="bg-white p-3 rounded-lg border border-gray-200">
                                                    <div className="flex justify-between items-start mb-1">
                                                        <span className="font-medium text-gray-800 text-sm flex-1">{String(getLocalizedText(menuItems.find(mi => mi.id === item.menu_item_id) || { name: item.item_name || item.name || '' }, 'name', language)).replace(/^[^a-zA-Z\s\u0900-\u097F\u0C00-\u0C7F\u0B80-\u0BFF\u0D00-\u0D7F]+/, '').trim()}</span>
                                                        <span className="text-xs text-gray-500 ml-2">x{item.quantity}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center text-xs">
                                                        {item.discountPercentage > 0 ? (
                                                            <div className="flex items-center gap-2">
                                                                <span className="line-through text-gray-400">₹{item.basePrice.toFixed(2)}</span>
                                                                <span className="bg-red-100 text-red-700 px-1.5 py-0.5 rounded text-[10px] font-bold">{item.discountPercentage}% OFF</span>
                                                                <span className="text-red-600 font-bold">{formatAmount(item.discountedPrice)}</span>
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-600">{formatAmount(item.basePrice)} each</span>
                                                        )}
                                                        <span className="font-bold text-gray-900">{formatAmount(item.total)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Redeem Points Checkbox/Toggle (Company 38 Only) */}
                                {parseInt(companyInfo?.id) === 38 && customerSession && (
                                    <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex flex-col gap-2">
                                        <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                            <input 
                                                type="checkbox"
                                                disabled={currentPts === 0}
                                                checked={useRedeem}
                                                onChange={(e) => {
                                                    setUseRedeem(e.target.checked);
                                                    if (!e.target.checked) setPointsToRedeem(0);
                                                }}
                                                className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 border-gray-300 disabled:opacity-50"
                                            />
                                            <div className="text-xs">
                                                <p className={`font-black ${currentPts === 0 ? 'text-gray-400' : 'text-emerald-900'}`}>Use Redeem Points</p>
                                                <p className={`text-[10px] ${currentPts === 0 ? 'text-gray-400' : 'text-emerald-700'}`}>You have {currentPts} points (save up to ₹{(maxPointsAllowed * pointVal).toFixed(2)})</p>
                                            </div>
                                        </label>
                                        
                                        {redeemSettings && (
                                            <div className="mt-2 text-[10px] text-emerald-800 font-semibold border-t border-emerald-100 pt-2 flex items-center justify-between">
                                                <span>Estimated points to earn from this order:</span>
                                                <span className="font-bold bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                                                    {redeemSettings.reward_type === 'orders' 
                                                        ? `+${redeemSettings.points_awarded} pts (on crossing ${redeemSettings.orders_threshold} orders)`
                                                        : `+${Math.floor(netTotal / parseFloat(redeemSettings.amount_threshold)) * redeemSettings.points_awarded} pts`
                                                    }
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="bg-gray-50 p-4 rounded-xl space-y-2 mb-6 text-sm">
                                    <div className="flex justify-between text-gray-500">
                                        <span>Subtotal</span>
                                        <span>{formatAmount(billData.subtotal)}</span>
                                    </div>
                                    <div className="flex justify-between text-gray-500">
                                        <span>SGST ({billData.sgst}%)</span>
                                        <span>{formatAmount(billData.sgstAmount)}</span>
                                    </div>
                                    <div className="flex justify-between text-gray-500">
                                        <span>CGST ({billData.cgst}%)</span>
                                        <span>{formatAmount(billData.cgstAmount)}</span>
                                    </div>
                                    {billData.festivalDiscountPercent > 0 && (
                                        <div className="flex justify-between font-bold text-red-600">
                                            <span>{billData.festivalName || 'Festival Discount'} ({billData.festivalDiscountPercent}%)</span>
                                            <span>-{formatAmount(billData.festivalDiscountAmount)}</span>
                                        </div>
                                    )}
                                    {/* Birthday Discount row */}
                                    {dobDiscount > 0 && (
                                        <div className="flex justify-between font-bold text-pink-600 mt-1">
                                            <span><i className="fas fa-birthday-cake mr-1"></i> Birthday Discount</span>
                                            <span>-{formatAmount(dobDiscount)}</span>
                                        </div>
                                    )}
                                    {/* Points discount row */}
                                    {pointsDiscount > 0 && (
                                        <div className="flex justify-between font-bold text-emerald-600">
                                            <span><i className="fas fa-award mr-1"></i> Points Redeemed ({maxPointsAllowed} pts)</span>
                                            <span>-{formatAmount(pointsDiscount)}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between font-black text-lg text-gray-900 border-t border-gray-200 pt-2 mt-2">
                                        <span>Total</span>
                                        <span>{formatAmount(netTotal)}</span>
                                    </div>
                                </div>

                                {/* QR Codes inside PDF */}
                                {billData.qrCodes && billData.qrCodes.length > 0 && (
                                    <div className="mt-6 mb-4 border-t pt-4">
                                        <p className="text-center text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Scan to Pay</p>
                                        <div className="flex justify-center gap-4 flex-wrap">
                                            {(() => {
                                                let codes = billData.qrCodes;
                                                if (typeof codes === 'string') {
                                                    try { codes = JSON.parse(codes); } catch (e) { codes = []; }
                                                }
                                                return Array.isArray(codes) ? codes.map((qr, i) => (
                                                    <div key={i} className="bg-white p-2 rounded-xl border border-gray-100 shadow-sm flex items-center justify-center">
                                                        <img src={qr} alt="Payment QR" className="w-full h-auto max-w-[280px] object-contain" />
                                                    </div>
                                                )) : null;
                                            })()}
                                        </div>
                                    </div>
                                )}

                                <p className="text-center text-[10px] text-gray-400 italic mt-4 mb-2">
                                    {(!t('showBillToCount') || t('showBillToCount') === 'showBillToCount')
                                        ? 'If paying by cash, please present this bill at the counter.'
                                        : t('showBillToCount')}
                                </p>
                            </div>

                            {/* Payment Section (Buttons only) */}
                            <div className="space-y-6 mt-6">
                                <div className="border-t pt-6">
                                    <h3 className="text-center text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Pay via UPI App</h3>
                                    <div className="grid grid-cols-1 gap-2 mb-6">
                                        {billData.upiId ? (() => {
                                            const upiLink = `upi://pay?pa=${encodeURIComponent(billData.upiId)}&pn=${encodeURIComponent(billData.companyName || 'Restaurant')}&am=${netTotal.toFixed(2)}&cu=INR&tn=${encodeURIComponent(`Table ${tableNumber} Bill`)}`;
                                            const upiParams = upiLink.split('?')[1];
                                            const phonepeLink = `phonepe://pay?${upiParams}`;
                                            const paytmLink = `paytmmp://pay?${upiParams}`;
                                            return (
                                                <>
                                                    <a href={upiLink} className="flex items-center justify-center gap-3 bg-[#4285F4] text-white py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all active:scale-95">
                                                        <i className="fas fa-mobile-alt"></i>
                                                        <span>Google Pay / UPI</span>
                                                    </a>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <a href={phonepeLink} className="flex items-center justify-center gap-2 bg-[#5f259f] text-white py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all active:scale-95 text-xs">
                                                            PHONEPE
                                                        </a>
                                                        <a href={paytmLink} className="flex items-center justify-center gap-2 bg-[#00baf2] text-white py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all active:scale-95 text-xs">
                                                            PAYTM
                                                        </a>
                                                    </div>
                                                </>
                                            );
                                        })() : (
                                            <p className="text-center text-xs text-gray-500 py-2 italic">Open Google Pay, PhonePe, or Paytm and scan the QR code above to pay.</p>
                                        )}
                                    </div>
                                </div>

                                <button onClick={handleSaveBill} className="w-full border-2 border-gray-900 text-gray-900 bg-white hover:bg-gray-50 py-3 rounded-xl font-bold uppercase tracking-widest transition-all">
                                    <i className="fas fa-file-download mr-2"></i> {t('saveBill') || 'Save / Download PDF'}
                                </button>

                                {/* Confirm Checkout Button */}
                                {!sessionCheckedOut && currentSessionId && (
                                    <div className="mt-4 border-t-2 border-dashed border-gray-200 pt-4">
                                        <p className="text-center text-[10px] text-gray-400 mb-3 uppercase tracking-widest font-bold">Done with your meal?</p>
                                        <button
                                            onClick={handleCheckoutSession}
                                            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white py-4 rounded-xl font-black uppercase tracking-widest transition-all shadow-lg active:scale-95 flex items-center justify-center gap-3"
                                        >
                                            <i className="fas fa-sign-out-alt text-lg"></i>
                                            <span>Confirm Checkout</span>
                                        </button>
                                        <p className="text-center text-[9px] text-gray-400 mt-2">Tap after payment is complete. Your session will close.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Out of Range Error Modal */}
            {outOfRangeModal.show && (
                <div className="fixed inset-0 modal-backdrop z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md transition-all">
                    <div className="bg-white rounded-3xl max-w-sm w-full p-8 shadow-2xl text-center transform scale-100 animate-fade-in-up flex flex-col items-center">
                        <div className="w-20 h-20 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-5 shadow-inner">
                            <i className="fas fa-map-marker-alt text-4xl animate-bounce"></i>
                        </div>
                        <h3 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">You're Too Far Away</h3>
                        {companyInfo?.name && (
                            <div className="bg-emerald-50 border border-emerald-100 rounded-xl px-4 py-2 mb-4">
                                <p className="text-sm font-bold text-emerald-700">
                                    <i className="fas fa-utensils mr-1.5"></i>
                                    {companyInfo.name}
                                </p>
                            </div>
                        )}
                        <p className="text-gray-600 mb-2 font-medium leading-relaxed text-sm">
                            You need to be physically present at{' '}
                            <span className="font-bold text-gray-800">{companyInfo?.name || 'the restaurant'}</span>{' '}
                            to place an order.
                        </p>
                        {outOfRangeModal.message ? (
                            <p className="text-red-600 font-bold text-xs mb-7 leading-relaxed bg-red-50 p-3 rounded-lg border border-red-100">
                                {outOfRangeModal.message}
                            </p>
                        ) : (
                            <p className="text-gray-400 text-xs mb-7 leading-relaxed">
                                Please visit the restaurant and scan the QR code again when you are nearby.
                            </p>
                        )}
                        <button
                            onClick={() => setOutOfRangeModal({ show: false, message: '' })}
                            className="w-full bg-gray-900 hover:bg-black text-white py-3.5 rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-95"
                        >
                            <i className="fas fa-check mr-2"></i>Got it
                        </button>
                    </div>
                </div>
            )}


            {/* Leaflet Map Picker Modal (Company 37 Only) */}
            {isMapModalOpen && (
                <div className="fixed inset-0 modal-backdrop z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setIsMapModalOpen(false)}>
                    <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl modal-content animate-fade-in-up" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-base font-black text-gray-900 flex items-center gap-1.5">
                                <i className="fas fa-map-marker-alt text-emerald-600"></i> Adjust Delivery Location
                            </h3>
                            <button 
                                onClick={() => setIsMapModalOpen(false)}
                                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <p className="text-xs text-gray-500 mb-4 font-medium">
                            Drag the pin or click on the map to set your exact delivery location.
                        </p>
                        
                        <div className="relative">
                            {isMapLoading && (
                                <div className="absolute inset-0 bg-white/80 backdrop-blur-xs z-10 flex flex-col items-center justify-center rounded-2xl">
                                    <i className="fas fa-spinner animate-spin text-2xl text-emerald-600 mb-2"></i>
                                    <span className="text-xs text-gray-500 font-bold">Loading Map Resources...</span>
                                </div>
                            )}
                            <div 
                                id="leaflet-picker-map" 
                                className="w-full h-72 rounded-2xl border border-gray-200 shadow-inner z-0"
                            ></div>
                        </div>
                        
                        <div className="mt-4 flex gap-3">
                            <button 
                                onClick={() => setIsMapModalOpen(false)}
                                className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black py-2.5 rounded-xl transition-all shadow-md active:scale-95 text-center text-xs"
                            >
                                Confirm Location
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Customer Login/Signup Gate Modal (Company 37 & 38 Only) */}
            {isGateModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/80 backdrop-blur-md">
                    <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 flex flex-col gap-4 animate-fade-in-up">
                        <div className="text-center space-y-2">
                            <div className="w-16 h-16 bg-gradient-to-tr from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center text-white text-2xl mx-auto shadow-lg">
                                <i className="fas fa-user-circle"></i>
                            </div>
                            <h3 className="text-xl font-black text-gray-900">
                                Welcome to {companyInfo?.name || 'Dineflow'}
                            </h3>
                            <p className="text-xs text-gray-500 font-medium">
                                Please create or log in to your profile to view the menu and place orders.
                            </p>
                        </div>
                        
                        {/* Tabs */}
                        <div className="flex bg-gray-100 p-1 rounded-xl">
                            <button
                                onClick={() => setGateTab('signup')}
                                className={`flex-1 py-2 text-xs font-black rounded-lg transition ${gateTab === 'signup' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Register (New Guest)
                            </button>
                            <button
                                onClick={() => setGateTab('login')}
                                className={`flex-1 py-2 text-xs font-black rounded-lg transition ${gateTab === 'login' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                Log In (Returning Guest)
                            </button>
                        </div>
                        
                        <form onSubmit={handleGateSubmit} className="space-y-4">
                            {gateTab === 'signup' ? (
                                <>
                                    <div>
                                        <label className="block text-[10px] font-black text-gray-550 uppercase tracking-wider mb-1">Choose Username *</label>
                                        <div className="relative">
                                            <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-xs">@</span>
                                            <input
                                                type="text"
                                                required
                                                value={gateUsername}
                                                onChange={(e) => setGateUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                                                placeholder="unique_username"
                                                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none font-bold"
                                            />
                                        </div>
                                        <p className="text-[9px] text-gray-400 mt-1 font-medium">Letters, numbers, and underscores only. Must be unique.</p>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-gray-550 uppercase tracking-wider mb-1">Mobile Number *</label>
                                        <input
                                            type="tel"
                                            required
                                            value={gatePhone}
                                            onChange={(e) => setGatePhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                            placeholder="10-digit mobile number"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none font-mono"
                                        />
                                    </div>
                                    {companyInfo && parseInt(companyInfo.id) !== 37 && (
                                        <div>
                                            <label className="block text-[10px] font-black text-gray-550 uppercase tracking-wider mb-1">Date of Birth (Optional)</label>
                                            <input
                                                type="date"
                                                value={gateDob}
                                                onChange={(e) => setGateDob(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none"
                                            />
                                            <p className="text-[9px] text-emerald-600 mt-1 font-medium">🎉 Enter your birthday to get a special discount!</p>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div>
                                    <label className="block text-[10px] font-black text-gray-555 uppercase tracking-wider mb-1">Username or Mobile Number *</label>
                                    <input
                                        type="text"
                                        required
                                        value={gateUsername}
                                        onChange={(e) => setGateUsername(e.target.value)}
                                        placeholder="Enter your username or phone"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none font-medium"
                                    />
                                </div>
                            )}
                            
                            <button
                                type="submit"
                                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black py-2.5 rounded-xl transition-all shadow-md active:scale-95 text-center text-sm"
                            >
                                {gateTab === 'signup' ? 'Create Profile & Enter' : 'Verify & Enter'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Simple Name + Phone Gate Modal (All companies except 38, and only when collect_customer_info is enabled) */}
            {isSimpleGateOpen && companyInfo && parseInt(companyInfo.id) !== 38 && companyInfo.collect_customer_info !== false && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/80 backdrop-blur-md">
                    <div className="bg-white rounded-3xl max-w-sm w-full p-7 shadow-2xl border border-gray-100 flex flex-col gap-5 animate-fade-in-up">
                        {/* Header */}
                        <div className="text-center space-y-2">
                            <div className="w-16 h-16 bg-gradient-to-tr from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center text-white text-2xl mx-auto shadow-lg">
                                <i className="fas fa-utensils"></i>
                            </div>
                            <h3 className="text-xl font-black text-gray-900">
                                Welcome to {companyInfo?.name || 'our Restaurant'}
                            </h3>
                            <p className="text-xs text-gray-500 font-medium leading-relaxed">
                                Enter your name and mobile number to view the menu and place orders. Returning customers will see their order history!
                            </p>
                        </div>

                        {/* Error */}
                        {simpleGateError && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl text-xs font-semibold flex items-center gap-2">
                                <i className="fas fa-exclamation-circle text-sm"></i>
                                <span>{simpleGateError}</span>
                            </div>
                        )}

                        {/* Form */}
                        <form onSubmit={handleSimpleGateSubmit} className="space-y-4">
                            <div>
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1.5">
                                    Your Name *
                                </label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400">
                                        <i className="fas fa-user text-xs"></i>
                                    </span>
                                    <input
                                        type="text"
                                        required
                                        value={simpleGateName}
                                        onChange={(e) => { setSimpleGateName(e.target.value); setSimpleGateError(''); }}
                                        placeholder="e.g. Rajan Kumar"
                                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none font-medium"
                                        autoFocus
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1.5">
                                    Mobile Number *
                                </label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center text-gray-400">
                                        <i className="fas fa-phone text-xs"></i>
                                    </span>
                                    <input
                                        type="tel"
                                        required
                                        inputMode="numeric"
                                        value={simpleGatePhone}
                                        onChange={(e) => { setSimpleGatePhone(e.target.value.replace(/\D/g, '').slice(0, 10)); setSimpleGateError(''); }}
                                        placeholder="10-digit mobile number"
                                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none font-mono"
                                    />
                                </div>
                                <p className="text-[9px] text-emerald-600 mt-1 font-medium">
                                    📋 We use your number to show you your order status and history.
                                </p>
                            </div>

                            <button
                                type="submit"
                                disabled={simpleGateLoading}
                                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black py-3 rounded-xl transition-all shadow-md active:scale-95 text-center text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                            >
                                {simpleGateLoading ? (
                                    <><i className="fas fa-circle-notch animate-spin"></i> Checking...</>
                                ) : (
                                    <><i className="fas fa-arrow-right"></i> View Menu</>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* Client Profile Modal (Company 37 & 38 Only) */}
            {isProfileModalOpen && customerSession && (parseInt(companyInfo?.id) === 38 || parseInt(companyInfo?.id) === 37) && (
                <div className="fixed inset-0 modal-backdrop z-[90] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => { setIsProfileModalOpen(false); setEditingAddressId(null); }}>
                    <div className="bg-white rounded-3xl shadow-2xl animate-fade-in-up max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} style={{ minWidth: '350px', maxWidth: '550px', width: '92%' }}>
                        {/* Header */}
                        <div className="p-6 pb-4 border-b border-gray-100">
                            <div className="flex items-center gap-4 text-left">
                                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                                    <i className="fas fa-user text-xl"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-lg font-black text-gray-900 truncate">{customerSession.username}</h3>
                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                        <i className="fas fa-phone-alt text-emerald-500"/>
                                        {customerSession.phone || 'No phone'}
                                    </p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">{parseInt(companyInfo?.id) === 38 ? 'Loyalty Club Member' : 'Delivery Customer'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-5">

                            {/* Loyalty points for company 38 */}
                            {parseInt(companyInfo?.id) === 38 && (
                                <>
                                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs text-gray-500 mb-0.5">Loyalty Points</p>
                                                <p className="text-2xl font-black text-amber-700">{customerSession.points} <span className="text-sm font-bold">pts</span></p>
                                            </div>
                                            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                                                <i className="fas fa-gem text-amber-500 text-lg"/>
                                            </div>
                                        </div>
                                        {customerSession.dob && (
                                            <p className="text-[10px] text-gray-500 mt-2 border-t border-amber-100 pt-2">
                                                <i className="fas fa-birthday-cake mr-1 text-amber-400"/>
                                                {new Date(customerSession.dob).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
                                            </p>
                                        )}
                                    </div>
                                    <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-2xl text-xs text-emerald-800">
                                        <h4 className="font-bold mb-1.5 flex items-center gap-1"><i className="fas fa-info-circle"/><span>Loyalty Rules</span></h4>
                                        <ul className="space-y-1 list-disc list-inside">
                                            <li>{redeemSettings?.reward_type === 'orders' ? `Earn ${redeemSettings.points_awarded} points for every ${redeemSettings.orders_threshold} completed orders.` : `Earn ${redeemSettings?.points_awarded || 50} points for every ₹${redeemSettings?.amount_threshold || 100} spent.`}</li>
                                            <li>1 Point = ₹{parseFloat(redeemSettings?.point_value || 1).toFixed(2)} discount.</li>
                                            {parseFloat(redeemSettings?.dob_discount_value || 0) > 0 && (<li>Birthday special: {redeemSettings.dob_discount_type === 'percentage' ? `${redeemSettings.dob_discount_value}%` : `₹${redeemSettings.dob_discount_value}`} off!</li>)}
                                        </ul>
                                    </div>
                                </>
                            )}

                            {/* Edit Address Form (when editingAddressId is active) */}
                            {parseInt(companyInfo?.id) === 37 && editingAddressId !== null && (
                                <form onSubmit={handleSaveEditAddress} className="space-y-3.5 text-left animate-fade-in">
                                    <div className="flex items-center justify-between border-b pb-2 mb-2">
                                        <h4 className="text-sm font-black text-gray-800 flex items-center gap-1.5">
                                            <i className="fas fa-edit text-emerald-600"></i>
                                            Edit Address Details
                                        </h4>
                                        <button 
                                            type="button"
                                            onClick={() => setEditingAddressId(null)}
                                            className="text-xs font-bold text-gray-400 hover:text-gray-600 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1">Label (e.g. Home, Office, Work)</label>
                                        <input
                                            type="text"
                                            required
                                            value={editLabel}
                                            onChange={(e) => setEditLabel(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1">Receiver Name</label>
                                        <input
                                            type="text"
                                            value={editName}
                                            onChange={(e) => setEditName(e.target.value)}
                                            placeholder="Who is receiving this order?"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1">Receiver Phone Number</label>
                                        <input
                                            type="tel"
                                            value={editPhone}
                                            onChange={(e) => setEditPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                            placeholder="10-digit number"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1">Delivery Address *</label>
                                        <textarea
                                            required
                                            value={editAddress}
                                            onChange={(e) => setEditAddress(e.target.value)}
                                            placeholder="Flat/House no, Building, Street, Area"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm h-16 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                        />
                                    </div>

                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <label className="block text-[9px] font-black text-gray-500 uppercase mb-1">City *</label>
                                            <input
                                                type="text"
                                                required
                                                value={editCity}
                                                onChange={(e) => setEditCity(e.target.value)}
                                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[9px] font-black text-gray-500 uppercase mb-1">State *</label>
                                            <input
                                                type="text"
                                                required
                                                value={editState}
                                                onChange={(e) => setEditState(e.target.value)}
                                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[9px] font-black text-gray-500 uppercase mb-1">Pincode *</label>
                                            <input
                                                type="text"
                                                required
                                                value={editPincode}
                                                onChange={(e) => setEditPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                                className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-2.5 pt-2">
                                        <button
                                            type="button"
                                            onClick={() => setEditingAddressId(null)}
                                            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2.5 rounded-xl text-xs transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 rounded-xl text-xs transition-all shadow-sm"
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Saved Addresses list — for company 37 */}
                            {parseInt(companyInfo?.id) === 37 && editingAddressId === null && (
                                <div className="text-left">
                                    <div className="flex items-center justify-between mb-3.5">
                                        <h4 className="text-sm font-black text-gray-800 flex items-center gap-1.5">
                                            <i className="fas fa-map-marker-alt text-emerald-500"/>
                                            Saved Addresses
                                        </h4>
                                        <span className="text-[10px] text-gray-400 font-bold bg-gray-100 px-2 py-0.5 rounded-full">{savedAddresses.length}/10</span>
                                    </div>

                                    {savedAddresses.length === 0 ? (
                                        <div className="text-center py-8 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                            <i className="fas fa-map-pin text-gray-300 text-2xl mb-2 block"/>
                                            <p className="text-xs text-gray-400 font-medium">No saved addresses yet</p>
                                            <p className="text-[10px] text-gray-400 mt-1">Save an address while placing your next order.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            {savedAddresses.map(addr => (
                                                <div key={addr.id} className="bg-gray-50 border border-gray-200 rounded-2xl p-4 transition-all hover:border-emerald-200 hover:bg-emerald-50/10">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex-1 min-w-0">
                                                            {/* Label row */}
                                                            <div className="flex items-center flex-wrap gap-1.5 mb-1.5">
                                                                <span className="inline-block text-[10px] font-black uppercase text-emerald-700 bg-emerald-100 px-2.5 py-0.5 rounded-md leading-none">{addr.address_label || 'Home'}</span>
                                                                {addr.name && <span className="text-xs font-bold text-gray-800 truncate">{addr.name}</span>}
                                                            </div>
                                                            {/* Phone */}
                                                            {addr.phone && (
                                                                <div className="flex items-center gap-1 mb-1.5">
                                                                    <i className="fas fa-phone text-gray-400" style={{fontSize:'9px'}}/>
                                                                    <span className="text-xs text-gray-500 font-mono">{addr.phone}</span>
                                                                </div>
                                                            )}
                                                            {/* Address */}
                                                            <p className="text-xs text-gray-600 leading-relaxed break-words">{addr.address}</p>
                                                            <p className="text-[11px] text-gray-400 mt-1 font-medium">{addr.city}, {addr.state} — {addr.pincode}</p>
                                                        </div>
                                                        
                                                        {/* Action Buttons (Edit & Delete) */}
                                                        <div className="flex gap-1 flex-shrink-0">
                                                            <button
                                                                onClick={() => handleStartEditAddress(addr)}
                                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                                                                title="Edit address"
                                                            >
                                                                <i className="fas fa-pencil-alt text-xs"/>
                                                            </button>
                                                            <button
                                                                onClick={async () => {
                                                                    if (!customerSession?.id) return;
                                                                    try {
                                                                        await fetch(`${API_URL}/api/customer/addresses/${addr.id}?customer_id=${customerSession.id}`, { method: 'DELETE' });
                                                                        fetchSavedAddresses();
                                                                    } catch(err) { console.error(err); }
                                                                }}
                                                                className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all"
                                                                title="Delete address"
                                                            >
                                                                <i className="fas fa-trash-alt text-xs"/>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-100">
                            <button
                                onClick={() => { setIsProfileModalOpen(false); setEditingAddressId(null); }}
                                className="w-full bg-gray-900 hover:bg-gray-800 text-white font-bold py-3 rounded-xl transition-all shadow-md"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Checkout Confirmation Modal */}
            {showCheckoutConfirmModal && (
                <div className="fixed inset-0 modal-backdrop z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowCheckoutConfirmModal(false)}>
                    <div className="bg-white rounded-3xl max-w-sm w-full p-6 text-center shadow-2xl modal-content animate-fade-in-up border border-gray-100" onClick={e => e.stopPropagation()}>
                        <div className="w-16 h-16 bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm border border-emerald-100">
                            <i className="fas fa-hand-holding-usd text-2xl"></i>
                        </div>
                        <h3 className="text-xl font-black text-gray-900 mb-2">Thanks for Dining!</h3>
                        <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                            Could you please confirm whether you have paid the bill or not?
                        </p>

                        <div className="flex flex-col gap-2.5">
                            <button
                                onClick={performCheckout}
                                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-black py-3 rounded-xl transition-all shadow-md active:scale-98 flex items-center justify-center gap-2"
                            >
                                <i className="fas fa-check-circle"></i>
                                <span>Yes, I have Paid</span>
                            </button>
                            <button
                                onClick={() => setShowCheckoutConfirmModal(false)}
                                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-3 rounded-xl transition-all active:scale-98"
                            >
                                No, Go Back
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Payment Modal for Company 36 */}
            {pendingPaymentOrder && (
                <div className="fixed inset-0 bg-black/50 modal-backdrop z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="bg-emerald-600 text-white p-4 flex justify-between items-center shrink-0">
                            <h2 className="text-xl font-bold">Complete Payment</h2>
                        </div>
                        <div className="p-6 overflow-y-auto">
                            <p className="text-center text-gray-600 mb-6">
                                Please scan the QR code to pay <strong>{formatAmount(pendingPaymentOrder.total_amount_inr)}</strong> via any UPI app.
                            </p>
                            
                            {(() => {
                                let qrs = companyInfo?.qr_codes;
                                if (typeof qrs === 'string') {
                                    try { qrs = JSON.parse(qrs); } catch(e){}
                                }
                                const upiId = qrs?.upi_id || companyInfo?.upi_id;
                                const phonepeLink = upiId ? `phonepe://pay?pa=${upiId}&pn=${encodeURIComponent(companyInfo.name)}&am=${pendingPaymentOrder.total_amount_inr}&cu=INR` : '';
                                const paytmLink = upiId ? `paytmmp://pay?pa=${upiId}&pn=${encodeURIComponent(companyInfo.name)}&am=${pendingPaymentOrder.total_amount_inr}&cu=INR` : '';
                                const gpayLink = upiId ? `gpay://upi/pay?pa=${upiId}&pn=${encodeURIComponent(companyInfo.name)}&am=${pendingPaymentOrder.total_amount_inr}&cu=INR` : '';

                                return (
                                    <div className="space-y-4">
                                        {companyInfo?.qr_codes?.table_qr && (
                                            <div className="flex justify-center mb-4">
                                                <img src={companyInfo.qr_codes.table_qr} alt="Payment QR" className="w-48 h-48 object-contain rounded-xl border-2 border-emerald-100 p-2" />
                                            </div>
                                        )}
                                        {upiId ? (
                                            <div className="grid grid-cols-3 gap-2">
                                                <a href={phonepeLink} onClick={() => setHasClickedPaymentLink(true)} className="flex items-center justify-center gap-1 bg-[#5f259f] text-white py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all active:scale-95 text-[11px]">
                                                    PHONEPE
                                                </a>
                                                <a href={gpayLink} onClick={() => setHasClickedPaymentLink(true)} className="flex items-center justify-center gap-1 bg-white border border-gray-300 text-gray-800 py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all active:scale-95 text-[11px]">
                                                    <span className="text-blue-600">G</span><span className="text-red-500">P</span><span className="text-yellow-500">a</span><span className="text-green-600">y</span>
                                                </a>
                                                <a href={paytmLink} onClick={() => setHasClickedPaymentLink(true)} className="flex items-center justify-center gap-1 bg-[#00baf2] text-white py-3 rounded-xl font-bold shadow-md hover:shadow-lg transition-all active:scale-95 text-[11px]">
                                                    PAYTM
                                                </a>
                                            </div>
                                        ) : (
                                            <p className="text-center text-xs text-gray-500 py-2 italic">Open Google Pay, PhonePe, or Paytm and scan the QR code above to pay.</p>
                                        )}

                                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 mt-4">
                                            <p className="text-xs font-bold text-blue-900 mb-1">Enter UTR / Reference No</p>
                                            <p className="text-[11px] text-blue-800 font-medium mb-2">After paying, enter the 12-digit UTR/Reference No:</p>
                                            <input 
                                                type="text" 
                                                value={utrNumber} 
                                                onChange={(e) => setUtrNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                                                placeholder="e.g. 312345678901" 
                                                className="w-full p-3 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-center tracking-widest text-lg"
                                            />
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                        <div className="p-4 border-t bg-gray-50 flex gap-3 shrink-0">
                            <button onClick={() => { setPendingPaymentOrder(null); setHasClickedPaymentLink(false); setUtrNumber(''); }} className="flex-1 py-3 bg-white border border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition">Cancel</button>
                            <button onClick={() => handleMarkPaid(pendingPaymentOrder.id)} className={`flex-1 py-3 font-bold rounded-xl transition ${utrNumber.length === 12 ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}>I Have Paid</button>
                        </div>
                    </div>
                </div>
            )}

            {
                showOrderSuccess && (
                    <div className="fixed inset-0 bg-black/50 modal-backdrop z-50 flex items-center justify-center">
                        <div className="bg-white rounded-2xl p-8 max-w-sm mx-4 text-center">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fas fa-check text-green-600 text-2xl"></i>
                            </div>
                            <h3 className="text-xl font-bold mb-2">{t('orderSuccess')}</h3>
                            {placedOrderNumber ? (
                                <div className="bg-emerald-100 text-emerald-800 py-3 px-4 rounded-xl mb-4 border border-emerald-200">
                                    <p className="text-[10px] font-black uppercase tracking-widest mb-1">Order Number</p>
                                    <p className="text-3xl font-black"># {placedOrderNumber}</p>
                                </div>
                            ) : waitingNumber ? (
                                companyInfo && parseInt(companyInfo.id) === 36 ? (
                                    <div className="bg-blue-100 text-blue-800 py-3 px-4 rounded-xl mb-4 border border-blue-200">
                                        <p className="text-[10px] font-black uppercase tracking-widest mb-1">Queue Number</p>
                                        <p className="text-3xl font-black"># {waitingNumber}</p>
                                        <p className="text-[11px] font-bold mt-1">Please collect once your number is ready</p>
                                    </div>
                                ) : (
                                    <div className="bg-orange-100 text-orange-800 py-3 px-4 rounded-xl mb-4 border border-orange-200">
                                        <p className="text-[10px] font-black uppercase tracking-widest mb-1">Queue Position</p>
                                        <p className="text-3xl font-black"># {waitingNumber}</p>
                                        <p className="text-[11px] font-bold mt-1">Please wait for your table assignment</p>
                                    </div>
                                )
                            ) : null}

                            {paymentMethod === 'cash' && isOfflineDelivery && (
                                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3.5 mb-4 text-left">
                                    <div className="flex gap-2 items-center text-indigo-950 font-black text-xs mb-1.5">
                                        <i className="fas fa-truck text-indigo-600"></i> Cash on Delivery
                                    </div>
                                    <p className="text-indigo-900 font-bold text-xs leading-tight">
                                        Please pay the order amount to the delivery partner on delivery.
                                    </p>
                                </div>
                            )}

                            {hasSelfServiceInOrder && (
                                <div className="mb-6 bg-amber-50 border-2 border-amber-200 rounded-xl p-4 animate-pulse">
                                    <div className="flex items-center justify-center gap-2 mb-2 text-amber-600">
                                        <i className="fas fa-hand-holding-hand text-2xl"></i>
                                        <span className="font-black uppercase tracking-widest text-xs">Self-Service</span>
                                    </div>
                                    <p className="text-amber-900 font-bold text-sm leading-tight">
                                        {t('selfServiceNotice') || "This order contains self-service items. Please collect them at the counter."}
                                    </p>
                                </div>
                            )}

                            <p className="text-sm text-gray-500 mb-6">{t('orderPrep')}</p>
                            <button onClick={() => { setShowOrderSuccess(false); setHasSelfServiceInOrder(false); setPlacedOrderNumber(null); }} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-xl transition shadow-lg active:scale-95 uppercase tracking-wider">OK</button>
                        </div>
                    </div>
                )
            }

            {/* Feedback Modal */}
            {
                feedbackModal.show && (
                    <div className="fixed inset-0 bg-black/50 modal-backdrop z-50 flex items-center justify-center">
                        <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 w-full">
                            <h3 className="text-xl font-bold mb-4 text-center">Rate Your Order</h3>
                            <p className="text-gray-600 text-center mb-6">How was your food?</p>
                            {/* ... feedback form ... */}
                            <div className="flex justify-center gap-4 mb-6">
                                {[1, 2, 3, 4, 5].map(star => (
                                    <button key={star} onClick={() => setFeedbackRating(star)} className={`text-3xl ${star <= feedbackRating ? 'text-yellow-400' : 'text-gray-300'}`}>★</button>
                                ))}
                            </div>
                            <textarea
                                value={feedbackComment}
                                onChange={(e) => setFeedbackComment(e.target.value)}
                                placeholder="Any comments? (Optional)"
                                className="w-full border border-gray-300 rounded-lg p-3 mb-4 h-24 resize-none"
                            />
                            <div className="flex gap-3">
                                <button onClick={handleFeedbackSubmit} className="flex-1 bg-emerald-600 text-white py-2 rounded-lg font-medium">Submit</button>
                                <button onClick={() => setFeedbackModal({ show: false, orderId: null, items: [] })} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg font-medium">Skip</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Table Selection Modal */}
            {
                isTableSelectionModalOpen && (
                    <div className="fixed inset-0 bg-black/80 modal-backdrop z-[60] flex items-center justify-center p-4">
                        <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center shadow-2xl">
                            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fas fa-chair text-emerald-600 text-2xl"></i>
                            </div>
                            <h3 className="text-xl font-bold mb-2">Select Your Table</h3>
                            <p className="text-gray-600 mb-6">Please select the table number you are seated at.</p>

                            <div className="grid grid-cols-3 gap-3 max-h-60 overflow-y-auto mb-6 p-2">
                                {isLoading ? (
                                    <div className="col-span-3 text-center py-4 text-gray-500">Loading tables...</div>
                                ) : (
                                    <>
                                        {displayedTableOptions.map(table => {
                                            const isDisabled = isManagementRole && table.isOccupied;
                                            return (
                                                <button
                                                    key={table.id}
                                                    onClick={() => {
                                                        if (isDisabled) return;
                                                        setTableNumber(table.table_number);
                                                        setIsTableSelectionModalOpen(false);
                                                        navigate(`?table=${table.table_number}`, { replace: true });
                                                    }}
                                                    disabled={isDisabled}
                                                    className={`rounded-xl p-3 transition flex flex-col items-center justify-center gap-1 border ${isDisabled ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed opacity-70' : 'bg-gray-50 hover:bg-emerald-50 border-gray-200 hover:border-emerald-200 text-gray-800'}`}
                                                >
                                                    <span className="text-lg font-bold">{table.table_number}</span>
                                                    <span className="text-[10px] truncate w-full text-center">
                                                        {table.table_name || 'Table'}
                                                        {currentUser?.role === 'admin' && !selectedBranch && table.branch_name && (
                                                            <div className="text-[8px] text-gray-400 truncate mt-0.5">{table.branch_name}</div>
                                                        )}
                                                    </span>
                                                    {isDisabled && (
                                                        <span className="text-[8px] font-black uppercase tracking-wider text-red-500">Occupied</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </>
                                )}
                            </div>

                            {!isLoading && tables.length === 0 && (
                                <p className="text-red-500 text-sm mb-4">No tables found. Please contact staff.</p>
                            )}
                        </div>
                    </div>
                )
            }




            {/* Cancellation Modal */}
            {
                cancellationModal.show && (
                    <div className="fixed inset-0 bg-black/50 modal-backdrop z-50 flex items-center justify-center">
                        <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 w-full">
                            <h3 className="text-xl font-bold mb-4 text-red-600">
                                Cancel {cancellationModal.type === 'order' ? 'Order' : 'Item'}
                            </h3>
                            <p className="text-gray-600 mb-4">
                                Are you sure you want to cancel? Please provide a reason.
                            </p>

                            <textarea
                                value={cancellationReason}
                                onChange={(e) => setCancellationReason(e.target.value)}
                                placeholder="Reason for cancellation..."
                                className="w-full p-3 border border-gray-300 rounded-lg mb-6 focus:ring-2 focus:ring-red-500 outline-none resize-none"
                                rows="3"
                            ></textarea>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setCancellationModal({ show: false, type: null, orderId: null, itemId: null })}
                                    className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                                >
                                    Back
                                </button>
                                <button
                                    onClick={handleCancellationSubmit}
                                    className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                                >
                                    Confirm Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Toast Notification */}
            {/* Toast Notification */}
            {toast.show && (
                <div className={`fixed top-4 right-4 z-[9999] flex items-center gap-3 px-6 py-4 rounded-xl shadow-2xl animate-fade-in-down transition-all ${toast.type === 'error' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white'
                    }`}>
                    <i className={`fas ${toast.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'} text-xl`}></i>
                    <span className="font-medium text-sm sm:text-base">{toast.message}</span>
                </div>
            )}
        </div>
    );
}

export default CustomerPage;
